import { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense, Component } from "react";
import { statusIgraca, zastaviceStatusa, meceviKluba } from "./live-status";
import { overallRankStats } from "./overall-rank";

const API_BASE = "https://fpl-srbija-backend-production.up.railway.app";

// v60 · PLANER — ucitava se KAO ZASEBAN CHUNK, ne ulazi u main bundle.
// Backlog stavka 7 (code splitting) je time resena bez cepanja App.jsx:
// ne cepamo postojeci kod, samo mu vise ne dodajemo. Ko ne otvori tab,
// ovaj kod nikad ne skine.
//
// UCITAVANJE SA PONOVNIM POKUSAJEM.
// Najcesci nacin da lenji import pukne nema veze sa nasim kodom: korisnik drzi
// otvorenu stranicu preko deploya, klikne Planer, a chunk sa starim hesom vise
// ne postoji. Bez ovoga to je bela stranica. Sa ovim:
//   1. jos jedan pokusaj posle 600ms  — resava mrezni trzaj
//   2. ako i to padne, JEDNO osvezavanje — povlaci nov index.html sa novim
//      hesevima, pa planer proradi
//   3. ako padne i posle osvezavanja, baca se dalje i hvata PlannerBoundary
// Pecat u sessionStorage sprecava petlju osvezavanja i nestaje sa karticom.
const CHUNK_RETRY_KEY = "fpl_chunk_reload";
function loadPlannerChunk() {
  return import("./Planner")
    .then((m) => {
      try { sessionStorage.removeItem(CHUNK_RETRY_KEY); } catch (e) { /* nedostupan */ }
      return m;
    })
    .catch((greska) =>
      new Promise((r) => setTimeout(r, 600))
        .then(() => import("./Planner"))
        .catch(() => {
          let vecProbano = null;
          try { vecProbano = sessionStorage.getItem(CHUNK_RETRY_KEY); } catch (e) { /* nedostupan */ }
          if (!vecProbano) {
            try { sessionStorage.setItem(CHUNK_RETRY_KEY, "1"); } catch (e) { /* nedostupan */ }
            trackEvent("Planner Chunk Reload", {});
            window.location.reload();
            return new Promise(() => {}); // ne razresavaj — stranica se osvezava
          }
          trackEvent("Planner Chunk Failed", {});
          throw greska;
        })
    );
}
const Planner = lazy(loadPlannerChunk);

// v60 · Prekidac za planer. Dok je false, tab stoji zakljucan sa oznakom
// "uskoro" i ekran se ne moze dosegnuti.
// 28.08: otvoren za sve. Filip prosao kroz planer na telefonu, saobracaj je
// trenutno zanemarljiv, a ulaz je zaseban tab — ligu ne dira.
const PLANNER_ENABLED = true;

// ===================================================================
// v68 · GRANICA GRESKE OKO PLANERA
//
// ZASTO POSTOJI: `Suspense` NIJE granica greske. On hvata samo cekanje, ne
// bacanje. Bez ovoga, svako bacanje u `Planner.jsx` — ili neuspeh lenjog
// `import("./Planner")` — demontira CEO React stablo. Korisnik ne dobija
// pokvaren planer nego BELU STRANICU cele aplikacije, i ne moze nazad na ligu.
//
// Dva scenarija koja to izazovu bez ijedne greske u nasem kodu:
//   1. Korisnik drzi otvorenu staru stranicu preko deploya, klikne Planer, a
//      chunk sa starim hesom vise ne postoji -> import pukne -> bela stranica.
//   2. Sacuvan plan u localStorage-u u obliku koji planer ne ocekuje -> pad
//      pri renderu -> osvezavanje ponovo cita isti plan -> PETLJA iz koje
//      korisnik ne moze da izadje bez brisanja podataka sajta.
//
// Zato fallback ima i dugme koje brise sacuvan plan: to je izlaz iz petlje.
// ===================================================================
class PlannerBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { pao: false };
  }
  static getDerivedStateFromError() {
    return { pao: true };
  }
  componentDidCatch(err) {
    // Bez ovoga pad planera je nevidljiv — korisnik vidi belinu, mi ne vidimo nista.
    try {
      trackEvent("Planner Crashed", {
        message: String((err && err.message) || err).slice(0, 120),
      });
    } catch (e) { /* telemetrija ne sme da obori fallback */ }
  }
  render() {
    if (this.state.pao) return this.props.fallback;
    return this.props.children;
  }
}
const SUPABASE_URL = "https://wwzhamqfqvpobdfhufix.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Ind3emhhbXFmcXZwb2JkZmh1Zml4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcxMzIyNjIsImV4cCI6MjA5MjcwODI2Mn0.TxbzPEMH3YSJoI2DrsnTufCBi5BfccCW3AEvnSVw9Jo";

// ===================================================================
// v52 · SEZONA I ZASTITA OD ZASTARELIH ID-EVA
//
// FPL dodeljuje NOV entry ID svake sezone, redom registracije.
// Proslogodisnji ID ili ne postoji, ili je u medjuvremenu pripao DRUGOM coveku.
// Provereno 06.08.2026:
//   /entry/5647565/ (Filipov ID iz 2025/26) -> 404 / "No Entry matches the given query."
//   /entry/3079777/ (novi ID)               -> "Veliki znalac", Filip Jovanovic
//
// Bez ovoga bi stalni korisnik posle 21.08 tiho ucitao TUDJ tim — najgora
// vrsta greske jer je korisnik ne primeti.
// ===================================================================
const SEASON = "2026/27";
const SEASON_KEY = "fpl_season";

// v54: "Samo razlike" u Duelu. Preferencija prikaza, NE brise se pri promeni
// sezone (isto kao fpl_lang) — zato nije u SEASON_SCOPED_KEYS.
const ONLY_DIFF_KEY = "fpl_duel_only_diff";

// v52: Supabase baza (~6M redova) drzi ID-eve iz sezone 2025/26 — svi su mrtvi.
// Ponovni scrape je moguc najranije 22–23.08.2026 (Overall liga 314 dobija
// poredak tek posle GW1). Dok je ovo `true`, pretraga po imenu prikazuje
// upozorenje i nudi unos Team ID-a kao pouzdan put.
//
// >>> POSLE PONOVNOG SCRAPE-A: promeni u `false` i pushuj. To je sve. <<<
const STALE_DB = false;

// v52: "Pozovi ligu" — deljiv link oblika fplcenter.com/?liga=1236390
// Cita se JEDNOM pri ucitavanju modula, pre nego sto useState initializeri
// odluce koji ekran da prikazu (da ne bljesne home pa tek onda liga).
// Prihvatamo i ?league= zbog engleskih deljenja.
// v52: DEMO LIGA — glavni CTA na home ekranu ("Probaj demo ligu").
// Stara demo liga 1957483 je iz 2025/26 i vraca HTTP 404 (provereno 10.08.2026).
// LIGE DOBIJAJU NOVE ID-EVE SVAKE SEZONE, isto kao timovi — to nije bilo u handoff-u.
// Uz to, PRE GW1 nijedna liga nema tabelu (Overall 314 i Serbia 210 vracaju results: []).
// Zato je dugme sakriveno dok ovde ne upises ZIVU ligu sa bar 10 clanova.
//
// >>> POSLE 21.08 19:30: uzmi ID neke svoje javne lige sa tabelom i upisi ga ovde. <<<
const DEMO_LEAGUE_ID = null;

// v70 (31.08.2026) · PREKIDAC ZA EMAIL FORMU — SAKRIVENA.
//
// ZASTO: forma obecava "One email per Gameweek: your mini-league drama,
// captain calls, who caught who. No spam, unsubscribe anytime" — a nacin da se
// taj mejl posalje NE POSTOJI. Backend nema biblioteku za slanje, Supabase nema
// nijednu edge funkciju, nema domena sa SPF/DKIM, nema odjave, i nema politike
// privatnosti nigde u aplikaciji. Skupljanje staje dok isporuka ne postoji.
//
// BROJ ZBOG KOG JE ODLUKA DONETA (31.08.2026, posle Burazzers IG objave):
// 22 prijave od ~400 poseta, za sat i 43 minuta = ~5,5%. Potraznja je stvarna i
// forma radi. Gasi se zbog NEISPORUCIVOSTI, ne zbog slabog odziva.
// Ukupno u bazi na taj dan: 23 reda.
//
// >>> VRACANJE: promeni u `true`. Komponenta, stringovi i testovi su netaknuti. <<<
// >>> PRE VRACANJA obavezno: (1) politika privatnosti, (2) provajder za slanje  <<<
// >>> sa odjavom, (3) `teamId` na pozivu komponente — sada je hardkodovan       <<<
// >>> `null`, pa se ne zna cija je adresa, i obecana personalizacija je         <<<
// >>> neizvodljiva bez obzira na trud.                                          <<<
const EMAIL_CAPTURE_ENABLED = false;

// v53: GW1 deadline sezone 2026/27.
// Zvanicno: petak 21.08.2026, 18:30 BST = 17:30 UTC = 19:30 po nasem vremenu.
// Cuva se kao UTC da bi odbrojavanje bilo tacno bez obzira gde je korisnik.
// Posle ovog trenutka PreSeasonNotice sam sebe gasi — nema sta rucno da se menja.
const GW1_DEADLINE_MS = Date.parse("2026-08-21T17:30:00Z");

const INVITED_LEAGUE_ID = (() => {
  try {
    const p = new URLSearchParams(window.location.search);
    const raw = (p.get("liga") || p.get("league") || "").trim();
    return /^\d{1,10}$/.test(raw) ? raw : null;
  } catch (e) {
    return null;
  }
})();

// Kljucevi koji vaze samo za JEDNU sezonu.
// fpl_lang NAMERNO nije ovde — jezik nije vezan za sezonu.
const SEASON_SCOPED_KEYS = ["fpl_team_id", "fpl_my_team_id", "fpl_league_id", "fpl_last_league"];

function purgeStaleSeasonData() {
  try {
    if (localStorage.getItem(SEASON_KEY) === SEASON) return false;
    // Nov korisnik nema sta da se brise — samo mu upisemo pecat, bez poruke.
    const hadData = SEASON_SCOPED_KEYS.some((k) => localStorage.getItem(k) != null);
    SEASON_SCOPED_KEYS.forEach((k) => localStorage.removeItem(k));
    // nudge-dismissed-gwN je takodje po sezoni (GW brojevi se ponavljaju)
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith("nudge-dismissed-gw")) localStorage.removeItem(k);
    }
    localStorage.setItem(SEASON_KEY, SEASON);
    return hadData;
  } catch (e) {
    // localStorage nedostupan (privatni mod, in-app webview) — ne rusi app.
    return false;
  }
}

// Pokrece se JEDNOM, pri ucitavanju modula — dakle PRE nego sto bilo koji
// useState initializer procita localStorage.
const SEASON_WAS_RESET = purgeStaleSeasonData();

// Railway proxy vraca FPL greske sa statusom 200 i telom {detail: "..."}.
// Zato status kod NIJE dovoljan — mora se gledati i sadrzaj.
// Oblik: tacno jedan kljuc, zove se "detail", vrednost je string.
function isNotFoundPayload(data) {
  return !!(
    data &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    Object.keys(data).length === 1 &&
    typeof data.detail === "string"
  );
}

async function apiFetch(path) {
  const res = await fetch(`${API_BASE}/api${path}`);
  if (!res.ok) {
    const err = new Error(`API error: ${res.status}`);
    err.status = res.status;
    err.isMaintenance = res.status === 502 || res.status === 503 || res.status === 504;
    throw err;
  }
  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    const err = new Error("FPL returned non-JSON response (likely maintenance)");
    err.isMaintenance = true;
    throw err;
  }
  const data = await res.json();
  // v52: {detail:"..."} kroz proxy stize kao 200. Bez ove provere bi pozivalac
  // dobio objekat umesto podataka i tiho prikazao prazan/pogresan tim.
  if (isNotFoundPayload(data)) {
    const err = new Error(`Nema podataka na ${path}: ${data.detail}`);
    err.notFound = true;
    err.detail = data.detail;
    throw err;
  }
  return data;
}

// ===================================================================
// v52 · RETENTION INSTRUMENT
//
// Kill-gate (okt/nov) trazi odgovor na pitanje "da li se ISTI covek vraca".
// Plausible daje posete, ne kohorte. Zato pamtimo datum PRVE posete lokalno
// i uz svaki dogadjaj saljemo bucket koliko je nedelja proslo od tada.
//
// VAZNO: fpl_first_seen NIJE u SEASON_SCOPED_KEYS — namerno prezivljava
// promenu sezone, jer nas bas i zanima ko se vratio preko leta.
// ===================================================================
const FIRST_SEEN_KEY = "fpl_first_seen";

function getWeeksSinceFirstVisit() {
  try {
    const now = Date.now();
    const raw = Number(localStorage.getItem(FIRST_SEEN_KEY));
    if (!raw || Number.isNaN(raw)) {
      localStorage.setItem(FIRST_SEEN_KEY, String(now));
      return "0";
    }
    const weeks = Math.floor((now - raw) / (7 * 24 * 60 * 60 * 1000));
    if (weeks <= 0) return "0";
    if (weeks === 1) return "1";
    if (weeks === 2) return "2";
    if (weeks <= 4) return "3-4";
    if (weeks <= 8) return "5-8";
    return "9+";
  } catch (e) {
    return "unknown";
  }
}

// ===================================================================
// v52 · DETEKCIJA IN-APP PRETRAZIVACA
//
// Plausible (apr–avg 2026): 1.6k od 2.4k poseta kroz "Mobile App" browser,
// tj. Instagram webview. U tom okruzenju download generisane slike cesto
// NE radi. Zbog toga "Share Image Generated" od 0.9% mozda ne meri
// nezainteresovanost korisnika nego tehnicku blokadu.
// Bez ovog propa ne mozemo da razlikujemo to dvoje.
// ===================================================================
function detectInAppBrowser() {
  try {
    const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
    if (/Instagram/i.test(ua)) return "instagram";
    if (/FBAN|FBAV|FB_IAB/i.test(ua)) return "facebook";
    if (/TikTok|BytedanceWebview/i.test(ua)) return "tiktok";
    if (/Snapchat|\bLine\/|TwitterAndroid/i.test(ua)) return "other_social";
    return null;
  } catch (e) {
    return null;
  }
}
const IN_APP_BROWSER = detectInAppBrowser();

// Upisi pecat prve posete ODMAH pri ucitavanju modula.
// Ranije se pisao tek unutar trackEvent-a, a trackEvent nista ne radi ako
// Plausible skripta nije stigla — pa bi korisnik koji odmah izadje ostao
// nezabelezen i kvario imenilac za retention.
getWeeksSinceFirstVisit();

// Plausible custom event tracker. Fire-and-forget, never throws, never blocks.
// If Plausible script isn't loaded (dev/local), silently no-op.
// v52: svaki dogadjaj automatski nosi weeks_since_first i in_app.
function trackEvent(name, props) {
  try {
    if (typeof window !== "undefined" && typeof window.plausible === "function") {
      window.plausible(name, {
        props: {
          ...(props || {}),
          weeks_since_first: getWeeksSinceFirstVisit(),
          in_app: IN_APP_BROWSER || "no",
        },
      });
    }
  } catch (e) {}
}

// v51: Region ISO Alpha-3 → emoji zastavica.
// FPL koristi alpha-3 (SRB, CZE, ENG), emoji koristi alpha-2 (RS, CZ, GB).
// UK podregioni (ENG/SCO/WLS/NIR) imaju svoje emoji-je sa subdivision flags.
// Za nepostojeća zemlja → fallback null (UI prikazuje ISO kod).
const ISO3_TO_FLAG = {
  // Balkan & near
  SRB: "🇷🇸", BIH: "🇧🇦", HRV: "🇭🇷", MKD: "🇲🇰", MNE: "🇲🇪", SVN: "🇸🇮", BGR: "🇧🇬", ROU: "🇷🇴", HUN: "🇭🇺", ALB: "🇦🇱", GRC: "🇬🇷", TUR: "🇹🇷",
  // UK & Ireland (FPL razdvaja regione)
  ENG: "🏴󠁧󠁢󠁥󠁮󠁧󠁿", SCO: "🏴󠁧󠁢󠁳󠁣󠁴󠁿", WLS: "🏴󠁧󠁢󠁷󠁬󠁳󠁿", NIR: "🇬🇧", IRL: "🇮🇪",
  // Western Europe
  FRA: "🇫🇷", DEU: "🇩🇪", ITA: "🇮🇹", ESP: "🇪🇸", PRT: "🇵🇹", NLD: "🇳🇱", BEL: "🇧🇪", CHE: "🇨🇭", AUT: "🇦🇹", LUX: "🇱🇺",
  // Northern Europe
  NOR: "🇳🇴", SWE: "🇸🇪", DNK: "🇩🇰", FIN: "🇫🇮", ISL: "🇮🇸",
  // Eastern Europe
  CZE: "🇨🇿", POL: "🇵🇱", SVK: "🇸🇰", UKR: "🇺🇦", RUS: "🇷🇺", BLR: "🇧🇾", LTU: "🇱🇹", LVA: "🇱🇻", EST: "🇪🇪",
  // Americas
  USA: "🇺🇸", CAN: "🇨🇦", MEX: "🇲🇽", BRA: "🇧🇷", ARG: "🇦🇷", CHL: "🇨🇱", COL: "🇨🇴", PER: "🇵🇪",
  // Asia
  CHN: "🇨🇳", JPN: "🇯🇵", KOR: "🇰🇷", IND: "🇮🇳", PAK: "🇵🇰", BGD: "🇧🇩", IDN: "🇮🇩", THA: "🇹🇭", VNM: "🇻🇳", PHL: "🇵🇭", MYS: "🇲🇾", SGP: "🇸🇬",
  // Middle East
  ISR: "🇮🇱", ARE: "🇦🇪", SAU: "🇸🇦", QAT: "🇶🇦", LBN: "🇱🇧", EGY: "🇪🇬", IRN: "🇮🇷", IRQ: "🇮🇶",
  // Africa
  NGA: "🇳🇬", ZAF: "🇿🇦", KEN: "🇰🇪", GHA: "🇬🇭", MAR: "🇲🇦", DZA: "🇩🇿", TUN: "🇹🇳",
  // Oceania
  AUS: "🇦🇺", NZL: "🇳🇿",
};

function regionFlag(iso) {
  if (!iso) return null;
  return ISO3_TO_FLAG[iso.toUpperCase()] || null;
}

// Format ranga: 1234567 → "#1.2M", 12345 → "#12k", null → null
function formatRankShort(rank) {
  if (!rank || rank <= 0) return null;
  if (rank >= 1_000_000) return "#" + (rank / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (rank >= 1_000) return "#" + Math.round(rank / 1_000) + "k";
  return "#" + rank;
}

const TEXT = {
  en: {
    tagline: "Follow your league in real time",
    tabTeam: "Your name / ID",
    tabLeague: "League ID",
    tabPlayer: "Player name",
    tabTeamName: "Team name",
    teamId: "Player name / Team name / ID",
    leagueId: "League ID",
    teamIdOptional: "Your Team ID",
    optional: "(optional, for highlight)",
    findLeagues: "Find my leagues →",
    loadLeague: "Load league →",
    loading: "Loading...",
    loadingYourLeague: "Loading your league...",
    teamNotFound: "Team not found. Check your Team ID.",
    teamNotFoundSeason: "That team doesn't exist this season. FPL gives everyone a NEW Team ID every season — find yours again on the FPL site.",
    emailTitle: "Get the Gameweek recap",
    emailSub: "One email per Gameweek: your mini-league drama, captain calls, who caught who. No spam, unsubscribe anytime.",
    emailPlaceholder: "your@email.com",
    emailBtn: "Sign me up",
    emailSending: "Sending...",
    emailDone: "You're in. See you after the Gameweek.",
    emailErrorInvalid: "That doesn't look like an email address.",
    emailErrorGeneric: "Couldn't sign you up. Try again in a moment.",
    emailDismiss: "No thanks",
    preseasonTitle: "GW1 deadline in",
    preseasonBody: "No league has a table yet. FPL locks squads and publishes standings only after the GW1 deadline — Fri 21 Aug, 19:30 CET. Until then there is nothing real to show, here or anywhere else.",
    preseasonCta: "Do this now: enter your Team ID below. We'll remember it, and on Friday at 19:30 your league loads in one tap.",
    seasonResetTitle: "New season, new Team IDs",
    seasonResetBody: "FPL assigns a new Team ID every season, so your saved team was cleared. Enter your new Team ID once and we'll remember it.",
    staleDbWarning: "Name search still holds last season's IDs. Until we refresh it (after GW1), entering your Team ID is the reliable way in.",
    useTeamIdInstead: "Enter Team ID instead →",
    leagueError: "Error. Check your League ID.",
    leagueNotFoundSeason: "That league doesn't exist this season. FPL creates new league IDs every season — grab the new ID from your FPL mini-league page.",
    leagueEmptyPreseason: "This league has no standings yet. FPL publishes them after the GW1 deadline (21 Aug, 19:30 CET). Come back then.",
    teamIdHint: "Type player name, team name or Team ID",
    teamIdPlaceholder: "e.g. Marko Petrović or 1234567",
    playerNamePlaceholder: "Your name or player name",
    teamNamePlaceholder: "Team name",
    leagueIdPlaceholderNew: "League ID - 1235390",
    searchByName: "Search by name",
    searchByNamePlaceholder: "Type your name (min. 2 letters)",
    searchHint: "Don't know your Team ID? Search Serbian players by name",
    searchNoResults: "No results found",
    searchLoading: "Searching...",
    searchByNameMinChars: "Type at least 2 characters",
    searchCloseBtn: "Use Team ID instead",
    leagueIdHint: "League ID → fantasy.premierleague.com → Leagues & Cups tab",
    selectLeague: "Select a league",
    noLeagues: "No leagues",
    changeTeamId: "Change Team ID",
    public: "Public",
    private: "Private",
    position: "Position",
    features: [
      { icon: "📊", t: "Live table", d: "Points during matches" },
      { icon: "⭐", t: "Bonus points", d: "BPS calculation" },
      { icon: "🔄", t: "Auto-refresh", d: "Every 60 seconds" },
      { icon: "💬", t: "Share success", d: "Show off your league position" }
    ],
    managers: "managers",
    searchPlaceholder: "Search manager, team or ID...",
    results: "results",
    result: "result",
    noResults: "No results for",
    headerMgr: "Manager",
    headerCap: "Captain",
    headerGW: "GW",
    headerHit: "Hit",
    headerNet: "Net",
    headerTotal: "Total",
    headerForm: "Form",
    provisionalBonus: "Provisional Bonus",
    goalsAssists: "Goals & Assists",
    bench: "BENCH",
    team: "Team",
    otherLeagues: "Other leagues",
    noOtherLeagues: "No other leagues",
    gwNet: "GW Net",
    total: "Total",
    highestRank: "Best overall rank",
    currentRank: "Current overall rank",
    lowestRank: "Worst overall rank",
    goals: "Goals",
    assists: "Assists",
    cleanSheet: "Clean sheet",
    minutes: "Minutes",
    bonusPoints: "Bonus",
    yellowCards: "Yellow cards",
    redCards: "Red cards",
    saves: "Saves",
    penaltiesSaved: "Penalties saved",
    penaltiesMissed: "Penalties missed",
    ownGoals: "Own goals",
    goalsConceded: "Goals conceded",
    defensiveContributions: "Defensive contributions",
    totalPoints: "Total Points",
    notPlayed: "Not played yet",
    dgwBannerTitle: "Double Gameweek",
    dgwBanner: "Double Gameweek in progress. This player has 2 matches — showing his total points only. Detailed breakdown will appear after the DGW finishes.",
    noEvents: "No goals or assists",
    yourPosition: "Your position",
    leadingBy: "leading by",
    behindLeader: "behind leader",
    aheadOf: "ahead of next",
    changeInGw: "change in gw",
    global: "global",
    overallRank: "overall rank",
    howToFind: "How to find your Team ID?",
    tryDemo: "Try demo league",
    tryDemoHint: "See how the app works without entering your Team ID",
    or: "or",
    tutorialTitle: "Finding your Team ID",
    tutorialIntro: "You can paste the full URL from the FPL website — we'll extract the ID automatically.",
    tutorialStep1Title: "On desktop / web browser",
    tutorialStep1: "Open fantasy.premierleague.com and click the 'Pick Team' or 'Points' tab. Your Team ID is the number in the URL between /entry/ and /event/",
    tutorialStep1Example: "fantasy.premierleague.com/entry/1528511/event/34",
    tutorialStep2Title: "If you only use the FPL mobile app",
    tutorialStep2: "The app doesn't show your Team ID directly. Open any browser on your phone, go to fantasy.premierleague.com, log in with the same account, tap 'Points' or 'Pick Team', and copy the URL from the address bar.",
    tutorialStep3Title: "Easiest way",
    tutorialStep3: "Just copy the entire FPL page URL from your browser and paste it in the Team ID field above — we'll extract the number automatically.",
    closeBtn: "Close",
    pasteFromClipboard: "Paste from clipboard",
    share: "Share",
    shareYourMoment: "Share your moment",
    shareSubtitle: "Pick a card to share with friends",
    shareDownload: "Download",
    shareCard1Title: "League Leader",
    shareCard1Lock: "You're not leading the league",
    shareCard2Title: "GW Winner",
    shareCard2Lock: "You don't have the highest GW score",
    shareCard3Title: "Captain King",
    shareCard3Lock: "Captain scored less than 12 pts",
    shareCardUnlocked: "Ready to share",
    shareCardLocked: "Locked",
    shareCardHeadline1: "League Leader",
    shareCardHeadline2: "GW Winner",
    shareCardHeadline3: "Captain King",
    shareCard4Title: "Top Rank",
    shareCard4Lock: "No overall rank yet",
    nudgeLeaderTitle: "Currently leading",
    nudgeLeaderSub: "{count} mgr · brag now",
    nudgeWinnerTitle: "Best GW in",
    nudgeWinnerSub: "{count} mgr · {pts} pts · brag now",
    nudgeCaptainTitle: "Your Captain smashed it!",
    nudgeCaptainTitleTC: "Your Triple Captain smashed it!",
    nudgeCaptainSub: "{name} · {pts} pts · brag now",
    nudgeDefaultTitle: "Share your result",
    nudgeDefaultSub: "Card ready — GW {gw}",
    nudgeShare: "Share",
    bankLabel: "Bank",
    transfersTitle: "Transfers",
    transfersHit: "{count} transfers · {cost} pts",
    transfersHitOne: "{count} transfer · {cost} pts",
    transfersFree: "{count} transfers · free",
    transfersFreeOne: "{count} transfer · free",
    transfersChip: "{count} transfers · {chip} active",
    transfersLoading: "Loading transfers...",
    transfersLeft: "{count} left",
    transfersLeftOne: "{count} left",
    compare: "Compare",
    compareWithMe: "Compare with me",
    compareTitle: "Compare Teams",
    comparePickRival: "Pick a rival from your league",
    compareVs: "You vs Rival",
    compareBoldDiff: "green = ahead",
    compareSameMatch: "grey = same player",
    compareLevel: "level",
    compareToPlay: "Still to play",
    compareOnlyDiff: "Differences only",
    compareNoDiff: "Identical teams — nothing to compare",
    fplDownTitle: "FPL is updating the game",
    fplDownSub: "Usually takes a few minutes after the GW deadline — live data will be back soon",
    fplDownHint: "We'll auto-refresh when FPL is back. No need to reload.",
    fplDownRetry: "Try again",
    shareCardGWNet: "GW Net",
    shareCardTotal: "Total",
    shareCardPtsThisGW: "Points this GW",
    shareCardTopPlayer: "Top player",
    shareCardCapPts: "Points as captain",
    shareCardGoals: "Goals",
    shareCardAssists: "Assists",
    shareCardBonus: "Bonus",
    setPieceTitle: "Set-piece takers",
    setPieceSub: "Penalties \u00b7 free kicks \u00b7 corners",
    setPiecePen: "Penalties",
    setPieceFK: "Free kicks",
    setPieceCorners: "Corners",
    setPieceAll: "All",
    setPieceNone: "not listed",
    setPieceSource: "Official FPL data \u2014 updates on its own when a club changes its order.",
    setPieceError: "Couldn't load set-piece data.",
    tabLive: "Live",
    tabPlanner: "Planner",
    tabSetPieces: "Set pieces",
    tabSoon: "SOON",
    plannerTitle: "Planner",
    plannerDeadline: "deadline",
    plannerFT: "FT",
    plannerTransfers: "Transfers",
    plannerHit: "Hit",
    plannerBank: "Bank",
    plannerBench: "Bench",
    plannerBlank: "no match",
    plannerHome: "h",
    plannerAway: "a",
    plannerError: "Couldn't load the planner.",
    plannerCrash: "The planner hit a problem and stopped.",
    plannerCrashHint: "The rest of the app is fine. If it keeps happening, clear your saved plan.",
    plannerCrashBack: "Back to league",
    plannerCrashReset: "Clear saved plan",
    plannerNoTeam: "Enter your Team ID first \u2014 the planner needs to know which squad to load.",
    plannerTapToSwap: "Tap a player to swap him",
    plannerPickFor: "Replace",
    plannerUpTo: "up to",
    plannerSearch: "search player or club\u2026",
    plannerByPoints: "By points",
    plannerByPrice: "By price",
    plannerByForm: "By form",
    plannerOwned: "in squad",
    plannerClubLimit: "3 from club",
    plannerTooPricey: "too pricey",
    plannerPts: "pts",
    plannerShowing: "showing",
    plannerNoMatch: "Nobody matches that search.",
    plannerOverBudget: "This plan no longer fits the budget \u2014 prices changed since you made it. Remove a transfer or pick someone cheaper.",
    plannerPlanCost: "Whole plan costs",
    plannerTapPlayer: "Tap a player to change something",
    plannerEmptySlot: "Empty this slot",
    plannerManyTransfers: "transfers this gameweek",
    plannerShowList: "Show",
    plannerHideList: "Hide",
    duelSubbedIn: "Came on from the bench",
    duelShare: "Share this duel",
    duelShareGenerating: "Making image…",
    duelShareError: "Could not make the image. Try again.",
    duelCardLead: "Leading",
    duelCardLeadSub: "catch me if you can",
    duelCardTrail: "Chasing",
    duelCardTrailSub: "not over yet",
    duelCardTied: "Tied",
    plannerResetGw: "Reset all",
    plannerResetPlan: "Reset plan",
    plannerResetAsk: "Reset the whole plan?",
    plannerResetYes: "Reset",
    plannerFillSlot: "Fill slot",
    plannerEmpty: "empty",
    plannerOneEmpty: "1 empty slot — your plan isn't finished yet.",
    plannerManyEmpty: "empty slots — your plan isn't finished yet.",
    plannerNoCaptain: "No captain in your XI. Tap a starter to set one.",
    plannerShare: "Save image",
    plannerShareWorking: "Generating...",
    plannerShareError: "Could not generate the image.",
    plannerShareBlocked: "Images can't be saved from the Instagram browser. Open fplcenter.com in Chrome or Safari (menu top right, Open in browser).",
    plannerSwapPlayer: "Transfer player",
    plannerChip: "Chip",
    plannerNoChip: "none",
    plannerChipWCInfo: "Wildcard: transfers this gameweek are free, and your saved free transfers stay untouched.",
    plannerChipFHInfo: "Free Hit: transfers are free and the squad reverts next gameweek \u2014 the planner already accounts for that.",
    plannerChipBBInfo: "Bench Boost: all four bench players score too.",
    plannerChipTCInfo: "Triple Captain: your captain scores triple.",
    plannerToBench: "Move to bench",
    plannerToXI: "Move into the XI",
    plannerMakeCaptain: "Make captain",
    plannerMakeVice: "Make vice-captain",
    plannerCancel: "Cancel",
    plannerPickPartner: "Pick who he swaps places with",
    plannerNoSwap: "No legal swap for him in this formation.",
  },
  sr: {
    tagline: "Prati svoju ligu u realnom vremenu",
    tabTeam: "Tvoje ime / ID",
    tabLeague: "Liga ID",
    tabPlayer: "Po imenu",
    tabTeamName: "Po ekipi",
    teamId: "Ime igrača / Ime tima / ID",
    leagueId: "Liga ID",
    teamIdOptional: "Tvoj Team ID",
    optional: "(opciono, za highlight)",
    findLeagues: "Pronadji moje lige →",
    loadLeague: "Ucitaj ligu →",
    loading: "Ucitavanje...",
    loadingYourLeague: "Ucitavanje tvoje lige...",
    teamNotFound: "Tim nije pronadjen. Proveri Team ID.",
    teamNotFoundSeason: "Taj tim ne postoji u ovoj sezoni. FPL svakoj sezoni dodeljuje NOV Team ID — pronadji svoj ponovo na FPL sajtu.",
    emailTitle: "Rezime kola na mejl",
    emailSub: "Jedan mejl po kolu: drama u tvojoj mini-ligi, kapiteni, ko je koga prestigao. Bez spama, odjava kad hoces.",
    emailPlaceholder: "tvoj@mejl.com",
    emailBtn: "Prijavi me",
    emailSending: "Saljem...",
    emailDone: "Upisan si. Vidimo se posle kola.",
    emailErrorInvalid: "Ovo ne lici na mejl adresu.",
    emailErrorGeneric: "Nije uspelo. Probaj ponovo za koji trenutak.",
    emailDismiss: "Ne, hvala",
    preseasonTitle: "GW1 deadline za",
    preseasonBody: "Nijedna liga jos nema tabelu. FPL zakljucava timove i objavljuje poredak tek posle GW1 deadline-a — petak 21.08, 19:30. Do tada nema sta da se prikaze, ni ovde ni bilo gde drugde.",
    preseasonCta: "Uradi sad ovo: unesi svoj Team ID ispod. Zapamticemo ga, i u petak u 19:30 ti se liga otvara iz prve.",
    seasonResetTitle: "Nova sezona, novi Team ID-evi",
    seasonResetBody: "FPL svake sezone dodeljuje nov Team ID, pa je tvoj sacuvan tim obrisan. Unesi novi Team ID jednom i zapamticemo ga.",
    staleDbWarning: "Pretraga po imenu jos uvek ima ID-eve iz prosle sezone. Dok je ne osvezimo (posle GW1), unos Team ID-a je pouzdan put.",
    useTeamIdInstead: "Unesi Team ID umesto toga →",
    leagueError: "Greska. Proveri Liga ID.",
    leagueNotFoundSeason: "Ta liga ne postoji u ovoj sezoni. FPL pravi nove ID-eve liga svake sezone — uzmi novi ID sa stranice svoje mini-lige.",
    leagueEmptyPreseason: "Ova liga jos nema tabelu. FPL je objavljuje posle GW1 deadline-a (21.08, 19:30). Vrati se tada.",
    teamIdHint: "Ukucaj ime igrača, ime tima ili Team ID",
    teamIdPlaceholder: "npr. Marko Petrović ili 1234567",
    playerNamePlaceholder: "Tvoje ime ili ime igraca",
    teamNamePlaceholder: "Ime ekipe",
    leagueIdPlaceholderNew: "Liga ID - 1235390",
    searchByName: "Pretraga po imenu",
    searchByNamePlaceholder: "Ukucaj svoje ime (min. 2 slova)",
    searchHint: "Ne znaš svoj Team ID? Potraži srpske igrače po imenu",
    searchNoResults: "Nema rezultata",
    searchLoading: "Pretraga...",
    searchByNameMinChars: "Ukucaj bar 2 slova",
    searchCloseBtn: "Koristi Team ID",
    leagueIdHint: "Liga ID → fantasy.premierleague.com → Leagues & Cups tab",
    selectLeague: "Izaberi ligu",
    noLeagues: "Nema liga",
    changeTeamId: "Promeni Team ID",
    public: "Javna",
    private: "Privatna",
    position: "Pozicija",
    features: [
      { icon: "📊", t: "Live tabela", d: "Poeni tokom meca" },
      { icon: "⭐", t: "Bonus poeni", d: "BPS kalkulacija" },
      { icon: "🔄", t: "Auto-refresh", d: "Svakih 60 sekundi" },
      { icon: "💬", t: "Podeli uspeh", d: "Pokazi svoju poziciju u ligi" }
    ],
    managers: "menadzera",
    searchPlaceholder: "Pretrazi menadzera, tim ili ID...",
    results: "rezultata",
    result: "rezultat",
    noResults: "Nema rezultata za",
    headerMgr: "Menadzer",
    headerCap: "Kapiten",
    headerGW: "GW",
    headerHit: "Hit",
    headerNet: "Net",
    headerTotal: "Ukupno",
    headerForm: "Forma",
    provisionalBonus: "Provisional Bonus",
    goalsAssists: "Golovi i asistencije",
    bench: "KLUPA",
    team: "Tim",
    otherLeagues: "Ostale lige",
    noOtherLeagues: "Nema drugih liga",
    gwNet: "GW Net",
    total: "Ukupno",
    highestRank: "Najbolji overall rank",
    currentRank: "Trenutni overall rank",
    lowestRank: "Najgori overall rank",
    goals: "Golovi",
    assists: "Asistencije",
    cleanSheet: "Clean sheet",
    minutes: "Minuti",
    bonusPoints: "Bonus",
    yellowCards: "Zuti kartoni",
    redCards: "Crveni kartoni",
    saves: "Odbrane",
    penaltiesSaved: "Odbranjeni penali",
    penaltiesMissed: "Promaseni penali",
    ownGoals: "Autogolovi",
    goalsConceded: "Primljeni golovi",
    defensiveContributions: "Odbrambene akcije",
    totalPoints: "Ukupno poena",
    notPlayed: "Jos nije igrao",
    dgwBannerTitle: "Duplo Kolo",
    dgwBanner: "Duplo kolo u toku. Ovaj igrac ima 2 utakmice — prikazujemo samo njegove ukupne poene. Detaljniji prikaz bice dostupan kada se zavrsi duplo kolo.",
    noEvents: "Nema golova ni asistencija",
    yourPosition: "Tvoja pozicija",
    leadingBy: "ispred",
    behindLeader: "iza prvog",
    aheadOf: "ispred sledeceg",
    changeInGw: "promena u gw",
    global: "globalno",
    overallRank: "overall rank",
    howToFind: "Kako naci svoj Team ID?",
    tryDemo: "Probaj demo ligu",
    tryDemoHint: "Vidi kako sajt radi pre nego sto unesete svoj Team ID",
    or: "ili",
    tutorialTitle: "Kako pronaci Team ID",
    tutorialIntro: "Mozes da paste-ujes ceo URL sa FPL sajta — mi cemo automatski izvuci broj.",
    tutorialStep1Title: "Na desktopu / web browseru",
    tutorialStep1: "Otvori fantasy.premierleague.com i klikni 'Pick Team' ili 'Points' tab. Tvoj Team ID je broj u URL-u izmedju /entry/ i /event/",
    tutorialStep1Example: "fantasy.premierleague.com/entry/1528511/event/34",
    tutorialStep2Title: "Ako koristis samo FPL mobilnu aplikaciju",
    tutorialStep2: "App ne pokazuje Team ID direktno. Otvori bilo koji browser na telefonu, idi na fantasy.premierleague.com, uloguj se istim nalogom, tapni 'Points' ili 'Pick Team', i kopiraj URL iz address bar-a.",
    tutorialStep3Title: "Najlaksi nacin",
    tutorialStep3: "Samo kopiraj ceo URL sa FPL sajta iz svog browsera i paste-uj ga u Team ID polje — mi cemo automatski izvuci broj.",
    closeBtn: "Zatvori",
    pasteFromClipboard: "Paste iz clipboard-a",
    share: "Podeli",
    shareYourMoment: "Podeli svoj trenutak",
    shareSubtitle: "Izaberi karticu za deljenje sa drugarima",
    shareDownload: "Preuzmi",
    shareCard1Title: "Vodim u ligi",
    shareCard1Lock: "Nisi lider lige",
    shareCard2Title: "Pobeda u ovom kolu",
    shareCard2Lock: "Nisi imao najvise GW poena",
    shareCard3Title: "Kapiten pokidao",
    shareCard3Lock: "Kapiten ima manje od 12 poena",
    shareCardUnlocked: "Spremno za deljenje",
    shareCardLocked: "Zakljucano",
    shareCardHeadline1: "Vodim u ligi!",
    shareCardHeadline2: "Pobeda u ovom kolu!",
    shareCardHeadline3: "Kapiten pokidao!",
    shareCard4Title: "Top Rank",
    shareCard4Lock: "Jos uvek nemas overall rank",
    nudgeLeaderTitle: "Trenutno si lider u",
    nudgeLeaderSub: "{count} mgr · pohvali se",
    nudgeWinnerTitle: "Najbolji GW u",
    nudgeWinnerSub: "{count} mgr · {pts} poena · pohvali se",
    nudgeCaptainTitle: "Tvoj kapiten je pokidao",
    nudgeCaptainTitleTC: "Tvoj Triple Captain je pokidao",
    nudgeCaptainSub: "{name} · {pts} poena · pohvali se",
    nudgeDefaultTitle: "Podeli svoj rezultat",
    nudgeDefaultSub: "Kartica je spremna — GW {gw}",
    nudgeShare: "Podeli",
    bankLabel: "Banka",
    transfersTitle: "Transferi",
    transfersHit: "{count} transfera · {cost} poena",
    transfersHitOne: "{count} transfer · {cost} poena",
    transfersFree: "{count} transfera · besplatan",
    transfersFreeOne: "{count} transfer · besplatan",
    transfersChip: "{count} transfera · {chip} aktivan",
    transfersLoading: "Ucitavam transfere...",
    transfersLeft: "{count} preostalo",
    transfersLeftOne: "{count} preostalo",
    compare: "Uporedi",
    compareWithMe: "Uporedi sa mnom",
    compareTitle: "Uporedi timove",
    comparePickRival: "Izaberi rivala iz lige",
    compareVs: "Ti vs Rival",
    compareBoldDiff: "zeleno = vodi",
    compareSameMatch: "sivo = isti igrac",
    compareLevel: "nereseno",
    compareToPlay: "Jos igra",
    compareOnlyDiff: "Samo razlike",
    compareNoDiff: "Identicni timovi — nema sta da se poredi",
    fplDownTitle: "FPL azurira igru",
    fplDownSub: "Obicno traje par minuta posle deadline-a — live podaci se vracaju uskoro",
    fplDownHint: "Automatski cemo se osvezavati cim FPL bude spreman. Ne moras da reload-ujes.",
    fplDownRetry: "Pokusaj ponovo",
    shareCardGWNet: "GW Net",
    shareCardTotal: "Ukupno",
    shareCardPtsThisGW: "Poena ovog kola",
    shareCardTopPlayer: "Najbolji igrac tima",
    shareCardCapPts: "Poena kao kapiten",
    shareCardGoals: "Golovi",
    shareCardAssists: "Asist.",
    shareCardBonus: "Bonus",
    setPieceTitle: "Izvodjaci prekida",
    setPieceSub: "Penali \u00b7 slobodni udarci \u00b7 korneri",
    setPiecePen: "Penali",
    setPieceFK: "Slobodni",
    setPieceCorners: "Korneri",
    setPieceAll: "Sve",
    setPieceNone: "nije navedeno",
    setPieceSource: "Zvanicni FPL podaci \u2014 azuriraju se sami kad klub promeni redosled.",
    setPieceError: "Ne mogu da ucitam podatke o prekidima.",
    tabLive: "Uzivo",
    tabPlanner: "Planer",
    tabSetPieces: "Prekidi",
    tabSoon: "USKORO",
    plannerTitle: "Planer",
    plannerDeadline: "rok",
    plannerFT: "FT",
    plannerTransfers: "Transferi",
    plannerHit: "Hit",
    plannerBank: "Banka",
    plannerBench: "Klupa",
    plannerBlank: "nema meca",
    plannerHome: "d",
    plannerAway: "g",
    plannerError: "Ne mogu da ucitam planer.",
    plannerCrash: "Planer je naisao na problem i stao.",
    plannerCrashHint: "Ostatak aplikacije radi normalno. Ako se ponavlja, obrisi sacuvan plan.",
    plannerCrashBack: "Nazad na ligu",
    plannerCrashReset: "Obrisi sacuvan plan",
    plannerNoTeam: "Prvo unesi svoj Team ID \u2014 planer mora da zna koji sastav da ucita.",
    plannerTapToSwap: "Dodirni igraca da ga zamenis",
    plannerPickFor: "Zamena za",
    plannerUpTo: "do",
    plannerSearch: "trazi igraca ili klub\u2026",
    plannerByPoints: "Po poenima",
    plannerByPrice: "Po ceni",
    plannerByForm: "Po formi",
    plannerOwned: "vec u timu",
    plannerClubLimit: "3 iz kluba",
    plannerTooPricey: "preskupo",
    plannerPts: "poena",
    plannerShowing: "prikazano",
    plannerNoMatch: "Niko ne odgovara toj pretrazi.",
    plannerOverBudget: "Plan vise ne staje u budzet \u2014 cene su se promenile otkad si ga napravio. Skini jedan transfer ili uzmi jeftinijeg igraca.",
    plannerPlanCost: "Ceo plan kosta",
    plannerTapPlayer: "Dodirni igraca da nesto promenis",
    plannerEmptySlot: "Isprazni mesto",
    plannerManyTransfers: "transfera u ovom kolu",
    plannerShowList: "Prikazi",
    plannerHideList: "Sakrij",
    duelSubbedIn: "Usao sa klupe",
    duelShare: "Podeli duel",
    duelShareGenerating: "Pravim sliku…",
    duelShareError: "Slika nije napravljena. Probaj ponovo.",
    duelCardLead: "Vodim",
    duelCardLeadSub: "stigni me ako mozes",
    duelCardTrail: "Jurim te",
    duelCardTrailSub: "jos nije gotovo",
    duelCardTied: "Nereseno",
    plannerResetGw: "Ponisti sve",
    plannerResetPlan: "Resetuj plan",
    plannerResetAsk: "Da resetujem ceo plan?",
    plannerResetYes: "Resetuj",
    plannerFillSlot: "Popuni mesto",
    plannerEmpty: "prazno",
    plannerOneEmpty: "1 prazno mesto — plan jos nije gotov.",
    plannerManyEmpty: "praznih mesta — plan jos nije gotov.",
    plannerNoCaptain: "Nema kapitena u prvih 11. Dodirni igraca sa terena da ga postavis.",
    plannerShare: "Sacuvaj sliku",
    plannerShareWorking: "Pravim sliku...",
    plannerShareError: "Slika nije mogla da se napravi.",
    plannerShareBlocked: "Slika ne moze da se sacuva iz Instagram pregledaca. Otvori fplcenter.com u Chrome-u ili Safari-ju (meni gore desno, Otvori u pregledacu).",
    plannerSwapPlayer: "Transfer igraca",
    plannerChip: "Cip",
    plannerNoChip: "bez cipa",
    plannerChipWCInfo: "Wildcard: transferi ovog kola su besplatni, a sacuvani FT ostaju netaknuti.",
    plannerChipFHInfo: "Free Hit: transferi su besplatni, a tim se sledeceg kola vraca \u2014 planer to vec racuna.",
    plannerChipBBInfo: "Bench Boost: boduju se i cetiri igraca sa klupe.",
    plannerChipTCInfo: "Triple Captain: kapiten nosi trostruke poene.",
    plannerToBench: "Na klupu",
    plannerToXI: "U prvih 11",
    plannerMakeCaptain: "Kapiten",
    plannerMakeVice: "Potkapiten",
    plannerCancel: "Otkazi",
    plannerPickPartner: "Izaberi s kim menja mesto",
    plannerNoSwap: "Za njega nema dozvoljene zamene u ovoj formaciji.",
  }
};

function calculateProvisionalBonus(playersBps) {
  if (!playersBps || playersBps.length === 0) return {};
  const sorted = [...playersBps].filter((p) => p.bps > 0).sort((a, b) => b.bps - a.bps);
  const bonusMap = {};
  if (sorted.length === 0) return bonusMap;
  let rank = 1;
  let awarded = 0;
  let prevBps = null;
  let prevBonus = 0;
  for (const p of sorted) {
    if (awarded >= 3 && p.bps !== prevBps) break;
    let bonus;
    if (p.bps === prevBps) {
      bonus = prevBonus;
    } else {
      if (rank === 1) bonus = 3;
      else if (rank === 2) bonus = 2;
      else if (rank === 3) bonus = 1;
      else break;
    }
    bonusMap[p.id] = bonus;
    prevBps = p.bps;
    prevBonus = bonus;
    awarded++;
    rank = awarded + 1;
  }
  return bonusMap;
}

// NEW: Calculate provisional bonus per fixture (CORRECT FPL behavior)
// Groups players by fixture, then awards 3/2/1 bonus points within each fixture
// Only calculates bonus for fixtures past 60 minutes (as FPL Gameweek does)
function calculateProvisionalBonusByFixture(liveData, bootstrap, fixtures) {
  if (!liveData || !bootstrap || !fixtures) return {};
  const bonusMap = {};
  
  for (const fx of fixtures) {
    // Skip upcoming fixtures
    if (fx.status === "upcoming") continue;
    
    // Find all players from both teams in this fixture
    const teamIds = [fx.team_h, fx.team_a];
    const playersInFixture = [];
    let maxMinutesInFixture = 0;
    let fplAlreadyAwardedBonus = false;
    
    for (const teamId of teamIds) {
      const teamPlayers = bootstrap.elements.filter(el => el.team === teamId);
      for (const player of teamPlayers) {
        const live = liveData[player.id];
        if (!live) continue;
        
        // Track max minutes any player has played in this fixture
        if (live.minutes > maxMinutesInFixture) {
          maxMinutesInFixture = live.minutes;
        }
        
        // CRITICAL: if any player in this fixture already has FPL bonus, 
        // FPL has finalized bonus distribution — do NOT add provisional bonus
        // to anyone else in this fixture
        if (live.bonus > 0) {
          fplAlreadyAwardedBonus = true;
        }
        
        if (live.bps > 0 && live.bonus === 0) {
          playersInFixture.push({ id: player.id, bps: live.bps });
        }
      }
    }
    
    // If FPL already finalized bonus for this fixture, skip provisional calculation entirely
    if (fplAlreadyAwardedBonus) continue;
    
    // FPL rule: provisional bonus only shows after 60 minutes of play
    // Use max minutes of any player as proxy for fixture time
    if (maxMinutesInFixture < 60) continue;
    
    // Calculate bonus for this fixture using existing logic
    const fixtureBonus = calculateProvisionalBonus(playersInFixture);
    Object.assign(bonusMap, fixtureBonus);
  }
  
  return bonusMap;
}

function applyAutosubs(picks, liveData, bootstrap, fixtures) {
  const activePicks = picks.map(p => ({ ...p }));
  const starters = activePicks.filter(p => p.position <= 11);
  const bench = activePicks.filter(p => p.position > 11).sort((a, b) => a.position - b.position);
  const getPos = (pid) => bootstrap.elements.find(p => p.id === pid)?.element_type;
  const getTeam = (pid) => bootstrap.elements.find(p => p.id === pid)?.team;
  const didNotPlay = (pick) => {
    const lv = liveData[pick.element];
    if (!lv) return false;
    const teamId = getTeam(pick.element);
    const fx = fixtures.find(f => f.team_h === teamId || f.team_a === teamId);
    return lv.minutes === 0 && fx && fx.status === "finished";
  };
  const subs = [];
  const usedBench = new Set();
  const gkStarter = starters.find(s => getPos(s.element) === 1);
  if (gkStarter && didNotPlay(gkStarter)) {
    const gkBench = bench.find(b => getPos(b.element) === 1 && !didNotPlay(b));
    if (gkBench) {
      subs.push({ out: gkStarter.element, in: gkBench.element });
      usedBench.add(gkBench.element);
    }
  }
  const outfieldBench = bench.filter(b => getPos(b.element) !== 1 && !didNotPlay(b));
  const failingStarters = starters.filter(s => getPos(s.element) !== 1 && didNotPlay(s));
  const getFormation = (activeIds) => {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0 };
    activeIds.forEach(id => { const pos = getPos(id); if (pos) counts[pos]++; });
    return counts;
  };
  let activeIds = starters.map(s => {
    const sub = subs.find(x => x.out === s.element);
    return sub ? sub.in : s.element;
  });
  for (const failing of failingStarters) {
    for (const benchPlayer of outfieldBench) {
      if (usedBench.has(benchPlayer.element)) continue;
      const simIds = activeIds.map(id => id === failing.element ? benchPlayer.element : id);
      const form = getFormation(simIds);
      if (form[1] === 1 && form[2] >= 3 && form[3] >= 2 && form[4] >= 1) {
        subs.push({ out: failing.element, in: benchPlayer.element });
        usedBench.add(benchPlayer.element);
        activeIds = simIds;
        break;
      }
    }
  }
  return subs;
}

// ============================================================================
// v74 (01.09.2026) · JEDAN ULAZ ZA AUTOSUBOVE
//
// ZASTO POSTOJI: isti blok je bio prepisan na TRI mesta (tabela lige, manager
// modal, ucitavanje rivala za Duel). To je LEKCIJA 42 koja ceka da se desi.
//
// STA JE IZMERENO 01.09 na 149 pravih timova (GW2, kroz Filipov browser):
//   - FPL u `picks` vraca sastav VEC PRERADJEN posle autosuba: ko je usao nosi
//     poziciju <= 11 i mnozilac >= 1, ko je izasao poziciju >= 12 i mnozilac 0.
//   - Nasa `applyAutosubs` na zavrsenom kolu nadje NULA zamena, a FPL ih ima
//     kod 76 od 149 timova (51%). Heuristika je tu mrtva.
//   - Odstupanje u bodovima: NULA. Skor ide iz mnozioca koji FPL vec postavi,
//     pa heuristika nije ni kvarila ni popravljala skor — samo se oznaka
//     "usao sa klupe" nije videla ni kod koga.
//
// PRAVILO: kad `automatic_subs` postoji, postavljaju se SAMO oznake.
// Mnozilac se NE dira — on je vec tacan i od njega zavisi svaki skor.
// (Uz to: od 117 zamena jedna NIJE postovala pravilo pozicija/mnozilac.
// Zato se iz `automatic_subs` ne izvodi nista osim oznaka.)
//
// Heuristika ostaje za KOLO U TOKU — FPL puni `automatic_subs` tek pri
// zakljucavanju. To ponasanje NIJE izmereno (GW2 je bio gotov u trenutku
// merenja) i proverava se tokom sledeceg kola.
// ============================================================================
function processPicksSaAutosubovima(payload, liveData, bootstrap, fixtures) {
  const picks = (payload && payload.picks) || [];
  const zvanicni = Array.isArray(payload && payload.automatic_subs) ? payload.automatic_subs : [];

  if (zvanicni.length > 0) {
    return picks.map(p => {
      const copy = { ...p };
      if (zvanicni.some(z => z.element_out === p.element)) copy.wasSubbedOut = true;
      else if (zvanicni.some(z => z.element_in === p.element)) copy.wasSubbedIn = true;
      return copy;
    });
  }

  const isBboost = payload && payload.active_chip === "bboost";
  const subs = isBboost ? [] : applyAutosubs(picks, liveData, bootstrap, fixtures);
  return picks.map(p => {
    const subOut = subs.find(s => s.out === p.element);
    const subIn = subs.find(s => s.in === p.element);
    const copy = { ...p };
    if (subOut) { copy.multiplier = 0; copy.wasSubbedOut = true; }
    else if (subIn) { copy.multiplier = 1; copy.wasSubbedIn = true; }
    return copy;
  });
}

function StatusPill({ status, minute, showMinute = false }) {
  if (status === "finished") return <span style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: "#f1f5f9", color: "#64748b" }}>FT</span>;
  if (status === "inPlay") return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: "#fef2f2", color: "#dc2626", letterSpacing: "0.3px" }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#dc2626", animation: "pulse 1.4s ease infinite" }}/>
      {showMinute ? `${minute}'` : "LIVE"}
    </span>
  );
  return <span style={{ display: "inline-flex", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: "#f8fafc", color: "#94a3b8" }}>TBD</span>;
}

function ChipBadge({ chip }) {
  const map = {
    bboost: { label: "BB", bg: "#dcfce7", color: "#15803d" },
    "3xc": { label: "TC", bg: "#fef3c7", color: "#b45309" },
    freehit: { label: "FH", bg: "#dbeafe", color: "#1d4ed8" },
    wildcard: { label: "WC", bg: "#fee2e2", color: "#b91c1c" },
    manager: { label: "AM", bg: "#e0e7ff", color: "#4338ca" },
  };
  const c = map[chip] || { label: chip?.toUpperCase() || "", bg: "#eff6ff", color: "#2563eb" };
  return (
    <span style={{ marginLeft: 6, padding: "1px 6px", borderRadius: 5, fontSize: 9, fontWeight: 800, background: c.bg, color: c.color, fontFamily: "var(--mono)", letterSpacing: "0.3px" }}>
      {c.label}
    </span>
  );
}

function Medal({ position, size = 36 }) {
  const palettes = {
    1: { ribbon: "#dc2626", ribbonDark: "#991b1b", body: "#fbbf24", bodyDark: "#d97706", shine: "#fef3c7", text: "#78350f" },
    2: { ribbon: "#3b82f6", ribbonDark: "#1d4ed8", body: "#cbd5e1", bodyDark: "#64748b", shine: "#f1f5f9", text: "#334155" },
    3: { ribbon: "#16a34a", ribbonDark: "#15803d", body: "#cd7f32", bodyDark: "#92400e", shine: "#fde68a", text: "#7c2d12" },
  };
  const p = palettes[position];
  if (!p) return null;
  const id = `m${position}`;
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.18))" }}>
      <defs>
        <linearGradient id={`${id}-body`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={p.body}/>
          <stop offset="100%" stopColor={p.bodyDark}/>
        </linearGradient>
      </defs>
      <path d={`M 11 2 L 14 18 L 18 14 L 11 2 Z`} fill={p.ribbonDark}/>
      <path d={`M 25 2 L 22 18 L 18 14 L 25 2 Z`} fill={p.ribbon}/>
      <path d={`M 11 2 L 18 14 L 25 2 L 22 2 L 18 8 L 14 2 Z`} fill={p.ribbon} opacity="0.6"/>
      <circle cx="18" cy="22" r="11" fill={`url(#${id}-body)`} stroke={p.bodyDark} strokeWidth="0.5"/>
      <circle cx="18" cy="22" r="8" fill="none" stroke={p.bodyDark} strokeWidth="0.4" opacity="0.5"/>
      <ellipse cx="14" cy="18" rx="3.5" ry="2" fill={p.shine} opacity="0.5"/>
      <text x="18" y="26" textAnchor="middle" fontSize="11" fontWeight="800" fill={p.text} fontFamily="var(--sans)">{position}</text>
    </svg>
  );
}

function Logo({ size = 36 }) {
  return (
    <img
      src="/logo-icon.png"
      alt="FPL Center"
      width={size}
      height={size}
      style={{ display: "block", objectFit: "contain" }}
    />
  );
}

const COUNTRY_NAMES = [
  "Overall", "Serbia", "Srbija", "England", "Scotland", "Wales", "Northern Ireland",
  "Bosnia and Herzegovina", "Croatia", "Montenegro", "North Macedonia", "Slovenia", "Kosovo",
];

// Lige koje ne želimo da prikazujemo u dropdown listama
const HIDDEN_LEAGUE_IDS = [333]; // Second Chance - automatski ubacuje FPL, korisnici ne treba da je vide

function sortLeagues(leagues) {
  return [...leagues].filter(lg => !HIDDEN_LEAGUE_IDS.includes(lg.id)).sort((a, b) => {
    const aIsPrivate = a.league_type === "x";
    const bIsPrivate = b.league_type === "x";
    if (aIsPrivate && !bIsPrivate) return -1;
    if (!aIsPrivate && bIsPrivate) return 1;
    if (!aIsPrivate && !bIsPrivate) {
      const aIsOverall = a.name === "Overall";
      const bIsOverall = b.name === "Overall";
      if (aIsOverall && !bIsOverall) return -1;
      if (!aIsOverall && bIsOverall) return 1;
      const aIsCountry = COUNTRY_NAMES.some(c => a.name.includes(c));
      const bIsCountry = COUNTRY_NAMES.some(c => b.name.includes(c));
      if (aIsCountry && !bIsCountry) return -1;
      if (!aIsCountry && bIsCountry) return 1;
    }
    return 0;
  });
}

// ============ SHARE CARD COMPONENTS ============
// Instagram Story format: 1080x1920

// Card 1: League Leader
function ShareCardLeader({ data, t, cardRef }) {
  return (
    <div ref={cardRef} style={{
      width: 540, height: 960,
      position: "relative", overflow: "hidden",
      background: "linear-gradient(135deg, #1e40af 0%, #2563eb 50%, #60a5fa 100%)",
      color: "#fff",
      fontFamily: "var(--sans)",
      padding: "60px 48px",
      display: "flex", flexDirection: "column",
      boxSizing: "border-box"
    }}>
      <div style={{ position: "absolute", top: -150, right: -120, width: 520, height: 520, background: "radial-gradient(circle, rgba(255,255,255,0.18) 0%, transparent 70%)", borderRadius: "50%" }}/>
      <div style={{ position: "absolute", bottom: -200, left: -150, width: 600, height: 600, background: "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)", borderRadius: "50%" }}/>

      <div style={{ display: "flex", alignItems: "center", gap: 12, zIndex: 2, position: "relative" }}>
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
          <circle cx="18" cy="18" r="16" fill="#fff"/>
          <path d="M18 11 L22.5 14.3 L20.8 19.5 L15.2 19.5 L13.5 14.3 Z" fill="#2563eb"/>
          <path d="M18 11 L22.5 14.3 L25.5 11.5 L22 7.5 L14 7.5 L10.5 11.5 L13.5 14.3 Z" fill="none" stroke="#2563eb" strokeWidth="1.2"/>
          <path d="M15.2 19.5 L13 25 M20.8 19.5 L23 25 M15.2 19.5 L20.8 19.5 M13 25 L23 25" stroke="#2563eb" strokeWidth="1.2" strokeLinecap="round"/>
        </svg>
        <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: "1px" }}>FPL CENTER</div>
      </div>

      <div style={{ fontSize: 270, textAlign: "center", margin: "50px 0 20px", lineHeight: 1, zIndex: 2, position: "relative", filter: "drop-shadow(0 12px 30px rgba(0,0,0,0.3))" }}>🥇</div>
      <div style={{ fontSize: 62, fontWeight: 900, textAlign: "center", marginTop: 16, lineHeight: 1.05, zIndex: 2, position: "relative", letterSpacing: "-1px" }}>{t.shareCardHeadline1}</div>
      <div style={{ textAlign: "center", fontSize: 20, fontWeight: 600, opacity: 0.85, marginTop: 20, letterSpacing: "0.5px", textTransform: "uppercase", zIndex: 2, position: "relative" }}>{data.leagueName} · GW {data.gwNum}</div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: "auto", zIndex: 2, position: "relative" }}>
        <div style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 16, padding: "20px 22px" }}>
          <div style={{ fontSize: 40, fontWeight: 800, fontFamily: "var(--sans)" }}>{data.gwNet}</div>
          <div style={{ fontSize: 14, textTransform: "uppercase", letterSpacing: "1.2px", opacity: 0.75, marginTop: 4, fontWeight: 600 }}>{t.shareCardGWNet}</div>
        </div>
        <div style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 16, padding: "20px 22px" }}>
          <div style={{ fontSize: 40, fontWeight: 800, fontFamily: "var(--sans)" }}>{data.totalPts.toLocaleString()}</div>
          <div style={{ fontSize: 14, textTransform: "uppercase", letterSpacing: "1.2px", opacity: 0.75, marginTop: 4, fontWeight: 600 }}>{t.shareCardTotal}</div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 15, opacity: 0.6, fontWeight: 500, marginTop: 28, zIndex: 2, position: "relative" }}>
        <span>{data.handle}</span>
        <span style={{ fontFamily: "var(--mono)", letterSpacing: "0.5px" }}>fplcenter.com</span>
      </div>
    </div>
  );
}

// Card 2: GW Winner
function ShareCardGWWinner({ data, t, cardRef }) {
  return (
    <div ref={cardRef} style={{
      width: 540, height: 960,
      position: "relative", overflow: "hidden",
      background: "linear-gradient(180deg, #052e16 0%, #064e3b 100%)",
      color: "#fff",
      fontFamily: "var(--sans)",
      padding: "60px 48px",
      display: "flex", flexDirection: "column",
      boxSizing: "border-box"
    }}>
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 30% 20%, rgba(34,197,94,0.3) 0%, transparent 50%)", pointerEvents: "none" }}/>

      <div style={{ display: "flex", alignItems: "center", gap: 12, zIndex: 2, position: "relative" }}>
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
          <circle cx="18" cy="18" r="16" fill="#22c55e"/>
          <path d="M18 11 L22.5 14.3 L20.8 19.5 L15.2 19.5 L13.5 14.3 Z" fill="#fff"/>
          <path d="M18 11 L22.5 14.3 L25.5 11.5 L22 7.5 L14 7.5 L10.5 11.5 L13.5 14.3 Z" fill="none" stroke="#fff" strokeWidth="1.2"/>
          <path d="M15.2 19.5 L13 25 M20.8 19.5 L23 25 M15.2 19.5 L20.8 19.5 M13 25 L23 25" stroke="#fff" strokeWidth="1.2" strokeLinecap="round"/>
        </svg>
        <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: "1px" }}>FPL CENTER</div>
      </div>

      <svg width="120" height="105" viewBox="0 0 80 70" fill="none" style={{ margin: "45px auto 18px", display: "block", zIndex: 2, position: "relative", filter: "drop-shadow(0 8px 22px rgba(0,0,0,0.3))" }}>
        <path d="M 10 50 L 8 22 L 22 36 L 32 12 L 40 28 L 48 12 L 58 36 L 72 22 L 70 50 Z" fill="#fde047" stroke="#f59e0b" strokeWidth="2" strokeLinejoin="round"/>
        <rect x="10" y="48" width="60" height="10" rx="2" fill="#f59e0b" stroke="#b45309" strokeWidth="1.5"/>
        <circle cx="22" cy="36" r="3" fill="#dc2626"/>
        <circle cx="40" cy="28" r="3" fill="#2563eb"/>
        <circle cx="58" cy="36" r="3" fill="#dc2626"/>
      </svg>

      <div style={{ fontSize: 68, fontWeight: 900, textAlign: "center", marginTop: 4, lineHeight: 1.05, zIndex: 2, position: "relative", letterSpacing: "-1.5px", color: "#86efac" }}>{t.shareCardHeadline2}</div>
      <div style={{ textAlign: "center", fontSize: 18, opacity: 0.85, marginTop: 14, letterSpacing: "2px", textTransform: "uppercase", fontWeight: 600, zIndex: 2, position: "relative" }}>GW {data.gwNum} · {data.leagueName}</div>

      <div style={{ textAlign: "center", margin: "44px 0", zIndex: 2, position: "relative" }}>
        <div style={{ fontSize: 140, fontWeight: 900, fontFamily: "var(--sans)", lineHeight: 1, letterSpacing: "-4px", color: "#86efac" }}>{data.gwNet}</div>
        <div style={{ fontSize: 17, opacity: 0.75, textTransform: "uppercase", letterSpacing: "2.5px", marginTop: 10, fontWeight: 600 }}>{t.shareCardPtsThisGW}</div>
      </div>

      <div style={{ background: "rgba(134,239,172,0.1)", border: "1px solid rgba(134,239,172,0.25)", borderRadius: 20, padding: "28px 26px", marginTop: "auto", zIndex: 2, position: "relative", textAlign: "center" }}>
        <div style={{ fontSize: 14, opacity: 0.75, textTransform: "uppercase", letterSpacing: "2px", fontWeight: 700, marginBottom: 8, color: "#86efac" }}>🏆 Manager</div>
        <div style={{ fontSize: 36, fontWeight: 900, color: "#fff", lineHeight: 1.1, letterSpacing: "-0.5px" }}>{data.managerName}</div>
        <div style={{ fontSize: 16, opacity: 0.7, marginTop: 6, fontFamily: "var(--mono)", fontWeight: 600 }}>{data.teamName}</div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", fontSize: 15, opacity: 0.6, fontWeight: 500, marginTop: 28, zIndex: 2, position: "relative" }}>
        <span style={{ fontFamily: "var(--mono)", letterSpacing: "0.5px" }}>fplcenter.com</span>
      </div>
    </div>
  );
}

// Card 3: Captain King
function ShareCardCaptain({ data, t, cardRef }) {
  return (
    <div ref={cardRef} style={{
      width: 540, height: 960,
      position: "relative", overflow: "hidden",
      background: "linear-gradient(135deg, #7f1d1d 0%, #dc2626 50%, #fca5a5 100%)",
      color: "#fff",
      fontFamily: "var(--sans)",
      padding: "60px 48px",
      display: "flex", flexDirection: "column",
      boxSizing: "border-box"
    }}>
      <div style={{ position: "absolute", top: -120, right: -120, width: 520, height: 520, background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)", borderRadius: "50%" }}/>

      <div style={{ display: "flex", alignItems: "center", gap: 12, zIndex: 2, position: "relative" }}>
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
          <circle cx="18" cy="18" r="16" fill="#fff"/>
          <path d="M18 11 L22.5 14.3 L20.8 19.5 L15.2 19.5 L13.5 14.3 Z" fill="#dc2626"/>
          <path d="M18 11 L22.5 14.3 L25.5 11.5 L22 7.5 L14 7.5 L10.5 11.5 L13.5 14.3 Z" fill="none" stroke="#dc2626" strokeWidth="1.2"/>
          <path d="M15.2 19.5 L13 25 M20.8 19.5 L23 25 M15.2 19.5 L20.8 19.5 M13 25 L23 25" stroke="#dc2626" strokeWidth="1.2" strokeLinecap="round"/>
        </svg>
        <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: "1px" }}>FPL CENTER</div>
      </div>

      <div style={{ margin: "40px auto 18px", display: "flex", justifyContent: "center", zIndex: 2, position: "relative" }}>
        <div style={{ width: 140, height: 140, background: "rgba(255,255,255,0.2)", border: "4px solid rgba(255,255,255,0.4)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 80, fontWeight: 900, fontFamily: "var(--sans)" }}>C</div>
      </div>

      <div style={{ fontSize: 72, fontWeight: 900, textAlign: "center", marginTop: 24, lineHeight: 1, zIndex: 2, position: "relative", letterSpacing: "-1.5px" }}>{t.shareCardHeadline3}</div>
      <div style={{ textAlign: "center", fontSize: 36, fontWeight: 700, marginTop: 18, zIndex: 2, position: "relative" }}>{data.captainName}</div>

      <div style={{ textAlign: "center", fontSize: 110, fontWeight: 900, fontFamily: "var(--sans)", marginTop: 22, lineHeight: 1, zIndex: 2, position: "relative" }}>{data.captainPts}</div>
      <div style={{ fontSize: 17, opacity: 0.8, textTransform: "uppercase", letterSpacing: "2.5px", marginTop: 8, textAlign: "center", fontWeight: 600, zIndex: 2, position: "relative" }}>{t.shareCardCapPts}</div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginTop: "auto", zIndex: 2, position: "relative" }}>
        <div style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 14, padding: "14px 10px", textAlign: "center" }}>
          <div style={{ fontSize: 26, marginBottom: 4 }}>⚽</div>
          <div style={{ fontSize: 24, fontWeight: 800, fontFamily: "var(--sans)" }}>{data.captainGoals}</div>
          <div style={{ fontSize: 12, opacity: 0.75, textTransform: "uppercase", letterSpacing: "0.8px", marginTop: 3, fontWeight: 600 }}>{t.shareCardGoals}</div>
        </div>
        <div style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 14, padding: "14px 10px", textAlign: "center" }}>
          <div style={{ fontSize: 26, marginBottom: 4 }}>🎯</div>
          <div style={{ fontSize: 24, fontWeight: 800, fontFamily: "var(--sans)" }}>{data.captainAssists}</div>
          <div style={{ fontSize: 12, opacity: 0.75, textTransform: "uppercase", letterSpacing: "0.8px", marginTop: 3, fontWeight: 600 }}>{t.shareCardAssists}</div>
        </div>
        <div style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 14, padding: "14px 10px", textAlign: "center" }}>
          <div style={{ fontSize: 26, marginBottom: 4 }}>⭐</div>
          <div style={{ fontSize: 24, fontWeight: 800, fontFamily: "var(--sans)" }}>+{data.captainBonus}</div>
          <div style={{ fontSize: 12, opacity: 0.75, textTransform: "uppercase", letterSpacing: "0.8px", marginTop: 3, fontWeight: 600 }}>{t.shareCardBonus}</div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 15, opacity: 0.6, fontWeight: 500, marginTop: 28, zIndex: 2, position: "relative" }}>
        <span>{data.handle}</span>
        <span style={{ fontFamily: "var(--mono)", letterSpacing: "0.5px" }}>fplcenter.com</span>
      </div>
    </div>
  );
}

// Card 4: Overall Rank Milestone
function ShareCardRank({ data, t, cardRef }) {
  // Tier font size - keyed by EXACT label, not character count. Character count
  // is not a safe proxy for pixel width in a proportional font: "TOP 50K" and
  // "TOP 10" are both 6-7 chars but need very different sizes. Measured 15.09.2026
  // against the real Onest 900 file + letter-spacing -3px + proportional-nums,
  // available width 444 CSS px (card 540 - 2*48 padding). Each value keeps a
  // >=4px safety margin under the measured per-label maximum.
  const TIER_FONT_SIZES = {
    "TOP 5": 145,
    "TOP 10": 120, "TOP 20": 120, "TOP 50": 120, "TOP 1K": 120, "TOP 5K": 120, "TOP 1M": 120,
    "TOP 100": 102, "TOP 200": 102, "TOP 500": 102, "TOP 10K": 102, "TOP 20K": 102, "TOP 50K": 102,
    "TOP 100K": 88, "TOP 200K": 88, "TOP 500K": 88,
  };
  const tierFontSize = TIER_FONT_SIZES[data.tier] || 88;

  return (
    <div ref={cardRef} style={{
      width: 540, height: 960,
      position: "relative", overflow: "hidden",
      background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
      color: "#fff",
      fontFamily: "var(--sans)",
      padding: "60px 48px",
      display: "flex", flexDirection: "column",
      boxSizing: "border-box"
    }}>
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 45%, rgba(251,191,36,0.18) 0%, transparent 60%)", pointerEvents: "none" }}/>

      <div style={{ display: "flex", alignItems: "center", gap: 12, zIndex: 2, position: "relative" }}>
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
          <circle cx="18" cy="18" r="16" fill="#fbbf24"/>
          <path d="M18 11 L22.5 14.3 L20.8 19.5 L15.2 19.5 L13.5 14.3 Z" fill="#0f172a"/>
          <path d="M18 11 L22.5 14.3 L25.5 11.5 L22 7.5 L14 7.5 L10.5 11.5 L13.5 14.3 Z" fill="none" stroke="#0f172a" strokeWidth="1.2"/>
          <path d="M15.2 19.5 L13 25 M20.8 19.5 L23 25 M15.2 19.5 L20.8 19.5 M13 25 L23 25" stroke="#0f172a" strokeWidth="1.2" strokeLinecap="round"/>
        </svg>
        <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: "1px" }}>FPL CENTER</div>
      </div>

      <div style={{ marginTop: 48, textAlign: "center", zIndex: 2, position: "relative" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#94a3b8", letterSpacing: "3px", textTransform: "uppercase", marginBottom: 10 }}>Manager</div>
        <div style={{ fontSize: 36, fontWeight: 900, lineHeight: 1.1, letterSpacing: "-0.5px", color: "#ffffff" }}>{data.managerName}</div>
        <div style={{ fontSize: 16, fontFamily: "var(--mono)", color: "#94a3b8", marginTop: 6, fontWeight: 600 }}>{data.teamName}</div>
      </div>

      <div style={{ textAlign: "center", marginTop: 44, zIndex: 2, position: "relative", fontSize: 13, fontWeight: 700, color: "#94a3b8", letterSpacing: "3px", textTransform: "uppercase" }}>Overall Rank</div>
      <div style={{ textAlign: "center", fontSize: tierFontSize, fontWeight: 900, color: "#fbbf24", lineHeight: 1, letterSpacing: "-3px", zIndex: 2, position: "relative", marginTop: 10, marginBottom: 10, textShadow: "0 6px 24px rgba(251,191,36,0.35)", whiteSpace: "nowrap", fontVariantNumeric: "proportional-nums" }}>{data.tier}</div>
      <div style={{ textAlign: "center", fontSize: 18, color: "#fbbf24", lineHeight: 1, marginTop: 14, zIndex: 2, position: "relative" }}>★</div>
      <div style={{ textAlign: "center", fontSize: 26, fontFamily: "var(--sans)", color: "#cbd5e1", marginTop: 18, zIndex: 2, position: "relative", fontWeight: 600 }}>#{data.exactRank.toLocaleString()} worldwide</div>
      <div style={{ textAlign: "center", fontSize: 20, color: "#94a3b8", marginTop: 10, zIndex: 2, position: "relative" }}>{data.percentileText}</div>

      <div style={{ marginTop: "auto", marginBottom: 30, padding: "20px 24px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 16, zIndex: 2, position: "relative" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#fbbf24", textTransform: "uppercase", letterSpacing: "2px", marginBottom: 12, textAlign: "center" }}>✨ Best GW this season</div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 12 }}>
          <span style={{ fontSize: 36, fontWeight: 900, fontFamily: "var(--sans)", lineHeight: 1, color: "#fff" }}>{data.bestGwPoints}</span>
          <span style={{ fontSize: 14, color: "#94a3b8", letterSpacing: "1px", textTransform: "uppercase", fontWeight: 700 }}>GW {data.bestGwNum}</span>
        </div>
        {data.captainName && (
          <div style={{ textAlign: "center", fontSize: 14, marginTop: 10, color: "#cbd5e1" }}>
            Captain: <span style={{ fontWeight: 700, color: "#fff" }}>{data.captainName}</span> <span style={{ color: "#64748b", margin: "0 4px" }}>·</span> <span style={{ fontFamily: "var(--sans)", color: "#fbbf24", fontWeight: 700 }}>{data.captainPts} pts</span>
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", fontSize: 15, color: "#64748b", fontWeight: 500, zIndex: 2, position: "relative" }}>
        <span style={{ fontFamily: "var(--mono)", letterSpacing: "0.5px" }}>fplcenter.com</span>
      </div>
    </div>
  );
}

// Card 5: Dashboard — available to all managers
function ShareCardDashboard({ data, t, cardRef }) {
  const isLeader = data.position === 1;
  const isTop3 = data.position <= 3;
  
  // Medal colors per position
  const medalColors = {
    1: { fill: "#fbbf24", center: "#fef3c7", text: "#451a03", stroke: "#451a03" },
    2: { fill: "#cbd5e1", center: "#f1f5f9", text: "#0f172a", stroke: "#0f172a" },
    3: { fill: "#cd7f32", center: "#fdba74", text: "#3a0a02", stroke: "#3a0a02" },
  };
  const medal = isTop3 ? medalColors[data.position] : null;
  
  return (
    <div ref={cardRef} style={{
      width: 540, height: 960,
      position: "relative", overflow: "hidden",
      background: "linear-gradient(135deg, #1e3a8a 0%, #312e81 50%, #1e1b4b 100%)",
      color: "#fff",
      fontFamily: "var(--sans)",
      padding: "60px 48px",
      display: "flex", flexDirection: "column",
      boxSizing: "border-box"
    }}>
      <div style={{ position: "absolute", top: -150, right: -120, width: 520, height: 520, background: "radial-gradient(circle, rgba(96,165,250,0.25) 0%, transparent 70%)", borderRadius: "50%" }}/>
      <div style={{ position: "absolute", bottom: -200, left: -150, width: 600, height: 600, background: "radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 70%)", borderRadius: "50%" }}/>

      {/* Header: logo + GW badge */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 2, position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
            <circle cx="18" cy="18" r="16" fill="#fff"/>
            <path d="M18 11 L22.5 14.3 L20.8 19.5 L15.2 19.5 L13.5 14.3 Z" fill="#1e3a8a"/>
            <path d="M18 11 L22.5 14.3 L25.5 11.5 L22 7.5 L14 7.5 L10.5 11.5 L13.5 14.3 Z" fill="none" stroke="#1e3a8a" strokeWidth="1.2"/>
            <path d="M15.2 19.5 L13 25 M20.8 19.5 L23 25 M15.2 19.5 L20.8 19.5 M13 25 L23 25" stroke="#1e3a8a" strokeWidth="1.2" strokeLinecap="round"/>
          </svg>
          <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: "1px" }}>FPL CENTER</div>
        </div>
        <div style={{ fontSize: 18, padding: "8px 16px", background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, fontWeight: 600 }}>GW {data.gwNum}</div>
      </div>

      {/* League name + team name + manager name */}
      <div style={{ marginTop: 60, zIndex: 2, position: "relative" }}>
        <div style={{ fontSize: 22, opacity: 0.65, marginBottom: 8, fontWeight: 500, letterSpacing: "0.5px", textTransform: "uppercase" }}>{data.leagueName}</div>
        <div style={{ fontSize: 46, fontWeight: 700, marginBottom: 8, lineHeight: 1.1 }}>{data.teamName}</div>
        <div style={{ fontSize: 22, opacity: 0.6, fontWeight: 500 }}>{data.managerName}</div>
      </div>

      {/* Position display - centered */}
      {(() => {
        const formatRankShort = (n) => {
          if (!n) return "—";
          if (n < 1000) return n.toLocaleString();
          if (n < 1000000) return Math.round(n / 1000) + "K";
          return (n / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
        };
        const formatTotal = (n) => {
          if (!n) return "";
          if (n < 1000) return n.toLocaleString();
          if (n < 1000000) return Math.round(n / 1000) + "K";
          return (n / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
        };
        const isOverall = data.isOverallGlobalLeague;
        // Overall liga: koristi K/M format sa manjim fontom + pill ispod
        // Normalna liga: full broj sa auto-shrink na osnovu sirine
        const posStr = isOverall ? `#${formatRankShort(data.position)}` : `#${data.position.toLocaleString()}`;
        const totalStr = `/${formatTotal(data.totalManagers)}`;
        let mainSize, slashSize;
        if (isOverall) {
          mainSize = 56; slashSize = 22;
        } else {
          const totalChars = posStr.length + totalStr.length;
          if (totalChars <= 6) { mainSize = 180; slashSize = 56; }
          else if (totalChars <= 8) { mainSize = 140; slashSize = 44; }
          else if (totalChars <= 10) { mainSize = 110; slashSize = 36; }
          else if (totalChars <= 13) { mainSize = 86; slashSize = 30; }
          else { mainSize = 68; slashSize = 26; }
        }
        const medalMain = isTop3 ? Math.round(mainSize * 0.6) : mainSize;
        const medalSlash = isTop3 ? Math.round(slashSize * 0.65) : slashSize;
        // Percentile pill boja prema bucket-u
        const bucket = data.percentileBucket;
        let pillBg, pillBorder, pillColor;
        if (bucket && bucket <= 1) { pillBg = "rgba(251,191,36,0.18)"; pillBorder = "rgba(251,191,36,0.4)"; pillColor = "#fbbf24"; }
        else if (bucket && bucket <= 10) { pillBg = "rgba(134,239,172,0.18)"; pillBorder = "rgba(134,239,172,0.4)"; pillColor = "#86efac"; }
        else { pillBg = "rgba(96,165,250,0.18)"; pillBorder = "rgba(96,165,250,0.4)"; pillColor = "#60a5fa"; }
        return (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", marginTop: 60, marginBottom: 40, zIndex: 2, position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 28 }}>
              {isTop3 && medal ? (
                <>
                  <svg width="170" height="220" viewBox="0 0 80 100">
                    <defs>
                      <radialGradient id={`medFill${data.position}`} cx="0.35" cy="0.3">
                        <stop offset="0%" stopColor="#fff"/>
                        <stop offset="35%" stopColor={medal.fill}/>
                        <stop offset="100%" stopColor={medal.stroke}/>
                      </radialGradient>
                    </defs>
                    <path d="M22 6 L34 38 L26 42 L14 10 Z" fill="#dc2626"/>
                    <path d="M58 6 L46 38 L54 42 L66 10 Z" fill="#b91c1c"/>
                    <path d="M22 6 L26 42 L34 38 L26 6 Z" fill="#7f1d1d"/>
                    <path d="M58 6 L54 42 L46 38 L54 6 Z" fill="#7f1d1d"/>
                    <circle cx="40" cy="60" r="26" fill={`url(#medFill${data.position})`} stroke={medal.stroke} strokeWidth="1.5"/>
                    <circle cx="40" cy="60" r="20" fill={medal.center} opacity="0.35"/>
                    <circle cx="40" cy="60" r="14" fill="none" stroke={medal.stroke} strokeWidth="0.5" opacity="0.5"/>
                    <text x="40" y="68" textAnchor="middle" fontSize="22" fontWeight="700" fill={medal.text} fontFamily="var(--sans)">{data.position}</text>
                    <ellipse cx="33" cy="50" rx="6" ry="3" fill="#fff" opacity="0.4"/>
                  </svg>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span style={{ fontSize: medalMain, fontWeight: 800, fontFamily: "var(--sans)", lineHeight: 1, letterSpacing: "-3px" }}>{posStr}</span>
                    <span style={{ fontSize: medalSlash, opacity: 0.55, fontWeight: 500 }}>{totalStr}</span>
                  </div>
                </>
              ) : (
                <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
                  <span style={{ fontSize: mainSize, fontWeight: 800, fontFamily: "var(--sans)", lineHeight: 1, letterSpacing: "-2px" }}>{posStr}</span>
                  <span style={{ fontSize: slashSize, opacity: 0.55, fontWeight: 500 }}>{totalStr}</span>
                </div>
              )}
            </div>
            {isOverall && bucket && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", background: pillBg, border: `1px solid ${pillBorder}`, borderRadius: 999, marginTop: 18 }}>
                <span style={{ fontSize: 13, color: pillColor, fontWeight: 700, letterSpacing: "1.2px" }}>★ TOP {bucket}%</span>
              </div>
            )}
          </div>
        );
      })()}

      {/* Stats grid - 4 statkartice za normalnu ligu, 2 za overall (jer rank je vec hero) */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: "auto", zIndex: 2, position: "relative" }}>
        <div style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 16, padding: "20px 22px" }}>
          <div style={{ fontSize: 38, fontWeight: 800, fontFamily: "var(--sans)" }}>{data.gwNet}</div>
          <div style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "1.2px", opacity: 0.7, marginTop: 4, fontWeight: 600 }}>GW NET</div>
        </div>
        <div style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 16, padding: "20px 22px" }}>
          <div style={{ fontSize: 38, fontWeight: 800, fontFamily: "var(--sans)" }}>{data.totalPts.toLocaleString()}</div>
          <div style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "1.2px", opacity: 0.7, marginTop: 4, fontWeight: 600 }}>TOTAL</div>
        </div>
        {!data.isOverallGlobalLeague && (
          <>
            <div style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 16, padding: "20px 22px" }}>
              <div style={{ fontSize: 38, fontWeight: 800, fontFamily: "var(--sans)", color: isLeader ? "#86efac" : "#fca5a5" }}>{isLeader ? "▲" : "▼"} {data.deltaValue}</div>
              <div style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "1.2px", opacity: 0.7, marginTop: 4, fontWeight: 600 }}>{isLeader ? "AHEAD OF NEXT" : "BEHIND LEADER"}</div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 16, padding: "20px 22px" }}>
              <div style={{ fontSize: 38, fontWeight: 800, fontFamily: "var(--sans)" }}>{data.overallRankFormatted}</div>
              <div style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "1.2px", opacity: 0.7, marginTop: 4, fontWeight: 600 }}>OVERALL RANK</div>
            </div>
          </>
        )}
      </div>

      {/* Watermark */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 15, opacity: 0.55, fontWeight: 500, marginTop: 28, zIndex: 2, position: "relative" }}>
        <span>{data.handle}</span>
        <span style={{ fontFamily: "var(--mono)", letterSpacing: "0.5px" }}>fplcenter.com</span>
      </div>
    </div>
  );
}

// Share Card Row — shows preview + share/download buttons or locked state
function ShareCardRow({ card, t, lang }) {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const cardRef = useRef(null);
  const CardComponent = card.component;

  const generateAndShare = async (action) => {
    if (!card.unlocked) return;
    setGenerating(true);
    setError(null);
    try {
      // Dynamically import html2canvas
      const html2canvas = (await import("html2canvas")).default;
      if (!cardRef.current) { setGenerating(false); return; }

      // Wait for render to complete (especially web fonts)
      await new Promise(r => setTimeout(r, 100));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: card.bgColor || "#ffffff",
        useCORS: true,
        logging: false,
        width: 540,
        height: 960,
        windowWidth: 540,
        windowHeight: 960,
        imageTimeout: 3000,
      });

      canvas.toBlob(async (blob) => {
        if (!blob) { setGenerating(false); return; }
        const filename = `fplcenter-${card.id}-${Date.now()}.jpg`;

        trackEvent("Share Image Generated", {
          card: card.id,
          method: action === "share" && navigator.share && navigator.canShare ? "native_share" : "download",
        });

        if (action === "share" && navigator.share && navigator.canShare) {
          const file = new File([blob], filename, { type: "image/jpeg" });
          if (navigator.canShare({ files: [file] })) {
            try {
              await navigator.share({
                files: [file],
                title: "FPL Center",
                text: "fplcenter.com",
              });
              setGenerating(false);
              return;
            } catch (e) {
              // User cancelled or not supported — fall through to download
            }
          }
        }
        // v52: Download fallback + zastita za in-app pretrazivac.
        // U Instagram/Facebook webview-u <a download> cesto tiho ne uradi nista:
        // slika se generise, korisnik ne dobije nista, i mi to u Plausible-u
        // vidimo kao "Share Image Generated" — dakle kao uspeh koji nije uspeh.
        // Ako smo u webview-u a Web Share API ne postoji, radije kazemo coveku
        // sta da uradi nego da se pravimo da je proslo.
        if (IN_APP_BROWSER && !(navigator.share && navigator.canShare)) {
          trackEvent("Share Blocked In App", { card: card.id, browser: IN_APP_BROWSER });
          setError(
            lang === "sr"
              ? "Slika ne moze da se sacuva iz Instagram pregledaca. Otvori fplcenter.com u Chrome-u ili Safari-ju (meni ⋯ gore desno → Otvori u pregledacu)."
              : "Images can't be saved from the Instagram browser. Open fplcenter.com in Chrome or Safari (⋯ menu, top right → Open in browser)."
          );
          setGenerating(false);
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setGenerating(false);
      }, "image/jpeg", 0.92);
    } catch (e) {
      console.error("Share error:", e);
      setError(lang === "sr" ? "Greska pri generisanju slike" : "Error generating image");
      setGenerating(false);
    }
  };

  return (
    <div style={{
      padding: 12,
      background: card.unlocked ? "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)" : "#f8fafc",
      border: card.unlocked ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
      borderRadius: 12,
      opacity: card.unlocked ? 1 : 0.7,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 48, height: 48,
          borderRadius: 10,
          background: card.unlocked ? "#fff" : "#f1f5f9",
          border: card.unlocked ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 28,
          flexShrink: 0,
          filter: card.unlocked ? "none" : "grayscale(80%)",
        }}>
          {card.emoji}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: card.unlocked ? "#0f172a" : "#64748b" }}>{card.title}</div>
          <div style={{ fontSize: 11, color: card.unlocked ? "#16a34a" : "#94a3b8", fontWeight: 600, marginTop: 2 }}>
            {card.unlocked ? ("✓ " + t.shareCardUnlocked) : ("🔒 " + card.lockReason)}
          </div>
        </div>
        {card.unlocked && (
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button
              onClick={() => generateAndShare("share")}
              disabled={generating}
              style={{
                padding: "8px 12px",
                background: "#2563eb",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontSize: 11,
                fontWeight: 700,
                cursor: generating ? "default" : "pointer",
                fontFamily: "var(--sans)",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                opacity: generating ? 0.6 : 1,
              }}
            >
              {generating ? "..." : (
                <>
                  <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                    <path d="M10 4.5L7 1.5L4 4.5M7 1.5V9M2.5 9.5v2c0 0.5 0.5 1 1 1h7c0.5 0 1-0.5 1-1v-2" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {t.share}
                </>
              )}
            </button>
          </div>
        )}
      </div>
      {error && <div style={{ fontSize: 10, color: "#dc2626", marginTop: 6 }}>{error}</div>}

      {/* Hidden card used for html2canvas capture */}
      {card.unlocked && (
        <div style={{ position: "absolute", left: -99999, top: 0, pointerEvents: "none" }}>
          <CardComponent data={card.data} t={t} cardRef={cardRef} />
        </div>
      )}
    </div>
  );
}

// ============ H2H COMPARE ============
function truncateName(name, maxLen = 11) {
  if (!name) return "";
  if (name.length <= maxLen) return name;
  if (name.includes("-")) {
    const [first, second] = name.split("-");
    if (first.length <= maxLen - 2) return `${first}-${second[0]}.`;
  }
  return name.slice(0, maxLen - 1) + ".";
}

// NudgeBanner - prikazuje real-time banner za Lider / GW Winner / Captain triggere
// Hijerarhija: Lider > Winner > Captain (samo jedan banner se prikazuje)
// Lider: real-time, tekst "Trenutno"
// Winner: SAMO kad je GW finalized (data_checked === true)
// Captain: real-time cim threshold ispunjen (>=12 NET poena, sa C/TC mnoziocem)
// X dugme = per-GW dismiss (sve banner-e blokira za taj GW)
function NudgeBanner({
  bannerType,    // 'leader' | 'winner' | 'captain' | null
  data,          // { leagueName, count, pts, captainName, isTC }
  t,
  onShare,
  onDismiss,
}) {
  if (!bannerType) return null;

  // Boje po tipu (Nivo 1: jaca pozadina + jaci border)
  const styles = {
    leader: {
      bg: "#fef3c7",
      border: "#fbbf24",
      title: "#78350f",
      sub: "#92400e",
      xColor: "#92400e",
    },
    winner: {
      bg: "#ecfdf5",
      border: "#34d399",
      title: "#064e3b",
      sub: "#047857",
      xColor: "#047857",
    },
    captain: {
      bg: "#faf5ff",
      border: "#c084fc",
      title: "#581c87",
      sub: "#6b21a8",
      xColor: "#6b21a8",
    },
    default: {
      bg: "#f8fafc",
      border: "#e2e8f0",
      title: "#334155",
      sub: "#64748b",
      xColor: "#94a3b8",
    },
  };
  const s = styles[bannerType];

  // Title i subtitle text formatting
  let titleText = "";
  let subText = "";
  if (bannerType === "leader") {
    titleText = `${t.nudgeLeaderTitle} ${data.leagueName}`;
    subText = (t.nudgeLeaderSub || "").replace("{count}", data.count);
  } else if (bannerType === "winner") {
    titleText = `${t.nudgeWinnerTitle} ${data.leagueName}`;
    subText = (t.nudgeWinnerSub || "").replace("{count}", data.count).replace("{pts}", data.pts);
  } else if (bannerType === "captain") {
    titleText = data.isTC ? t.nudgeCaptainTitleTC : t.nudgeCaptainTitle;
    subText = (t.nudgeCaptainSub || "").replace("{name}", data.captainName).replace("{pts}", data.pts);
  } else if (bannerType === "default") {
    titleText = t.nudgeDefaultTitle;
    subText = (t.nudgeDefaultSub || "").replace("{gw}", data.gwId);
  }

  // Ikona po tipu (Nivo 1: 32px umesto 26px)
  let icon = null;
  if (bannerType === "leader") {
    icon = (
      <svg width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="18" r="9" fill="#fbbf24" stroke="#d97706" strokeWidth="1.5"/><circle cx="16" cy="18" r="6" fill="#fcd34d"/><text x="16" y="22" textAnchor="middle" fontSize="9" fontWeight="700" fill="#92400e">1</text><path d="M9 4 L11 12 L16 8 L21 12 L23 4" fill="#dc2626" stroke="#991b1b" strokeWidth="0.5"/></svg>
    );
  } else if (bannerType === "winner") {
    icon = (
      <div style={{ width: 32, height: 32, background: "#059669", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 16, fontFamily: "var(--sans)" }}>⚡</div>
    );
  } else if (bannerType === "captain") {
    icon = (
      <div style={{ width: 32, height: 32, background: "#7c3aed", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 15, fontWeight: 700, fontFamily: "var(--sans)" }}>C</div>
    );
  } else if (bannerType === "default") {
    icon = (
      <svg width="32" height="32" viewBox="0 0 32 32">
        <path d="M16 6 L19 13.5 L27 14.3 L21 19.5 L22.8 27 L16 23 L9.2 27 L11 19.5 L5 14.3 L13 13.5 Z" fill="none" stroke="#94a3b8" strokeWidth="1.4" strokeLinejoin="round"/>
      </svg>
    );
  }

  return (
    <div style={{
      background: s.bg,
      border: `1.5px solid ${s.border}`,
      borderRadius: 10,
      padding: "11px 12px",
      display: "flex",
      alignItems: "center",
      gap: 10,
      margin: "8px 12px 0",
      fontFamily: "var(--sans)",
    }}>
      <div style={{ width: 32, height: 32, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: s.title, lineHeight: 1.25, letterSpacing: "-0.1px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {titleText}
        </div>
        <div style={{ fontSize: 11, color: s.sub, lineHeight: 1.25, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {subText}
        </div>
      </div>
      <button
        onClick={onShare}
        style={{
          background: "#1d1d1f",
          color: "white",
          border: 0,
          borderRadius: 980,
          padding: "7px 13px",
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          flexShrink: 0,
          fontFamily: "var(--sans)",
        }}
      >
        {t.nudgeShare}
      </button>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: "transparent",
          border: 0,
          color: s.xColor,
          padding: 4,
          cursor: "pointer",
          fontSize: 18,
          lineHeight: 1,
          opacity: 0.7,
          flexShrink: 0,
          fontFamily: "var(--sans)",
        }}
      >
        ×
      </button>
    </div>
  );
}


// ============================================================================
// v76 (01.09.2026) · DELJENJE DUELA
//
// ZASTO POSTOJI (merenja 31.08): kartice uspeha su dale 1 sliku na 131 prikazan
// baner. Brag kartica je EMITOVANJE ("pogledajte mene") i ljudi je odbijaju.
// Duel slika je PORUKA SA ADRESOM — ti protiv jednog imenovanog coveka, ide u
// grupu mini-lige koja vec postoji. Publika za to postoji: Compare Started je
// 65% svih koji otvore manager modal.
//
// MERILO USPEHA, da se posle ne raspravlja: slika plana ima 9 generisanja na
// 115 otvaranja planera (7,8%). Ako Duel izvoz ne predje ~2%, deljenje nije
// problem pozicije nego proizvoda i tema se zatvara.
// ============================================================================
function DuelShareCard({ data, t, cardRef }) {
  // Pod Bench Boostom je 15 redova umesto 11 — red se skuplja da kartica ne pukne.
  const ukupnoRedova = data.grupe.reduce((n, g) => n + g.rows.length, 0);
  // v76a · zaglavlje po varijanti A trosi vise vrha, pa redovi idu nize.
  // 11 redova x 36 + 4 zaglavlja pozicija ~ 492px, staje u ~549px koliko ostaje.
  // Pod Bench Boostom je 15 redova -> 30px, tesno ali staje.
  const rowH = ukupnoRedova > 12 ? 30 : 36;
  const boja = (a, b) => (a === b ? "#7c93b8" : a > b ? "#4ade80" : "#f87171");
  const bojaIshoda = data.diff > 0 ? "#4ade80" : data.diff < 0 ? "#f87171" : "#94a3b8";
  const podnaslovIshoda = data.diff > 0 ? t.duelCardLeadSub : data.diff < 0 ? t.duelCardTrailSub : null;
  const Znacka = ({ tip }) => {
    const st = { display: "inline-block", fontSize: 10, fontWeight: 900, borderRadius: 4, padding: "1px 4px", marginLeft: 5, verticalAlign: 1 };
    if (tip === "C") return <span style={{ ...st, background: "#2563eb", color: "#fff" }}>C</span>;
    if (tip === "V") return <span style={{ ...st, background: "#a78bfa", color: "#fff" }}>V</span>;
    if (tip === "S") return <span style={{ ...st, background: "#f59e0b", color: "#1a1205" }}>↑</span>;
    return null;
  };
  const Strana = ({ igrac, desno }) => (
    <div style={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: desno ? "right" : "left" }}>
      {igrac ? (desno ? <>{igrac.cap && <Znacka tip="C"/>}{igrac.vice && <Znacka tip="V"/>}{igrac.sub && <Znacka tip="S"/>}{igrac.name}</>
                     : <>{igrac.name}{igrac.cap && <Znacka tip="C"/>}{igrac.vice && <Znacka tip="V"/>}{igrac.sub && <Znacka tip="S"/>}</>) : "—"}
    </div>
  );
  return (
    <div ref={cardRef} style={{
      width: 540, height: 960, display: "flex", flexDirection: "column", overflow: "hidden",
      background: "linear-gradient(165deg, #0b1220 0%, #131f38 52%, #0b1220 100%)",
      color: "#fff", fontFamily: "var(--sans)", fontVariantNumeric: "tabular-nums",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "28px 34px 0" }}>
        <div style={{ fontSize: 18, fontWeight: 900, letterSpacing: "0.4px" }}>FPL CENTER</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#7c93b8" }}>GW {data.gwNum}{data.leagueName ? " · " + data.leagueName : ""}</div>
      </div>

      {/* v76a · VARIJANTA A: razlika dominira, rezultat je ispod nje.
          Kartica se u chatu prvo vidi kao slicica velicine nokta — u tom formatu
          se procita samo najveci element, pa najveci mora da bude ISHOD.
          Podnaslov postoji samo za vodstvo i zaostatak. Nereseno ga NEMA:
          Filipova odluka 01.09, "izbaci who blinks first, ostavi samo tied".
          Podnaslov je FIKSAN po stanju, ne nasumican — ista poruka u tri duela
          zaredom bi izgledala pokvareno, a rotacija je posebna odluka. */}
      <div style={{ margin: "22px 34px 0", padding: "22px 16px 16px", borderRadius: 16, background: "rgba(255,255,255,0.055)", border: "1px solid rgba(255,255,255,0.1)", textAlign: "center" }}>
        <div style={{ fontSize: 78, fontWeight: 900, lineHeight: 0.92, letterSpacing: "-3px", color: bojaIshoda }}>
          {data.diff > 0 ? "+" : ""}{data.diff}
        </div>
        <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: "3.2px", textTransform: "uppercase", color: bojaIshoda, marginTop: 9 }}>
          {data.diff > 0 ? t.duelCardLead : data.diff < 0 ? t.duelCardTrail : t.duelCardTied}
        </div>
        {podnaslovIshoda && (
          <div style={{ fontSize: 15, fontWeight: 600, color: "#8ba3c7", marginTop: 5 }}>{podnaslovIshoda}</div>
        )}

        <div style={{ display: "flex", alignItems: "flex-end", marginTop: 18, paddingTop: 15, borderTop: "1px solid rgba(255,255,255,0.09)" }}>
          <div style={{ flex: 1, minWidth: 0, textAlign: "center", padding: "0 5px" }}>
            <div style={{ fontSize: 16, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{data.me.team}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#7c93b8", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{data.me.name}</div>
            <div style={{ fontSize: 36, fontWeight: 900, lineHeight: 1, marginTop: 4, color: boja(data.me.pts, data.rival.pts) }}>{data.me.pts}</div>
          </div>
          <div style={{ width: 40, flex: "0 0 40px", textAlign: "center", fontSize: 12, fontWeight: 900, color: "#4a5f80", paddingBottom: 7 }}>VS</div>
          <div style={{ flex: 1, minWidth: 0, textAlign: "center", padding: "0 5px" }}>
            <div style={{ fontSize: 16, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{data.rival.team}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#7c93b8", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{data.rival.name}</div>
            <div style={{ fontSize: 36, fontWeight: 900, lineHeight: 1, marginTop: 4, color: boja(data.rival.pts, data.me.pts) }}>{data.rival.pts}</div>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, padding: "16px 26px 0", overflow: "hidden" }}>
        {data.grupe.map((g, gi) => (
          <div key={gi}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "2.2px", color: "#5a7099", margin: gi === 0 ? "0 0 3px" : "10px 0 3px", paddingLeft: 2 }}>{g.label}</div>
            {g.rows.map((r, ri) => {
              const isti = r.l && r.r && r.l.id === r.r.id && r.l.pts === r.r.pts;
              const lp = r.l ? r.l.pts : null, rp = r.r ? r.r.pts : null;
              return (
                <div key={ri} style={{
                  display: "flex", alignItems: "center", height: rowH, borderRadius: 8, padding: "0 8px",
                  opacity: isti ? 0.42 : 1,
                  background: isti ? "transparent" : (lp > rp ? "rgba(74,222,128,0.09)" : lp < rp ? "rgba(248,113,113,0.08)" : "transparent"),
                }}>
                  <Strana igrac={r.l}/>
                  <div style={{ width: 42, flex: "0 0 42px", textAlign: "center", fontSize: 18, fontWeight: 900, color: isti ? "#7c93b8" : boja(lp, rp) }}>{r.l ? lp : "—"}</div>
                  <div style={{ width: 42, flex: "0 0 42px", textAlign: "center", fontSize: 18, fontWeight: 900, color: isti ? "#7c93b8" : boja(rp, lp) }}>{r.r ? rp : "—"}</div>
                  <Strana igrac={r.r} desno/>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div style={{ padding: "14px 34px 30px", display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 14, fontWeight: 700, color: "#fbbf24" }}>
          {(data.toPlay.me > 0 || data.toPlay.rival > 0) && (
            <><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#fbbf24" }}/>{t.stillToPlay} {data.toPlay.me} : {data.toPlay.rival}</>
          )}
        </div>
        <div style={{ fontSize: 16, fontWeight: 800, color: "#7c93b8" }}>fplcenter.com</div>
      </div>
    </div>
  );
}

// Dugme + generisanje. NAMERNO ne koristi `ShareCardRow.generateAndShare`:
// ona je vezana za `card.unlocked` i stanje reda u modalu, pa bi njeno
// prepravljanje diralo put koji — koliko god slabo — danas radi.
// Duplira se ~35 linija svesno, u zamenu za nulti rizik po postojece kartice.
function DuelShareButton({ napraviData, t, lang }) {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  // v76 · LENJO. Kartica se ne racuna i ne montira dok se dugme ne pritisne.
  //
  // Prva verzija je racunala podatke u useMemo i drzala skrivenu karticu u DOM-u
  // pri SVAKOM otvaranju Duela. To znaci da bi svaka greska u kartici ili u
  // pripremi podataka rusila ceo Duel, iako korisnik nista nije ni kliknuo —
  // i tako je vec jednom i puklo (TDZ na getSlotsByPos).
  // Ovako je jedino sto se dodaje na otvaranje Duela jedan <button>.
  const [data, setData] = useState(null);
  const cardRef = useRef(null);

  const podeli = async () => {
    if (generating) return;
    trackEvent("Share Click", { source: "duel", position: "duel_top" });
    setGenerating(true);
    setError(null);
    try {
      const pripremljeno = napraviData();
      if (!pripremljeno) { setError(t.duelShareError); setGenerating(false); return; }
      setData(pripremljeno);
      // sacekaj da React montira skrivenu karticu pre nego sto je slikamo
      await new Promise(r => setTimeout(r, 80));
      const html2canvas = (await import("html2canvas")).default;
      if (!cardRef.current) { setGenerating(false); return; }
      await new Promise(r => setTimeout(r, 100));
      if (document.fonts && document.fonts.ready) await document.fonts.ready;
      const canvas = await html2canvas(cardRef.current, {
        scale: 2, backgroundColor: "#0b1220", useCORS: true, logging: false,
        width: 540, height: 960, windowWidth: 540, windowHeight: 960, imageTimeout: 3000,
      });
      canvas.toBlob(async (blob) => {
        if (!blob) { setGenerating(false); return; }
        const filename = `fplcenter-duel-${Date.now()}.jpg`;
        const mozeShare = !!(navigator.share && navigator.canShare);
        trackEvent("Share Image Generated", { card: "duel", method: mozeShare ? "native_share" : "download" });
        if (mozeShare) {
          const file = new File([blob], filename, { type: "image/jpeg" });
          if (navigator.canShare({ files: [file] })) {
            try {
              await navigator.share({ files: [file], title: "FPL Center", text: "fplcenter.com" });
              setGenerating(false);
              return;
            } catch (e) { /* otkazano ili nepodrzano — pada na download */ }
          }
        }
        // 82% saobracaja je u IG webview-u, gde <a download> cesto ne radi.
        // Radije posteno kazemo sta da uradi nego da se pravimo da je proslo.
        if (IN_APP_BROWSER && !mozeShare) {
          trackEvent("Share Blocked In App", { card: "duel", browser: IN_APP_BROWSER });
          setError(lang === "sr"
            ? "Slika ne moze da se sacuva iz Instagram pregledaca. Otvori fplcenter.com u Chrome-u ili Safari-ju (meni ⋯ gore desno → Otvori u pregledacu)."
            : "Images can't be saved from the Instagram browser. Open fplcenter.com in Chrome or Safari (⋯ menu, top right → Open in browser).");
          setGenerating(false);
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setGenerating(false);
      }, "image/jpeg", 0.92);
    } catch (e) {
      trackEvent("Share Blocked In App", { card: "duel", browser: IN_APP_BROWSER || "none" });
      setError(t.duelShareError);
      setGenerating(false);
    }
  };

  return (
    <>
      <button onClick={podeli} disabled={generating} style={{
        width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
        background: "#2563eb", border: "none", borderRadius: 11, color: "#fff",
        fontSize: 13, fontWeight: 800, fontFamily: "var(--sans)", padding: "11px 18px",
        boxShadow: "0 3px 10px rgba(37,99,235,0.3)", cursor: generating ? "default" : "pointer",
        opacity: generating ? 0.6 : 1, marginBottom: 8,
      }}>
        {generating ? t.duelShareGenerating : "↗ " + t.duelShare}
      </button>
      {error && <div style={{ fontSize: 11, color: "#dc2626", marginBottom: 8, lineHeight: 1.45 }}>{error}</div>}
      {data && (
        <div style={{ position: "absolute", left: -99999, top: 0, pointerEvents: "none" }}>
          <DuelShareCard data={data} t={t} cardRef={cardRef}/>
        </div>
      )}
    </>
  );
}

function H2HCompare({ myManager, rivalManager, bootstrap, liveData, bonusMap, t, lang, leagueName, onClose, onBack }) {
  // v73 (31.08.2026) · KLUPA. Lista je "ko se broji", ne "ko je bio u prvih 11".
  //
  // BUG KOJI SE OVIM ZATVARA: `position` je mesto u sastavu, ne u igri. Filter
  // `position <= 11` je ZADRZAVAO startera koji je odigrao 0 minuta i IZBACIVAO
  // igraca sa klupe koji je usao autosubom (njegov position je 12-15) i stvarno
  // doneo poene. Zaglavlje Duela je te poene brojalo (gwNet ide iz istog izvora),
  // a redovi ih nisu prikazivali — dva broja na istom ekranu su govorila razlicito.
  //
  // `processedPicks` (App.jsx ~3162) vec resava sve slucajeve:
  //   izbacen starter -> multiplier 0 · usao sa klupe -> multiplier 1
  //   potkapiten preuzeo -> multiplier 2 (3 pod TC)
  //   Bench Boost -> FPL sam salje multiplier 1 i za klupu, pa se dobije 15 redova
  // `multiplier > 0` je TACNO izraz kojim se racuna gwNet, pa redovi sada
  // sabiraju u zaglavlje po konstrukciji, a ne slucajno.
  //
  // NASLEDJENO OGRANICENJE: applyAutosubs trazi mec sa `fixtures.find(...)`, sto
  // u duplom kolu uzima PRVI mec tima (backlog 10). Ovde se ne uvodi — samo se
  // nasledjuje, i vec je vidljivo kroz gwNet.
  const myPicks = (myManager.picks || []).filter(p => (p.multiplier || 0) > 0);
  const rivalPicks = (rivalManager.picks || []).filter(p => (p.multiplier || 0) > 0);
  const posMap = { 1: "GK", 2: "DEF", 3: "MID", 4: "FWD" };
  const posOrder = [1, 2, 3, 4];

  // v54 Blok B: toggle "Samo razlike", pamti se izmedju otvaranja modala.
  const [onlyDiff, setOnlyDiff] = useState(() => {
    try { return localStorage.getItem(ONLY_DIFF_KEY) === "1"; } catch (e) { return false; }
  });
  const toggleOnlyDiff = () => {
    setOnlyDiff(prev => {
      const next = !prev;
      try { localStorage.setItem(ONLY_DIFF_KEY, next ? "1" : "0"); } catch (e) { /* webview bez localStorage */ }
      return next;
    });
  };

  // v54 Blok B: "Jos igra". liveData nosi tri stanja po igracu (live-status.js).
  //
  // v69 (28.08): broji se `isUpcoming`, NE `!isFinished`.
  // Stari uslov je spajao "mec jos nije poceo" i "igra bas sada", pa je brojac
  // stajao na 11 dok su Haland i Foden vec igrali. Ko je na terenu vise se ne
  // ceka — on igra. Igrac bez meca u kolu (blank) ne ulazi ni u jedno stanje.
  //
  // OGRANICENJE: broje se samo prvih 11. Ako je starteru utakmica gotova a odigrao
  // je 0 minuta, autosub sa klupe moze jos da igra i brojac ce reci 0. Za buducnost.
  const countToPlay = (picks) => picks.filter(p => {
    const lv = liveData?.[p.element];
    return !!lv && lv.isUpcoming;
  }).length;
  const myToPlay = countToPlay(myPicks);
  const rivalToPlay = countToPlay(rivalPicks);

  // v76 (01.09.2026) · UPARIVANJE IZVUCENO IZ JSX-a.
  //
  // ZASTO: share kartica mora da crta ISTE redove kao ekran. Da je logika ostala
  // u renderu, kartica bi je prepisala i dve kopije bi se pre ili kasnije razisle
  // (LEKCIJA 42, `live-status.js` je nastao tacno iz toga).
  // Tekst unutra je premesten DOSLOVNO — tri PRE-PUSH testa ga pinuju stringovima
  // (`const ptsOf = (slots, id)`, `onlyMyIds = ...`, `onlyRivalIds = ...`).
  // Filtriranje "Samo razlike" NIJE uslo ovde: ono je stvar ekrana, kartica uvek
  // prikazuje ceo sastav.
  const parujPoPoziciji = (pos) => {
    const mySlots = getSlotsByPos(myPicks, pos);
    const rivalSlots = getSlotsByPos(rivalPicks, pos);
    if (mySlots.length === 0 && rivalSlots.length === 0) return null;

    // Build paired rows: shared players go face-to-face, unique players in unique slots
    const myIds = new Set(mySlots.map(p => p.element));
    const rivalIds = new Set(rivalSlots.map(p => p.element));

    // Shared players (both managers have them)
    const sharedIds = [...myIds].filter(id => rivalIds.has(id));
    // v54 A4: unikatni se sortiraju po poenima opadajuce PRE uparivanja.
    // Ranije je par bio slucajan (redosled iz Set-a), pa je zeleno tvrdilo
    // "ovaj je bolji" za dva igraca bez ikakvog odnosa.
    const ptsOf = (slots, id) => {
      const info = getPlayerInfo(slots.find(p => p.element === id));
      return info ? info.totalPoints : -1;
    };
    const onlyMyIds = [...myIds].filter(id => !rivalIds.has(id)).sort((a, b) => ptsOf(mySlots, b) - ptsOf(mySlots, a));
    const onlyRivalIds = [...rivalIds].filter(id => !myIds.has(id)).sort((a, b) => ptsOf(rivalSlots, b) - ptsOf(rivalSlots, a));

    // Build rows: first unique pairs (zip my unique with rival unique), then shared
    const rows = [];
    const maxUnique = Math.max(onlyMyIds.length, onlyRivalIds.length);
    for (let i = 0; i < maxUnique; i++) {
      rows.push({
        myPick: onlyMyIds[i] ? mySlots.find(p => p.element === onlyMyIds[i]) : null,
        rivalPick: onlyRivalIds[i] ? rivalSlots.find(p => p.element === onlyRivalIds[i]) : null,
        isShared: false,
      });
    }
    // Then shared players face-to-face
    for (const sharedId of sharedIds) {
      rows.push({
        myPick: mySlots.find(p => p.element === sharedId),
        rivalPick: rivalSlots.find(p => p.element === sharedId),
        isShared: true,
      });
    }
    return { pos, rows };
  };

  const gwDiff = (myManager.gwNet || 0) - (rivalManager.gwNet || 0);

  // v54 A1/A2: zeleno+bold = vodi · slate = nereseno · sivo+tanko = gubi ili isti igrac.
  // Ranije je i gubitnik bio bold, pa je legenda "bold = better" bila netacna.
  const numStyle = (mine, theirs, isSame) => {
    if (isSame || mine == null) return { color: "#94a3b8", fontWeight: 500 };
    const opp = theirs == null ? 0 : theirs;
    if (mine > opp) return { color: "#16a34a", fontWeight: 800 };
    if (mine === opp) return { color: "#64748b", fontWeight: 600 };
    return { color: "#475569", fontWeight: 500 };
  };

  const getSlotsByPos = (picks, pos) => picks.filter(p => {
    const pl = bootstrap?.elements.find(el => el.id === p.element);
    return pl?.element_type === pos;
  });

  const getPlayerInfo = (pick) => {
    if (!pick) return null;
    const player = bootstrap?.elements.find(el => el.id === pick.element);
    if (!player) return null;
    // v54: bez live unosa igrac se i dalje prikazuje sa 0.
    // Ranije je red bio "—" i IME JE NESTAJALO dok liveData kasni.
    const live = liveData?.[pick.element] || { points: 0, bonus: 0 };
    const provBonus = bonusMap[pick.element] || 0;
    const basePoints = live.points + (live.bonus === 0 ? provBonus : 0);
    // v73: bilo `|| 1`. To je i SAKRIVALO bug sa klupom — izbacen starter ima
    // multiplier 0, a `0 || 1` daje 1, pa se crtao kao da normalno broji.
    const totalPoints = basePoints * (pick.multiplier || 0);
    return {
      name: player.web_name,
      totalPoints,
      // v74 (01.09.2026) · VRACENO na konvenciju iz manager modala, Filipova odluka:
      // "neka ostane ovako, da se zna da je V preuzeo".
      // C ostaje na originalnom kapitenu, V na potkapitenu. Kad kapiten blankira,
      // V stoji pored UDVOSTRUCENIH poena i to je znak da je preuzeo. Da C skoci
      // na potkapitena, preuzimanje bi postalo nevidljivo.
      // (v73 je ovo nakratko menjao na `multiplier >= 2` — povuceno da Duel i
      // manager modal ne bi imali dve konvencije za istu stvar.)
      isCaptain: pick.is_captain,
      isViceCaptain: pick.is_vice_captain,
      // v73: igrac koji je usao sa klupe. Bez ove oznake bi se u listi pojavio
      // igrac koga korisnik nije postavio i to bi licilo na kvar.
      subbedIn: !!pick.wasSubbedIn,
      playerId: pick.element,
      // v69 (28.08): tackica znaci "igra BAS SADA", ne "nije zavrsio".
      // Stari uslov `!isFinished` je davao tackicu i onima cija utakmica nije ni
      // pocela — dakle svima, sve dok prvi mec kola ne pocne.
      // NAMERNO se vise ne poklapa sa brojacem "Jos igra": brojac broji one koji
      // tek treba da izadju, tackica pokazuje one koji su na terenu.
      // Bez live unosa nema tackice — inace bi svi imali tackicu dok podaci stizu.
      stillPlaying: !!liveData?.[pick.element] && liveData[pick.element].inPlay,
    };
  };

  // v54: ako su timovi identicni, "Samo razlike" bi ostavio prazan beo okvir.
  // ⚠️ MORA STAJATI ISPOD `getSlotsByPos` i `getPlayerInfo`.
  // useMemo izvrsava svoju funkciju ODMAH pri renderu. Kad je ovaj blok stajao
  // odmah iza `gwDiff`, zvao je `parujPoPoziciji` -> `getSlotsByPos`, koji je
  // deklarisan nize sa `const` — temporal dead zone, ReferenceError i pad celog
  // Duela pri svakom otvaranju. PRE-PUSH gejt to NE HVATA: staticka provera vidi
  // da identifikator postoji, ne i kojim se redom izvrsava (LEKCIJA 35).
  // v76 · podaci za share karticu. Isti izvor kao ekran (parujPoPoziciji +
  // getPlayerInfo), pa slika i ekran ne mogu da se raziđu.
  // Kartica NE postuje "Samo razlike" — uvek crta ceo sastav. To je Filipova
  // odluka 31.08: "mozda da shareujemo svih prvih 11, to ljudi vole da vide".
  const napraviDuelData = () => {
    const uIgraca = (info, pick) => info ? {
      id: pick.element, name: truncateName(info.name, 13), pts: info.totalPoints,
      cap: info.isCaptain, vice: info.isViceCaptain, sub: info.subbedIn,
    } : null;
    const grupe = [];
    for (const pos of posOrder) {
      const u = parujPoPoziciji(pos);
      if (!u) continue;
      const rows = u.rows.map(r => ({
        l: uIgraca(getPlayerInfo(r.myPick), r.myPick || {}),
        r: uIgraca(getPlayerInfo(r.rivalPick), r.rivalPick || {}),
      })).filter(r => r.l || r.r);
      if (rows.length) grupe.push({ pos, label: posMap[pos], rows });
    }
    return {
      gwNum: myManager.gwNum, leagueName: leagueName || "",
      me: { name: myManager.name, team: myManager.teamName, pts: myManager.gwNet || 0 },
      rival: { name: rivalManager.name, team: rivalManager.teamName, pts: rivalManager.gwNet || 0 },
      diff: gwDiff, grupe, toPlay: { me: myToPlay, rival: rivalToPlay },
    };
  };

  const anyDiff = posOrder.some(pos => {
    const mine = getSlotsByPos(myPicks, pos);
    const theirs = getSlotsByPos(rivalPicks, pos);
    const myIdSet = new Set(mine.map(p => p.element));
    const rvIdSet = new Set(theirs.map(p => p.element));
    if (mine.some(p => !rvIdSet.has(p.element))) return true;
    if (theirs.some(p => !myIdSet.has(p.element))) return true;
    return mine.some(p => {
      const a = getPlayerInfo(p);
      const b = getPlayerInfo(theirs.find(x => x.element === p.element));
      return a && b && a.totalPoints !== b.totalPoints;
    });
  });

  return (
    <div style={{ padding: "20px 16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={onBack} style={{ background: "#f1f5f9", border: "none", borderRadius: 8, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 11L5 7l4-4" stroke="#64748b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>{t.compareVs || "Compare"}</div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>GW {myManager.gwNum || ""}</div>
          </div>
        </div>
        <button onClick={onClose} style={S.closeBtn}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11 3L3 11M3 3l8 8" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round"/></svg>
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 8, alignItems: "center", padding: "12px 10px", background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)", borderRadius: 12, marginBottom: 14 }}>
        <div style={{ textAlign: "left", paddingLeft: 4, minWidth: 0 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", letterSpacing: "1px", textTransform: "uppercase" }}>You</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{myManager.name}</div>
          <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "var(--sans)", color: "#2563eb", marginTop: 2 }}>{myManager.gwNet}</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", background: "#fff", padding: "3px 8px", borderRadius: 8 }}>VS</div>
          <div style={{ fontSize: 17, fontWeight: 800, fontFamily: "var(--mono)", color: gwDiff > 0 ? "#16a34a" : gwDiff < 0 ? "#dc2626" : "#64748b", marginTop: 4 }}>
            {gwDiff > 0 ? "+" : gwDiff < 0 ? "−" : ""}{Math.abs(gwDiff)}
          </div>
        </div>
        <div style={{ textAlign: "right", paddingRight: 4, minWidth: 0 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", letterSpacing: "1px", textTransform: "uppercase" }}>Rival</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{rivalManager.name}</div>
          <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "var(--sans)", color: rivalManager.gwNet > myManager.gwNet ? "#dc2626" : "#94a3b8", marginTop: 2 }}>{rivalManager.gwNet}</div>
        </div>
      </div>

      <div style={{ position: "relative" }}>
        <DuelShareButton napraviData={napraviDuelData} t={t} lang={lang}/>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button
          onClick={toggleOnlyDiff}
          aria-pressed={onlyDiff}
          style={{
            border: onlyDiff ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
            background: onlyDiff ? "#eff6ff" : "#fff",
            color: onlyDiff ? "#2563eb" : "#64748b",
            fontSize: 11, fontWeight: 700, padding: "5px 10px", borderRadius: 999, cursor: "pointer",
          }}
        >
          {t.compareOnlyDiff || "Differences only"}
        </button>
      </div>

      <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #f1f5f9", overflow: "hidden" }}>
        {posOrder.map(pos => {
          const uparen = parujPoPoziciji(pos);
          if (!uparen) return null;
          const rows = uparen.rows;

          // v54 Blok B: "Samo razlike" sklanja redove gde je isti igrac SA istim poenima.
          const visibleRows = onlyDiff ? rows.filter(r => {
            if (!r.isShared) return true;
            const a = getPlayerInfo(r.myPick);
            const b = getPlayerInfo(r.rivalPick);
            return !a || !b || a.totalPoints !== b.totalPoints;
          }) : rows;
          // prazna grupa -> ne crtaj ni zaglavlje pozicije
          if (visibleRows.length === 0) return null;

          return (
            <div key={pos}>
              <div style={{ background: "#f1f5f9", color: "#475569", fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: "1px", padding: "4px 10px", fontFamily: "var(--mono)" }}>{posMap[pos]}</div>

              {visibleRows.map((row, rowIdx) => {
                const myInfo = getPlayerInfo(row.myPick);
                const rivalInfo = getPlayerInfo(row.rivalPick);
                const myPts = myInfo ? myInfo.totalPoints : null;
                const rivalPts = rivalInfo ? rivalInfo.totalPoints : null;
                // v54 A1: prigusen red SAMO kad je isti igrac I isti broj poena.
                // Ranije je bilo dovoljno da je igrac isti, pa je kapitenska razlika
                // na zajednickom igracu (8 vs 4) bila potpuno nevidljiva.
                const sameId = row.isShared && myPts === rivalPts;
                const myNum = numStyle(myPts, rivalPts, sameId);
                const rivalNum = numStyle(rivalPts, myPts, sameId);

                return (
                  <div key={rowIdx} style={{ display: "grid", gridTemplateColumns: "1fr 1px 1fr", padding: "6px 0", borderTop: rowIdx > 0 ? "1px solid #f8fafc" : "none", opacity: sameId ? 0.55 : 1, alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "0 8px", minWidth: 0 }}>
                      {myInfo ? (
                        <>
                          {myInfo.stillPlaying && <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#16a34a", flexShrink: 0 }} />}
                          <span style={{ fontSize: 12, fontWeight: myNum.fontWeight, color: myNum.color, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{truncateName(myInfo.name)}</span>
                          {myInfo.isCaptain && <span style={{ background: "#fbbf24", color: "#fff", fontSize: 8, fontWeight: 800, padding: "1px 3px", borderRadius: 2, flexShrink: 0 }}>C</span>}
                          {myInfo.isViceCaptain && <span style={{ background: "#a78bfa", color: "#fff", fontSize: 8, fontWeight: 800, padding: "1px 3px", borderRadius: 2, flexShrink: 0 }}>V</span>}
                          {myInfo.subbedIn && <span title={t.duelSubbedIn} style={{ background: "#f59e0b", color: "#1a1205", fontSize: 8, fontWeight: 900, padding: "1px 3px", borderRadius: 2, flexShrink: 0 }}>↑</span>}
                          <span style={{ fontFamily: "var(--mono)", fontWeight: myNum.fontWeight, fontSize: 13, color: myNum.color, flexShrink: 0 }}>{myInfo.totalPoints}</span>
                        </>
                      ) : (
                        <span style={{ color: "#cbd5e1", fontSize: 12 }}>—</span>
                      )}
                    </div>
                    <div style={{ background: "#e2e8f0", alignSelf: "stretch" }}/>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "0 8px", minWidth: 0, flexDirection: "row-reverse" }}>
                      {rivalInfo ? (
                        <>
                          {rivalInfo.stillPlaying && <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#16a34a", flexShrink: 0 }} />}
                          <span style={{ fontSize: 12, fontWeight: rivalNum.fontWeight, color: rivalNum.color, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, textAlign: "right" }}>{truncateName(rivalInfo.name)}</span>
                          {rivalInfo.isCaptain && <span style={{ background: "#fbbf24", color: "#fff", fontSize: 8, fontWeight: 800, padding: "1px 3px", borderRadius: 2, flexShrink: 0 }}>C</span>}
                          {rivalInfo.isViceCaptain && <span style={{ background: "#a78bfa", color: "#fff", fontSize: 8, fontWeight: 800, padding: "1px 3px", borderRadius: 2, flexShrink: 0 }}>V</span>}
                          {rivalInfo.subbedIn && <span title={t.duelSubbedIn} style={{ background: "#f59e0b", color: "#1a1205", fontSize: 8, fontWeight: 900, padding: "1px 3px", borderRadius: 2, flexShrink: 0 }}>↑</span>}
                          <span style={{ fontFamily: "var(--mono)", fontWeight: rivalNum.fontWeight, fontSize: 13, color: rivalNum.color, flexShrink: 0 }}>{rivalInfo.totalPoints}</span>
                        </>
                      ) : (
                        <span style={{ color: "#cbd5e1", fontSize: 12 }}>—</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
        {onlyDiff && !anyDiff && (
          <div style={{ padding: "18px 12px", textAlign: "center", fontSize: 12, color: "#94a3b8" }}>
            {t.compareNoDiff || "Identical teams — nothing to compare"}
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 8, alignItems: "center", marginTop: 12, padding: "8px 10px", background: "#f8fafc", borderRadius: 10 }}>
        <div style={{ fontFamily: "var(--mono)", fontWeight: 800, fontSize: 15, color: "#0f172a", textAlign: "left", paddingLeft: 4 }}>{myToPlay}</div>
        <div style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", letterSpacing: "1px", textTransform: "uppercase" }}>{t.compareToPlay || "Still to play"}</div>
        <div style={{ fontFamily: "var(--mono)", fontWeight: 800, fontSize: 15, color: "#0f172a", textAlign: "right", paddingRight: 4 }}>{rivalToPlay}</div>
      </div>

      <div style={{ display: "flex", gap: 12, justifyContent: "center", padding: "10px 0 4px", fontSize: 10, color: "#94a3b8", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#16a34a" }}/>
          <span>{t.compareBoldDiff || "green = ahead"}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#64748b" }}/>
          <span>{t.compareLevel || "level"}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#cbd5e1" }}/>
          <span>{t.compareSameMatch || "grey = same player"}</span>
        </div>
      </div>
    </div>
  );
}

function H2HRivalPicker({ myId, sortedManagers, onPick, onClose, t }) {
  const rivals = sortedManagers.filter(m => String(m.id) !== String(myId) && m.hasPicks);

  return (
    <div style={{ padding: "20px 16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>{t.compareTitle || "Compare Teams"}</div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{t.comparePickRival || "Pick a rival from your league"}</div>
        </div>
        <button onClick={onClose} style={S.closeBtn}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11 3L3 11M3 3l8 8" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round"/></svg>
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 520, overflowY: "auto" }}>
        {rivals.map(r => (
          <div key={r.id} onClick={() => onPick(r)} style={{ display: "grid", gridTemplateColumns: "28px 1fr auto", alignItems: "center", gap: 10, padding: "10px 12px", background: "#f8fafc", borderRadius: 10, cursor: "pointer", border: "1px solid transparent" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#eff6ff"; e.currentTarget.style.borderColor = "#bfdbfe"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.borderColor = "transparent"; }}
          >
            <div style={{ fontFamily: "var(--mono)", fontWeight: 700, color: "#94a3b8", fontSize: 12, textAlign: "center" }}>{r.liveRank}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{r.name}</div>
              <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 1 }}>{r.teamName}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ fontFamily: "var(--mono)", fontWeight: 700, color: "#2563eb", fontSize: 13 }}>{r.gwNet}</div>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 3l4 4-4 4" stroke="#cbd5e1" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ FPL MAINTENANCE SCREEN ============
function FPLMaintenanceScreen({ t, onRetry, retrying }) {
  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "40px 24px",
      background: "linear-gradient(180deg, #f8fafc 0%, #eff6ff 100%)",
      textAlign: "center",
      fontFamily: "var(--sans)",
    }}>
      <div style={{ width: 120, height: 120, position: "relative", marginBottom: 32 }}>
        <div style={{
          position: "absolute", inset: 0, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(37,99,235,0.15) 0%, transparent 70%)",
          animation: "maintPulse 2s ease-in-out infinite",
        }}/>
        <div style={{
          position: "absolute", inset: 20, borderRadius: "50%",
          background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 10px 40px rgba(37,99,235,0.3)",
        }}>
          <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
            <circle cx="22" cy="22" r="18" fill="#fff" stroke="#1e3a8a" strokeWidth="1.5"/>
            <path d="M22 8 L26 14 L22 19 L18 14 Z M14 17 L18 14 L22 19 L18 24 L14 21 Z M30 17 L26 14 L22 19 L26 24 L30 21 Z M18 24 L22 19 L26 24 L24 30 L20 30 Z" fill="#1e3a8a" opacity="0.9"/>
          </svg>
        </div>
      </div>

      <h1 style={{
        fontSize: 22, fontWeight: 800, color: "#0f172a",
        letterSpacing: "-0.3px", marginBottom: 8, maxWidth: 400, lineHeight: 1.25,
      }}>{t.fplDownTitle}</h1>

      <p style={{
        fontSize: 14, color: "#64748b", lineHeight: 1.55,
        marginBottom: 24, maxWidth: 380,
      }}>{t.fplDownSub}</p>

      <div style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        padding: "10px 16px", background: "#fff", border: "1px solid #e0e7ff",
        borderRadius: 12, marginBottom: 24, maxWidth: 380,
        boxShadow: "0 1px 3px rgba(37,99,235,0.05)",
      }}>
        <div style={{
          width: 8, height: 8, borderRadius: "50%", background: "#f59e0b",
          animation: "maintDot 1.4s ease-in-out infinite", flexShrink: 0,
        }}/>
        <span style={{ fontSize: 12, color: "#475569", fontWeight: 500, textAlign: "left", lineHeight: 1.5 }}>
          {t.fplDownHint}
        </span>
      </div>

      <button
        onClick={onRetry}
        disabled={retrying}
        style={{
          padding: "12px 24px",
          background: retrying ? "#cbd5e1" : "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
          border: "none", borderRadius: 12, color: "#fff",
          fontSize: 14, fontWeight: 700, fontFamily: "var(--sans)",
          cursor: retrying ? "default" : "pointer",
          boxShadow: retrying ? "none" : "0 4px 12px rgba(37,99,235,0.3)",
          display: "inline-flex", alignItems: "center", gap: 8,
          transition: "all 0.2s",
        }}
      >
        {retrying && (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ animation: "spin 0.8s linear infinite" }}>
            <path d="M7 1a6 6 0 1 1 -6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        )}
        {t.fplDownRetry}
      </button>

      <div style={{ marginTop: 40, fontSize: 11, color: "#94a3b8", fontFamily: "var(--mono)" }}>
        fplcenter.com
      </div>
    </div>
  );
}

// ===================================================================
// v52 · EMAIL CAPTURE
//
// Zasto: posle 4 meseca rada, 2.4k poseta i nula sacuvanih adresa.
// GW1 (21.08) je jedini dan kad saobracaj sam dolazi; ako forma tada nije
// ziva, talas prolazi bez ijedne adrese. Ovo je jedini kanal koji je NAS.
//
// Gde ide: Supabase tabela public.email_signups.
// anon kljuc sme SAMO INSERT (RLS) — lista se ne moze procitati iz frontenda.
// Filip cita: Supabase dashboard -> Table Editor -> email_signups.
// ===================================================================
const EMAIL_DONE_KEY = "fpl_email_signed";

// v53: PRE-GW1 OBAVESTENJE
//
// Problem koji resava: do 21.08 19:30 nijedna liga nema tabelu (FPL vraca
// standings.results: []). Home ekran je do sada trazio Team ID bez ijedne reci
// o tome da iza tog unosa nema sta da se prikaze. Korisnik prodje tri koraka
// i zavrsi u praznoj sobi.
//
// Namerno NIJE zid sa porukom "vrati se kasnije". Zid bi ubio jedini razlog
// zbog kog neko moze da ostavi trag pre GW1 (Team ID + email). Umesto toga:
// prvo se kaze zasto je prazno, pa se odmah da posao za sada.
//
// Sam sebe gasi kad prodje deadline — nema prekidaca za rucnu izmenu.
function PreSeasonNotice({ t, lang }) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const shownRef = useRef(false);

  useEffect(() => {
    if (Date.now() >= GW1_DEADLINE_MS) return undefined;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (shownRef.current || Date.now() >= GW1_DEADLINE_MS) return;
    shownRef.current = true;
    trackEvent("Preseason Notice Shown");
  }, []);

  const diff = GW1_DEADLINE_MS - nowMs;
  if (!(diff > 0)) return null;

  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  const pad = (n) => String(n).padStart(2, "0");

  const units = [
    { v: days, l: lang === "en" ? "d" : "d" },
    { v: hours, l: lang === "en" ? "h" : "h" },
    { v: mins, l: lang === "en" ? "m" : "m" },
    { v: secs, l: lang === "en" ? "s" : "s" },
  ];

  return (
    <div style={{ background: "linear-gradient(135deg,#0f172a 0%,#1e293b 100%)", borderRadius: 14, padding: 16, marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: "#93c5fd", letterSpacing: "0.8px", textTransform: "uppercase", fontFamily: "var(--sans)", marginBottom: 8 }}>
        {t.preseasonTitle}
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {units.map((u, i) => (
          <div key={i} style={{ flex: 1, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 10, padding: "8px 4px", textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", fontFamily: "var(--sans)", fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>
              {i === 0 ? u.v : pad(u.v)}
            </div>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.6px", textTransform: "uppercase", fontFamily: "var(--sans)", marginTop: 2 }}>
              {u.l}
            </div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: "#cbd5e1", lineHeight: 1.55, fontFamily: "var(--sans)", marginBottom: 8 }}>
        {t.preseasonBody}
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#fff", lineHeight: 1.55, fontFamily: "var(--sans)", background: "rgba(37,99,235,0.22)", border: "1px solid rgba(96,165,250,0.35)", borderRadius: 10, padding: "9px 11px" }}>
        {t.preseasonCta}
      </div>
    </div>
  );
}

function EmailCapture({ t, lang, source, teamId }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [errMsg, setErrMsg] = useState(null);
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(EMAIL_DONE_KEY) === "true"; } catch (e) { return false; }
  });
  const shownRef = useRef(false);

  useEffect(() => {
    if (hidden || shownRef.current) return;
    shownRef.current = true;
    trackEvent("Email Form Shown", { source });
  }, [hidden, source]);

  if (hidden) return null;

  const dismiss = () => {
    try { localStorage.setItem(EMAIL_DONE_KEY, "true"); } catch (e) { /* ignore */ }
    trackEvent("Email Dismissed", { source });
    setHidden(true);
  };

  const submit = async () => {
    const value = email.trim().toLowerCase();
    // Ista provera koju radi i CHECK constraint u bazi — da ne saljemo bespotrebno.
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value) || value.length > 254) {
      setErrMsg(t.emailErrorInvalid);
      setState("error");
      return;
    }
    setState("sending");
    setErrMsg(null);
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/email_signups`, {
        method: "POST",
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          "Content-Type": "application/json",
          Prefer: "return=minimal",
        },
        body: JSON.stringify({
          email: value,
          lang,
          source,
          team_id: teamId ? String(teamId).slice(0, 16) : null,
        }),
      });
      // 409 = vec prijavljen. Za korisnika je to isto sto i uspeh — ne teramo ga
      // da se pita da li je proslo.
      if (res.ok || res.status === 409) {
        try { localStorage.setItem(EMAIL_DONE_KEY, "true"); } catch (e) { /* ignore */ }
        trackEvent("Email Submitted", { source, duplicate: res.status === 409 ? "yes" : "no" });
        setState("done");
        return;
      }
      throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      trackEvent("Email Failed", { source });
      setErrMsg(t.emailErrorGeneric);
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 14, padding: 14, marginBottom: 12, textAlign: "center" }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#15803d", fontFamily: "var(--sans)" }}>✓ {t.emailDone}</div>
      </div>
    );
  }

  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 14, marginBottom: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a", fontFamily: "var(--sans)", marginBottom: 3 }}>{t.emailTitle}</div>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: "#64748b", lineHeight: 1.5, fontFamily: "var(--sans)", marginBottom: 10 }}>{t.emailSub}</div>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (state === "error") setState("idle"); }}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder={t.emailPlaceholder}
          disabled={state === "sending"}
          style={{ flex: 1, minWidth: 0, border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", fontSize: 13, fontFamily: "var(--sans)", background: "#f8fafc", color: "#0f172a" }}
        />
        <button
          onClick={submit}
          disabled={state === "sending"}
          style={{ flex: "0 0 auto", background: "#2563eb", color: "#fff", border: "none", borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 800, fontFamily: "var(--sans)", cursor: state === "sending" ? "default" : "pointer", opacity: state === "sending" ? 0.6 : 1 }}
        >{state === "sending" ? t.emailSending : t.emailBtn}</button>
      </div>
      {errMsg && (
        <div style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", marginTop: 7, fontFamily: "var(--sans)" }}>{errMsg}</div>
      )}
      <button
        onClick={dismiss}
        style={{ marginTop: 8, background: "none", border: "none", padding: 0, color: "#94a3b8", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "var(--sans)" }}
      >{t.emailDismiss}</button>
    </div>
  );
}

// ===================================================================
// v57 · IZVODJACI PREKIDA (set-piece takeri)
//
// Podaci dolaze ISKLJUCIVO iz zvanicnog `bootstrap-static`:
//   penalties_order · direct_freekicks_order · corners_and_indirect_freekicks_order
// Nista se ne unosi rucno i nista se ne skrejpuje — kad FPL promeni hijerarhiju,
// ekran se promeni sam.
//
// Izmereno 27.08.2026 na zivom API-ju (`~/Desktop/FPL/set-piece-check.js`,
// 616 igraca / 20 klubova):
//   - NIJEDAN klub nije bez izvodjaca ni u jednoj od tri kategorije
//   - najdublji redosled: penali 5 · slobodni 5 · korneri 6
//   - `penalties_text` je PRAZAN za svih 616 igraca → nema napomena, samo redosled.
//     Ako FPL jednom pocne da ga puni, tu je mesto za jos jedan red.
// ===================================================================
// v58: traka tabova u ligi. Varijanta A (segmentirani pill) — Filipova odluka 27.08.
// Planer je namerno prisutan ali ZAKLJUCAN (`disabled`): tab pokazuje sta dolazi,
// ali ne vodi u prazan ekran. Kad planer dobije sadrzaj, skida se `disabled` i znacka.
function LeagueTabs({ active, onLive, onPlanner, onSetPieces, plannerEnabled, t }) {
  const base = {
    flex: 1, textAlign: "center", padding: "8px 4px", borderRadius: 9,
    fontSize: 12.5, fontWeight: 600, fontFamily: "var(--sans)",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
    border: "1px solid transparent", cursor: "pointer",
    transition: "background 0.15s, color 0.15s",
  };
  const onSt = { ...base, background: "#2563eb", color: "#fff", boxShadow: "0 2px 7px rgba(37,99,235,0.3)" };
  const offSt = { ...base, background: "#f1f5f9", color: "#64748b" };
  const lockSt = { ...offSt, opacity: 0.45, cursor: "default" };
  return (
    <div style={{ display: "flex", gap: 3, padding: "7px 12px 9px", background: "rgba(255,255,255,0.72)", backdropFilter: "blur(20px) saturate(180%)", WebkitBackdropFilter: "blur(20px) saturate(180%)", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
      <button onClick={onLive} style={active === "live" ? onSt : offSt}>{t.tabLive}</button>
      {plannerEnabled ? (
        <button onClick={onPlanner} style={active === "planner" ? onSt : offSt}>{t.tabPlanner}</button>
      ) : (
        <button disabled style={lockSt}>
          {t.tabPlanner}
          <span style={{ fontSize: 8.5, fontWeight: 700, fontFamily: "var(--mono)", background: "#e2e8f0", color: "#94a3b8", borderRadius: 4, padding: "1px 4px", letterSpacing: "0.3px" }}>{t.tabSoon}</span>
        </button>
      )}
      <button onClick={onSetPieces} style={active === "setpieces" ? onSt : offSt}>{t.tabSetPieces}</button>
    </div>
  );
}

// v58: Filip 27.08 — vise od tri izvodjaca po kategoriji deluje pretrpano.
const SP_MAX = 3;

const SP_CATS = [
  { key: "pen", field: "penalties_order", label: "setPiecePen" },
  { key: "fk", field: "direct_freekicks_order", label: "setPieceFK" },
  { key: "cor", field: "corners_and_indirect_freekicks_order", label: "setPieceCorners" },
];

function SetPieceTakers({ bootstrap, loading, error, t, onBack, onRetry, tabs }) {
  const [cat, setCat] = useState("all");

  const teams = useMemo(() => {
    if (!bootstrap) return [];
    const byId = new Map(
      bootstrap.teams.map((tm) => [tm.id, { id: tm.id, name: tm.name, short: tm.short_name, pen: [], fk: [], cor: [] }])
    );
    bootstrap.elements.forEach((el) => {
      const row = byId.get(el.team);
      if (!row) return;
      SP_CATS.forEach((c) => {
        if (el[c.field] != null) row[c.key].push(el);
      });
    });
    const out = Array.from(byId.values());
    out.forEach((row) => {
      SP_CATS.forEach((c) => {
        row[c.key].sort((a, b) => a[c.field] - b[c.field]);
      });
    });
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [bootstrap]);

  const shown = SP_CATS.filter((c) => cat === "all" || cat === c.key);
  const filters = [{ key: "all", label: t.setPieceAll }].concat(
    SP_CATS.map((c) => ({ key: c.key, label: t[c.label] }))
  );

  return (
    <>
      {/* v58: zaglavlje i traka tabova lepe se zajedno, u jednom sticky omotacu —
          tako nema pogadjanja visine zaglavlja za `top` drugog sticky elementa. */}
      <div style={{ position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ ...S.header, position: "static" }}>
          <button onClick={onBack} style={S.backBtn}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={S.headerTitle}>{t.setPieceTitle}</div>
            <div style={S.headerMeta}>{t.setPieceSub}</div>
          </div>
        </div>
        {tabs}
      </div>

      <div style={{ maxWidth: 440, margin: "0 auto", padding: "14px 16px 44px", animation: "fadeUp 0.4s ease" }}>
        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: "64px 0" }}>
            <div style={{ width: 24, height: 24, border: "2.5px solid #dbeafe", borderTopColor: "#2563eb", borderRadius: "50%", animation: "spin 0.7s linear infinite" }}/>
          </div>
        )}

        {!loading && error && (
          <div style={{ ...S.card, textAlign: "center" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#dc2626", marginBottom: 14 }}>{t.setPieceError}</div>
            <button onClick={onRetry} style={{ ...S.btn, width: "auto", padding: "10px 22px", marginTop: 0 }}>{t.fplDownRetry}</button>
          </div>
        )}

        {!loading && !error && bootstrap && (
          <>
            <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
              {filters.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setCat(opt.key)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: "var(--sans)",
                    cursor: "pointer",
                    background: cat === opt.key ? "#2563eb" : "#fff",
                    color: cat === opt.key ? "#fff" : "#64748b",
                    border: cat === opt.key ? "1px solid #2563eb" : "1px solid #e2e8f0",
                    transition: "background 0.15s, color 0.15s",
                  }}
                >{opt.label}</button>
              ))}
            </div>

            {teams.map((team) => (
              <div key={team.id} style={{ background: "#fff", border: "1px solid #e8eef6", borderRadius: 14, padding: "12px 14px 13px", marginBottom: 8, boxShadow: "0 1px 2px rgba(0,0,0,0.02)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, fontFamily: "var(--mono)", color: "#2563eb", background: "#eff6ff", border: "1px solid #dbeafe", borderRadius: 6, padding: "2px 6px", letterSpacing: "0.5px" }}>{team.short}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{team.name}</span>
                </div>
                {shown.map((c) => (
                  <div key={c.key} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 7 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.7px", color: "#94a3b8", width: 58, flexShrink: 0, paddingTop: 5 }}>{t[c.label]}</span>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5, minWidth: 0 }}>
                      {team[c.key].length === 0 ? (
                        <span style={{ fontSize: 12, color: "#cbd5e1", paddingTop: 4 }}>{t.setPieceNone}</span>
                      ) : team[c.key].slice(0, SP_MAX).map((p, i) => (
                        <span
                          key={p.id}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "3px 8px 3px 4px",
                            borderRadius: 8,
                            fontSize: 12.5,
                            fontWeight: i === 0 ? 700 : 500,
                            color: i === 0 ? "#0f172a" : "#475569",
                            background: i === 0 ? "#eff6ff" : "#f8fafc",
                            border: i === 0 ? "1px solid #dbeafe" : "1px solid #f1f5f9",
                          }}
                        >
                          <span style={{ fontSize: 9, fontWeight: 700, fontFamily: "var(--mono)", width: 15, height: 15, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", background: i === 0 ? "#2563eb" : "#e8eef6", color: i === 0 ? "#fff" : "#94a3b8", flexShrink: 0 }}>{p[c.field]}</span>
                          {p.web_name}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}

            <p style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.6, marginTop: 14, textAlign: "center" }}>{t.setPieceSource}</p>
          </>
        )}
      </div>
    </>
  );
}

export default function FPLCenter() {
  const [lang, setLang] = useState(() => localStorage.getItem("fpl_lang") || "en");
  const t = TEXT[lang];

  // v52: ?liga= link takodje starta u "loading" — inace bi pozvani korisnik
  // na trenutak video home ekran pa tek onda ligu.
  const [screen, setScreen] = useState(() => (INVITED_LEAGUE_ID || localStorage.getItem("fpl_last_league")) ? "loading" : "home");
  const [inputMode, setInputMode] = useState("player");
  const [leagueId, setLeagueId] = useState(() => localStorage.getItem("fpl_league_id") || "");
  const [teamId, setTeamId] = useState(() => localStorage.getItem("fpl_team_id") || "");
  // Search by name state
  const [searchResults, setSearchResults] = useState(null); // null=not searched, []=no results, [...]=has results
  const [searchLoading, setSearchLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [lastUpdate, setLastUpdate] = useState(null);
  const [bootstrap, setBootstrap] = useState(null);
  // v57: set-piece ekran nosi svoje loading/error stanje jer se na home ekranu
  // `bootstrap` uopste ne ucitava — loadBootstrap ide samo kroz loadLeagueByID.
  const [setPieceLoading, setSetPieceLoading] = useState(false);
  const [setPieceError, setSetPieceError] = useState(false);
  const [leagueData, setLeagueData] = useState(null);
  const [liveData, setLiveData] = useState(null);
  const [fixtures, setFixtures] = useState([]);
  const [managerPicks, setManagerPicks] = useState({});
  const [managerHistories, setManagerHistories] = useState({});
  const [selectedManager, setSelectedManager] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareModalScrollTo, setShareModalScrollTo] = useState(null); // 'leader' | 'gwwinner' | 'captain' | null - banner deep-link target
  // eslint-disable-next-line no-unused-vars
  const [nudgeDismissTick, setNudgeDismissTick] = useState(0); // force re-render kad se NudgeBanner dismiss-uje (localStorage promene ne triggeruju re-render)
  const [managerTransfers, setManagerTransfers] = useState({}); // {[managerId]: { transfers: [...], loading: bool }} - lazy fetch transferi po menadzeru
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [compareRival, setCompareRival] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [fplMaintenance, setFplMaintenance] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [currentGW, setCurrentGW] = useState(null);
  const [managerLeagues, setManagerLeagues] = useState({});
  // v77: sazetak iz /entry/{id}/ po menadzeru (overall rank / poeni).
  // Puni ga onaj isti poziv koji ionako ide zbog liste liga — nema novog fetch-a.
  const [managerEntries, setManagerEntries] = useState({});
  const [leaguesLoading, setLeaguesLoading] = useState(false);
  const [userLeagues, setUserLeagues] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showLeagueDropdown, setShowLeagueDropdown] = useState(false);
  const [headerLeagues, setHeaderLeagues] = useState(null);
  const intervalRef = useRef(null);

  // Extract Team ID from URL, link, or plain number
  const extractTeamId = (input) => {
    if (!input) return "";
    // Match /entry/NUMBER/ pattern
    const entryMatch = input.match(/\/entry\/(\d+)/);
    if (entryMatch) return entryMatch[1];
    // Match any long number in text
    const numMatch = input.match(/\d{4,8}/);
    if (numMatch) return numMatch[0];
    return input.trim();
  };

  useEffect(() => { localStorage.setItem("fpl_lang", lang); }, [lang]);
  useEffect(() => { if (leagueId) localStorage.setItem("fpl_league_id", leagueId); }, [leagueId]);
  useEffect(() => { if (teamId) localStorage.setItem("fpl_team_id", teamId); }, [teamId]);

  // Auto-scroll u Share modalu na specificnu karticu (kad se otvori iz banner Podeli klika)
  useEffect(() => {
    if (!showShareModal) {
      // Modal je zatvoren - reset scrollTo da sledeci put kad se otvori normalno ne skrolljuje
      if (shareModalScrollTo) setShareModalScrollTo(null);
      return;
    }
    if (!shareModalScrollTo) return;
    // Sacekaj jedan render frame da modal bude u DOM-u, pa onda scroll
    const timer = setTimeout(() => {
      const target = document.querySelector(`[data-share-card-id="${shareModalScrollTo}"]`);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [showShareModal, shareModalScrollTo]);

  useEffect(() => {
    if (!selectedManager) return;
    if (managerLeagues[selectedManager.id]) return;
    setLeaguesLoading(true);
    apiFetch(`/entry/${selectedManager.id}/`)
      .then(data => {
        setManagerLeagues(prev => ({ ...prev, [selectedManager.id]: data.leagues?.classic || [] }));
        // v77: isti odgovor nosi svez overall rank. Ranije se bacao, pa je modal
        // morao da ga vadi iz history-ja — a history tokom neobradjenog kola kasni.
        setManagerEntries(prev => ({ ...prev, [selectedManager.id]: {
          overallRank: data.summary_overall_rank ?? null,
          totalPoints: data.summary_overall_points ?? null,
          eventPoints: data.summary_event_points ?? null,
        } }));
      })
      .catch(() => {
        setManagerLeagues(prev => ({ ...prev, [selectedManager.id]: [] }));
      })
      .finally(() => setLeaguesLoading(false));
  }, [selectedManager, managerLeagues]);

  // Lazy fetch transferi za otvoreni Manager modal - samo kad je potrebno
  useEffect(() => {
    if (!selectedManager || !currentGW) return;
    const id = selectedManager.id;
    if (managerTransfers[id]) return; // vec ucitano (cache hit)

    // Optimization: ako je event_transfers === 0, preskoci fetch (FPL API i tako vraca prazno)
    const picksData = managerPicks[id];
    const eventTransfers = picksData?.entry_history?.event_transfers ?? selectedManager?.entry_history?.event_transfers ?? 0;
    if (eventTransfers === 0) {
      setManagerTransfers(prev => ({ ...prev, [id]: { transfers: [], loading: false } }));
      return;
    }

    setManagerTransfers(prev => ({ ...prev, [id]: { transfers: [], loading: true } }));
    apiFetch(`/entry/${id}/transfers/`)
      .then(data => {
        const gwTransfers = (Array.isArray(data) ? data : []).filter(t => t.event === currentGW.id);
        setManagerTransfers(prev => ({ ...prev, [id]: { transfers: gwTransfers, loading: false } }));
      })
      .catch(() => {
        setManagerTransfers(prev => ({ ...prev, [id]: { transfers: [], loading: false } }));
      });
  }, [selectedManager, currentGW, managerPicks, managerTransfers]);

  // Fetch user's leagues for the header dropdown (once we're in league view + have teamId)
  useEffect(() => {
    if (screen !== "league" || !teamId || headerLeagues !== null) return;
    apiFetch(`/entry/${teamId}/`)
      .then(data => {
        setHeaderLeagues(data.leagues?.classic || []);
      })
      .catch(() => {
        setHeaderLeagues([]);
      });
  }, [screen, teamId, headerLeagues]);

  const loadBootstrap = useCallback(async () => {
    const data = await apiFetch("/bootstrap-static/");
    setBootstrap(data);
    const current = data.events.find((e) => e.is_current);
    const next = data.events.find((e) => e.is_next);
    const gw = current || next;
    setCurrentGW(gw);
    return { data, gw };
  }, []);

  // v57: otvara ekran sa izvodjacima prekida i, ako bootstrap jos nije ucitan,
  // povlaci ga. Backend kesira /bootstrap-static/ 300s uz request coalescing,
  // pa ovo ne pravi nov pritisak na FPL.
  // v58: u set-piece ekran se ulazi ISKLJUCIVO iz trake tabova u ligi
  // (dugme na home ekranu je Filipovom odlukom uklonjeno 27.08), pa je
  // povratak uvek na ligu i traka se uvek crta.
  const openSetPieces = useCallback(async () => {
    trackEvent("Set Pieces Opened", { source: "league_tab" });
    setScreen("setpieces");
    window.scrollTo(0, 0);
    if (bootstrap) return;
    setSetPieceError(false);
    setSetPieceLoading(true);
    try {
      await loadBootstrap();
    } catch (e) {
      setSetPieceError(true);
    }
    setSetPieceLoading(false);
  }, [bootstrap, loadBootstrap]);

  // v58: povratak sa Prekida na Uzivo. Interval se sam pali na `screen === "league"`,
  // ali prvi tik je tek za 60s — zato se odmah rucno osvezi da rezultati ne budu bajati.
  const backToLeague = useCallback(() => {
    setScreen("league");
    window.scrollTo(0, 0);
    if (refreshFnRef.current) refreshFnRef.current();
  }, []);

  // v60: planeru treba VALIDIRAN brojcani ID. `teamId` state dolazi iz
  // fpl_team_id, koji sme da sadrzi tekst (npr. "Bojan") jer sluzi i kao
  // polje za pretragu. Merodavan je fpl_my_team_id — upisuje se tek posle
  // provere kroz /entry/{id}/.
  const plannerTeamId = useMemo(() => {
    try {
      const saved = localStorage.getItem("fpl_my_team_id");
      if (saved && /^\d+$/.test(saved)) return saved;
    } catch (e) { /* localStorage nedostupan */ }
    const raw = String(teamId || "").trim();
    return /^\d+$/.test(raw) ? raw : "";
  }, [teamId]);

  const openPlanner = useCallback(() => {
    trackEvent("Planner Opened");
    setScreen("planner");
    window.scrollTo(0, 0);
  }, []);

  const lookupTeam = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(`/entry/${teamId}/`);
      const managerName = `${data.player_first_name || ""} ${data.player_last_name || ""}`.trim();
      setUserInfo({
        name: managerName || "?",
        teamName: data.name || "?",
        overallRank: data.summary_overall_rank,
        totalPoints: data.summary_overall_points,
        eventPoints: data.summary_event_points,
      });
      setUserLeagues(data.leagues?.classic || []);
      // v52: ID je upravo POTVRDJEN kroz /entry/{id}/ — tek sada ga pamtimo
      // kao "moj tim". fpl_team_id je unified input i moze da sadrzi tekst
      // ("Bojan"), pa se na njega ne sme oslanjati planer ni bilo sta drugo.
      try {
        if (/^\d+$/.test(String(teamId).trim())) {
          localStorage.setItem("fpl_my_team_id", String(teamId).trim());
          localStorage.setItem(SEASON_KEY, SEASON);
        }
      } catch (e) { /* localStorage nedostupan */ }
    } catch (err) {
      if (err.isMaintenance) {
        setFplMaintenance(true);
      } else if (err.notFound) {
        // v52: ID ne postoji u OVOJ sezoni. Najcesci uzrok — sacuvan ID iz
        // prosle sezone. Brisemo ga da se greska ne ponavlja na svakom ulasku.
        try {
          localStorage.removeItem("fpl_my_team_id");
          localStorage.removeItem("fpl_team_id");
          localStorage.removeItem("fpl_last_league");
        } catch (e) { /* ignore */ }
        trackEvent("Stale Team ID", { teamId: String(teamId).slice(0, 12) });
        setError(t.teamNotFoundSeason);
      } else {
        setError(t.teamNotFound);
      }
      setUserLeagues(null);
      setUserInfo(null);
    } finally {
      setLoading(false);
    }
  }, [teamId, t.teamNotFound, t.teamNotFoundSeason]);

  // Detect if input is a search term (contains letters) vs Team ID (numbers only)
  // Returns true if the input looks like a name search, not a numeric Team ID
  const isSearchTerm = useCallback((value) => {
    if (!value) return false;
    const trimmed = value.trim();
    if (trimmed.length === 0) return false;
    // If it's a URL, it's a Team ID source
    if (trimmed.includes("/entry/")) return false;
    // If it's all digits, it's a Team ID
    if (/^\d+$/.test(trimmed)) return false;
    // Otherwise, treat as name search
    return true;
  }, []);

  // v51: Search redizajn FAZA A. 2 RPC funkcije zavisno od taba:
  // - "player" tab → search_by_player_name (1 reč ili više)
  // - "team" tab → search_by_team_name
  // RPC funkcije same rade hijerarhiju exact > prefix > fuzzy i sortiranje po score-u.
  // Stari RPC search_managers ostaje u bazi za rollback.
  const performSearch = useCallback(async () => {
    const trimmed = teamId.trim();
    if (trimmed.length < 2) return;

    // Plausible tracking - koliko reči i koji tab
    const queryWords = trimmed.split(/\s+/).filter(Boolean).length;

    // Bira RPC funkciju po tabu
    const rpcName = inputMode === "team" ? "search_by_team_name" : "search_by_player_name";

    setSearchLoading(true);
    setSearchResults(null);
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${rpcName}`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          p_query: trimmed,
          p_limit: 20,
        }),
      });
      if (!res.ok) {
        setSearchResults([]);
        return;
      }
      const data = await res.json();
      // Supabase RPC vraca direktno array, mapiramo na frontend format
      const results = (data || []).map(m => ({
        id: m.id,
        firstName: m.player_first_name || "",
        lastName: m.player_last_name || "",
        teamName: m.name || "",
        region: m.player_region_name,
        regionIso: m.player_region_iso_code_short,
        points: m.summary_overall_points || 0,
        overallRank: m.summary_overall_rank,
        score: m.score || 0,
      }));
      setSearchResults(results);

      // Plausible tracking sa search_type prop-om za novu analitiku
      const truncatedQuery = trimmed.toLowerCase().slice(0, 50);
      if (results.length === 0) {
        trackEvent("Search No Results", {
          query: truncatedQuery,
          query_words: queryWords,
          search_type: inputMode,
        });
      } else {
        trackEvent("Search Performed", {
          query: truncatedQuery,
          query_words: queryWords,
          results_count: Math.min(results.length, 20),
          search_type: inputMode,
        });
      }
    } catch (e) {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [teamId, inputMode]);

  const handleSearchPick = useCallback(async (managerId) => {
    const idStr = String(managerId);
    setTeamId(idStr);
    setSearchResults(null);
    setUserLeagues(null);
    setUserInfo(null);
    setError(null);
    setLoading(true);
    try {
      const data = await apiFetch(`/entry/${idStr}/`);
      setUserInfo({
        name: `${data.player_first_name || ""} ${data.player_last_name || ""}`.trim() || "?",
        teamName: data.name || "?",
        overallRank: data.summary_overall_rank,
        totalPoints: data.summary_overall_points,
        eventPoints: data.summary_event_points,
      });
      setUserLeagues(data.leagues?.classic || []);
      // v52.1: KLIK NA REZULTAT PRETRAGE JESTE IDENTIFIKACIJA.
      // Do sada se fpl_my_team_id pisao SAMO u lookupTeam (rucni unos Team ID-a).
      // Posle re-scrape-a (22–23.08) pretraga po imenu postaje glavni ulaz —
      // bez ovog upisa bi VS/Duel ostao ugasen za vecinu korisnika.
      try {
        if (/^\d+$/.test(idStr)) {
          localStorage.setItem("fpl_my_team_id", idStr);
          localStorage.setItem(SEASON_KEY, SEASON);
        }
      } catch (e) { /* localStorage nedostupan */ }
    } catch (err) {
      if (err.isMaintenance) {
        setFplMaintenance(true);
      } else if (err.notFound) {
        // Dok je STALE_DB true, rezultati pretrage nose ID-eve iz 2025/26.
        // Bez ove grane bi korisnik nasao sebe pa dobio genericku gresku.
        try { localStorage.removeItem("fpl_my_team_id"); } catch (e) { /* ignore */ }
        trackEvent("Stale Team ID", { source: "search_pick" });
        setError(t.teamNotFoundSeason);
      } else {
        setError(t.teamNotFound);
      }
      setUserLeagues(null);
      setUserInfo(null);
    } finally {
      setLoading(false);
    }
  }, [t.teamNotFound, t.teamNotFoundSeason]);

  const loadLeagueByID = useCallback(async (lgId, source = "other") => {
    // v71 (31.08.2026) · Ulaz u ligu se broji, i to po NACINU ulaska.
    // "typed" = covek je rucno ukucao league ID. To je jedini izvor uporediv sa
    // pretragom po imenu/imenu tima; ostali su automatski ili preciice.
    // NAMERNO NIJE dodato kao search_type na "Search Performed": taj brojac je
    // imenilac za `Search No Results` gejt, pa bi mesanje ulaza u ligu tiho
    // pokvarilo metriku koja se koristi za odluku.
    trackEvent("League Loaded", { source });
    setLoading(true);
    setIsDataLoading(true);
    setError(null);
    setScreen("loading");
    try {
      setLeagueId(String(lgId));
      const { data: bs, gw } = await loadBootstrap();
      const fix = await apiFetch("/fixtures/");
      const gwFixtures = fix.filter((f) => f.event === gw.id);
      const processedFixtures = gwFixtures.map((f) => {
        const homeTeam = bs.teams.find((tm) => tm.id === f.team_h);
        const awayTeam = bs.teams.find((tm) => tm.id === f.team_a);
        let status = "upcoming", minute = 0;
        if (f.finished || f.finished_provisional) { status = "finished"; minute = 90; }
        else if (f.started) { status = "inPlay"; minute = f.minutes || 0; }
        return {
          id: f.id,
          home: homeTeam?.short_name || "?",
          away: awayTeam?.short_name || "?",
          homeFull: homeTeam?.name || "?",
          awayFull: awayTeam?.name || "?",
          homeScore: f.team_h_score ?? 0,
          awayScore: f.team_a_score ?? 0,
          status, minute,
          team_h: f.team_h, team_a: f.team_a,
          stats: f.stats || [],
        };
      });
      setFixtures(processedFixtures);

      const live = await apiFetch(`/event/${gw.id}/live/`);
      const liveMap = {};
      live.elements.forEach((el) => {
        const player = bs.elements.find((p) => p.id === el.id);
        const playerStatus = statusIgraca(meceviKluba(gwFixtures, player?.team));
        liveMap[el.id] = {
          id: el.id, points: el.stats.total_points, minutes: el.stats.minutes,
          goals: el.stats.goals_scored, assists: el.stats.assists,
          cleanSheet: el.stats.clean_sheets > 0, saves: el.stats.saves,
          yellows: el.stats.yellow_cards, reds: el.stats.red_cards,
          bps: el.stats.bps, bonus: el.stats.bonus,
          goalsConceded: el.stats.goals_conceded,
          ownGoals: el.stats.own_goals,
          penaltiesSaved: el.stats.penalties_saved,
          penaltiesMissed: el.stats.penalties_missed,
          defensiveContribution: el.stats.defensive_contribution || 0,
          ...zastaviceStatusa(playerStatus),
        };
      });
      setLiveData(liveMap);

      const league = await apiFetch(`/leagues-classic/${lgId}/standings/`);
      
      // Defensive check: FPL sometimes returns 200 OK with an unexpected structure during maintenance
      if (!league || !league.standings || !Array.isArray(league.standings.results)) {
        const err = new Error("League data missing expected structure (likely FPL maintenance)");
        err.isMaintenance = true;
        throw err;
      }

      // v52: PRE GW1 NIJEDNA liga nema tabelu — FPL vraca 200 sa results: [].
      // Provereno 10.08.2026 na ligama 314 (Overall) i 210 (Serbia): oba prazna.
      // Bez ovoga korisnik udje u ligu i gleda praznu tabelu bez objasnjenja.
      if (league.standings.results.length === 0) {
        const err = new Error("League has no standings yet (pre-season or brand new league)");
        err.emptyLeague = true;
        throw err;
      }

      setLeagueData(league);

      // Show the league screen NOW with skeleton — names visible, numbers shimmer
      localStorage.setItem("fpl_last_league", String(lgId));
      setScreen("league");

      // Load picks/histories progressively in background
      const managers = league.standings.results.slice(0, 50);
      const picksMap = {};
      const historiesMap = {};
      for (let i = 0; i < managers.length; i += 5) {
        const batch = managers.slice(i, i + 5);
        await Promise.all(batch.map(async (m) => {
          try { picksMap[m.entry] = await apiFetch(`/entry/${m.entry}/event/${gw.id}/picks/`); } catch (e) {}
          try { historiesMap[m.entry] = await apiFetch(`/entry/${m.entry}/history/`); } catch (e) {}
        }));
        // Incrementally update state so rows populate as data arrives
        setManagerPicks({ ...picksMap });
        setManagerHistories({ ...historiesMap });
      }
      setLastUpdate(new Date());
      setIsDataLoading(false);
    } catch (err) {
      if (err.isMaintenance) {
        setFplMaintenance(true);
      } else {
        // v52 BUG FIX: ranije je ostajalo screen === "loading" zauvek.
        // setError() pise poruku koja se renderuje na HOME ekranu, a home se
        // nikad nije prikazao — korisnik je gledao spinner u beskraj.
        // Provereno 10.08: liga 1236390 (prosla sezona) -> HTTP 404.
        // LIGE TAKODJE DOBIJAJU NOVE ID-EVE SVAKE SEZONE, ne samo timovi.
        const deadLeague = err.notFound || err.status === 404;
        if (deadLeague) {
          try { localStorage.removeItem("fpl_last_league"); } catch (e) { /* ignore */ }
          trackEvent("Stale League ID", { league: String(lgId).slice(0, 12) });
        } else if (err.emptyLeague) {
          try { localStorage.removeItem("fpl_last_league"); } catch (e) { /* ignore */ }
          trackEvent("Empty League", { league: String(lgId).slice(0, 12) });
        }
        setError(deadLeague ? t.leagueNotFoundSeason : (err.emptyLeague ? t.leagueEmptyPreseason : t.leagueError));
        setScreen("home");
      }
      setIsDataLoading(false);
    } finally {
      setLoading(false);
    }
  }, [loadBootstrap, t.leagueError, t.leagueNotFoundSeason, t.leagueEmptyPreseason]);

  // v52: Jedan dogadjaj po sesiji — imenilac za kill-gate (okt/nov).
  // U Plausible-u: Session Start razbijen po weeks_since_first daje kohortu
  // ("koliko ljudi iz GW1 nedelje se vratilo u nedelji 3"), sto obicni
  // pageview ne moze da odgovori za SPA.
  const sessionStartRef = useRef(false);
  useEffect(() => {
    if (sessionStartRef.current) return;
    sessionStartRef.current = true;
    trackEvent("Session Start", { entry: INVITED_LEAGUE_ID ? "invite" : (localStorage.getItem("fpl_last_league") ? "returning" : "cold") });
  }, []);

  const autoLoadRef = useRef(false);
  useEffect(() => {
    if (autoLoadRef.current) return;
    autoLoadRef.current = true;
    // v52: ?liga=<id> ima prednost nad sacuvanom ligom.
    // Ovo je mehanizam rasta: jedan korisnik posalje link i dovede celu
    // mini-ligu odjednom. Duel/VS ekran nema smisla ako rivali nisu unutra.
    if (INVITED_LEAGUE_ID) {
      trackEvent("League Invite Opened", { league: INVITED_LEAGUE_ID });
      loadLeagueByID(INVITED_LEAGUE_ID, "invite");
      return;
    }
    const lastLeague = localStorage.getItem("fpl_last_league");
    if (lastLeague) {
      loadLeagueByID(lastLeague, "restore");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch userInfo + userLeagues SAMO na mount-u ako postoji sacuvan teamId iz localStorage.
  // NE reaguje na svaku promenu teamId-ja (lookupTeam radi to eksplicitno na klik/search pick).
  // Time se izbegava dupli fetch + flicker.
  useEffect(() => {
    if (!teamId) return;
    apiFetch(`/entry/${teamId}/`)
      .then(data => {
        setUserInfo({
          name: `${data.player_first_name || ""} ${data.player_last_name || ""}`.trim() || "?",
          teamName: data.name || "?",
          overallRank: data.summary_overall_rank,
          totalPoints: data.summary_overall_points,
          eventPoints: data.summary_event_points,
        });
        setUserLeagues(data.leagues?.classic || []);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate provisional bonus ONCE, memoized on liveData/bootstrap/fixtures
  const bonusMap = useMemo(() => {
    if (!liveData || !bootstrap || !fixtures) return {};
    return calculateProvisionalBonusByFixture(liveData, bootstrap, fixtures);
  }, [liveData, bootstrap, fixtures]);

  // Enriched live data uses memoized bonusMap
  const enrichedLive = useMemo(() => {
    if (!liveData) return null;
    const enriched = {};
    for (const id in liveData) {
      const player = liveData[id];
      const provBonus = bonusMap[id] || 0;
      const alreadyHasBonus = player.bonus > 0;
      enriched[id] = {
        ...player,
        points: alreadyHasBonus ? player.points : player.points + provBonus,
        provisionalBonus: alreadyHasBonus ? 0 : provBonus,
      };
    }
    return enriched;
  }, [liveData, bonusMap]);

  // Heavy computation: process 50 managers, sort them, calculate previous ranks
  // Memoized so this only re-runs when underlying data changes
  const sortedManagers = useMemo(() => {
    if (!leagueData || !leagueData.standings || !Array.isArray(leagueData.standings.results)) return [];
    
    const processed = leagueData.standings.results.slice(0, 50).map((m) => {
      const picks = managerPicks[m.entry];
      const history = managerHistories[m.entry];
      const hasPicks = !!picks && !!enrichedLive && !!bootstrap;
      let gwPoints = 0, captainName = "?", captainId = null;
      let processedPicks = [];
      if (hasPicks) {
        const isBboost = picks.active_chip === "bboost";
        const isTripleCap = picks.active_chip === "3xc";
        processedPicks = processPicksSaAutosubovima(picks, enrichedLive, bootstrap, fixtures);
        const capPick = processedPicks.find(p => p.is_captain);
        const vcPick = processedPicks.find(p => p.is_vice_captain);
        if (capPick && vcPick) {
          const capLive = enrichedLive[capPick.element];
          const capPlayer = bootstrap.elements.find(p => p.id === capPick.element);
          const capFx = capPlayer ? fixtures.find(f => f.team_h === capPlayer.team || f.team_a === capPlayer.team) : null;
          if (capLive && capLive.minutes === 0 && capFx && capFx.status === "finished") {
            capPick.multiplier = capPick.wasSubbedOut ? 0 : 1;
            vcPick.multiplier = isTripleCap ? 3 : 2;
            captainId = vcPick.element;
            captainName = bootstrap.elements.find(p => p.id === vcPick.element)?.web_name || "?";
          } else {
            captainId = capPick.element;
            captainName = bootstrap.elements.find(p => p.id === capPick.element)?.web_name || "?";
          }
        }
        const toCount = isBboost ? processedPicks : processedPicks.filter(p => p.multiplier > 0);
        toCount.forEach((pick) => {
          const live = enrichedLive[pick.element];
          if (live) gwPoints += live.points * pick.multiplier;
        });
      }
      const hitCost = picks?.entry_history?.event_transfers_cost || 0;
      const form = history?.current ? history.current.slice(-5).map((gw) => gw.points - gw.event_transfers_cost) : [];
      const activeChip = picks?.active_chip || null;
      // FIX: Koristi history.current[lastGW].total_points (live FPL data) umesto m.total (league standings - updatuje se 1x dnevno)
      // history.current sadrzi sve odigrane GW-ove SA total_points kumulativno posle svakog
      // Trazimo last GW PRE trenutnog (current GW jos uvek nije zavrsen)
      let previousTotal;
      const currentGwId = currentGW?.id;
      if (history?.current && currentGwId) {
        // Trazimo poslednji GW manje od trenutnog
        const previousGws = history.current.filter(gw => gw.event < currentGwId);
        if (previousGws.length > 0) {
          // total_points u poslednjem prethodnom GW = total bez trenutnog
          previousTotal = previousGws[previousGws.length - 1].total_points;
        } else {
          // Prvi GW ili nema history - fallback na 0
          previousTotal = 0;
        }
      } else {
        // Fallback na stari nacin ako history nije ucitan
        previousTotal = hasPicks ? (m.total - (picks?.entry_history?.points || 0)) : m.total;
      }
      return {
        id: m.entry,
        name: m.player_name,
        teamName: m.entry_name,
        rank: m.rank,
        lastRank: m.last_rank,
        previousTotal,
        captainName,
        captainId,
        gwPoints,
        hitCost,
        gwNet: gwPoints - hitCost,
        totalLive: hasPicks ? (previousTotal + gwPoints - hitCost) : m.total,
        form,
        activeChip,
        picks: processedPicks,
        hasPicks,
      };
    });
    
    // Sort by live total
    const sorted = [...processed].sort((a, b) => b.totalLive - a.totalLive);
    sorted.forEach((m, i) => (m.liveRank = i + 1));
    
    // Calculate previous rank (before this GW) by sorting on previousTotal
    const byPreviousTotal = [...processed].sort((a, b) => b.previousTotal - a.previousTotal);
    byPreviousTotal.forEach((m, i) => {
      const liveManagerRef = sorted.find(sm => sm.id === m.id);
      if (liveManagerRef) liveManagerRef.previousRank = i + 1;
    });
    
    return sorted;
  }, [leagueData, managerPicks, managerHistories, enrichedLive, bootstrap, fixtures, currentGW?.id]);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [previousPoints, setPreviousPoints] = useState({});

  // Use ref to keep latest data without triggering useCallback recreation
  const liveDataRef = useRef(liveData);
  useEffect(() => { liveDataRef.current = liveData; }, [liveData]);

  const refreshLiveData = useCallback(async () => {
    try {
      if (!currentGW || !bootstrap) return;
      setIsRefreshing(true);
      
      // Refresh fixtures and live data in parallel
      const [fixData, live] = await Promise.all([
        apiFetch("/fixtures/"),
        apiFetch(`/event/${currentGW.id}/live/`),
      ]);
      
      // Update fixtures (minute, score, status — important for live scores)
      const gwFixtures = fixData.filter((f) => f.event === currentGW.id);
      const updatedFixtures = gwFixtures.map((f) => {
        const homeTeam = bootstrap.teams.find((tm) => tm.id === f.team_h);
        const awayTeam = bootstrap.teams.find((tm) => tm.id === f.team_a);
        let status = "upcoming", minute = 0;
        if (f.finished || f.finished_provisional) { status = "finished"; minute = 90; }
        else if (f.started) { status = "inPlay"; minute = f.minutes || 0; }
        return {
          id: f.id,
          home: homeTeam?.short_name || "?",
          away: awayTeam?.short_name || "?",
          homeFull: homeTeam?.name || "?",
          awayFull: awayTeam?.name || "?",
          homeScore: f.team_h_score ?? 0,
          awayScore: f.team_a_score ?? 0,
          status, minute,
          team_h: f.team_h, team_a: f.team_a,
          stats: f.stats || [],
        };
      });
      setFixtures(updatedFixtures);
      
      const liveMap = {};
      live.elements.forEach((el) => {
        const player = bootstrap.elements.find((p) => p.id === el.id);
        // Use freshly updated fixtures (not stale closure) for player status
        const playerStatus = statusIgraca(meceviKluba(updatedFixtures, player?.team));
        liveMap[el.id] = {
          id: el.id, points: el.stats.total_points, minutes: el.stats.minutes,
          goals: el.stats.goals_scored, assists: el.stats.assists,
          cleanSheet: el.stats.clean_sheets > 0, saves: el.stats.saves,
          yellows: el.stats.yellow_cards, reds: el.stats.red_cards,
          bps: el.stats.bps, bonus: el.stats.bonus,
          goalsConceded: el.stats.goals_conceded,
          ownGoals: el.stats.own_goals,
          penaltiesSaved: el.stats.penalties_saved,
          penaltiesMissed: el.stats.penalties_missed,
          defensiveContribution: el.stats.defensive_contribution || 0,
          ...zastaviceStatusa(playerStatus),
        };
      });
      // Track previous points for animation (using ref to avoid re-renders)
      const prevLive = liveDataRef.current;
      if (prevLive) {
        const prevPts = {};
        for (const id in prevLive) {
          prevPts[id] = prevLive[id].points;
        }
        setPreviousPoints(prevPts);
      }
      setLiveData(liveMap);
      setLastUpdate(new Date());
    } catch (e) {}
    finally {
      setTimeout(() => setIsRefreshing(false), 400);
    }
  }, [currentGW, bootstrap]); // Removed liveData - using ref instead

  // Use ref to call latest refreshLiveData without resetting interval
  const refreshFnRef = useRef(refreshLiveData);
  useEffect(() => { refreshFnRef.current = refreshLiveData; }, [refreshLiveData]);

  useEffect(() => {
    if (screen === "league") {
      intervalRef.current = setInterval(() => {
        refreshFnRef.current();
      }, 60000);
      return () => clearInterval(intervalRef.current);
    }
  }, [screen]); // Removed refreshLiveData - using ref instead, so interval never resets

  // Pull-to-refresh
  useEffect(() => {
    if (screen !== "league") return;
    let startY = 0;
    let pulling = false;

    const onTouchStart = (e) => {
      if (window.scrollY > 0) return;
      startY = e.touches[0].clientY;
      pulling = true;
    };

    const onTouchMove = (e) => {
      if (!pulling) return;
      const currentY = e.touches[0].clientY;
      const distance = currentY - startY;
      if (distance > 0 && window.scrollY === 0) {
        const dampedDistance = Math.min(distance * 0.5, 100);
        setPullDistance(dampedDistance);
      }
    };

    const onTouchEnd = () => {
      if (!pulling) return;
      pulling = false;
      if (pullDistance > 60) {
        refreshLiveData();
      }
      setPullDistance(0);
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd);

    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [screen, pullDistance, refreshLiveData]);

  const navigateToLeague = (lgId) => {
    setSelectedManager(null);
    setSelectedPlayer(null);
    setSelectedFixture(null);
    clearInterval(intervalRef.current);
    loadLeagueByID(lgId, "navigate");
    window.scrollTo(0, 0);
  };

  // Retry handler for FPL maintenance screen
  const handleMaintenanceRetry = useCallback(async () => {
    setRetrying(true);
    try {
      await apiFetch("/bootstrap-static/");
      setFplMaintenance(false);
      const lastLeague = localStorage.getItem("fpl_last_league");
      if (lastLeague) {
        loadLeagueByID(lastLeague, "maintenance_retry");
      }
    } catch (err) {
      setTimeout(() => setRetrying(false), 500);
      return;
    }
    setRetrying(false);
  }, [loadLeagueByID]);

  // Auto-retry every 30s when on maintenance screen
  useEffect(() => {
    if (!fplMaintenance) return;
    const timer = setInterval(async () => {
      try {
        await apiFetch("/bootstrap-static/");
        setFplMaintenance(false);
        const lastLeague = localStorage.getItem("fpl_last_league");
        if (lastLeague) {
          loadLeagueByID(lastLeague, "maintenance_retry");
        }
      } catch {}
    }, 30000);
    return () => clearInterval(timer);
  }, [fplMaintenance, loadLeagueByID]);

  // Show maintenance screen if FPL API is down
  if (fplMaintenance) {
    return (
      <div style={S.page}><style>{CSS}</style>
        <FPLMaintenanceScreen t={t} onRetry={handleMaintenanceRetry} retrying={retrying} />
      </div>
    );
  }

  if (screen === "loading") {
    return (
      <div style={S.page}><style>{CSS}</style>
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 24 }}>
          <div style={{ position: "relative" }}>
            <div style={{ position: "absolute", inset: -20, borderRadius: "50%", background: "radial-gradient(circle, rgba(37,99,235,0.15) 0%, transparent 70%)", animation: "glowPulse 2s ease infinite" }}/>
            <div style={{ position: "relative" }}><Logo size={64}/></div>
          </div>
          <div style={{ width: 24, height: 24, border: "2.5px solid #dbeafe", borderTopColor: "#2563eb", borderRadius: "50%", animation: "spin 0.7s linear infinite" }}/>
          <div style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>{t.loadingYourLeague}</div>
        </div>
      </div>
    );
  }

  if (screen === "home") {
    return (
      <div style={S.page}><style>{CSS}</style>
        {/* Tutorial Modal */}
        {showTutorial && (
          <div style={S.overlay} onClick={() => setShowTutorial(false)}>
            <div style={{ ...S.modal, maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
                <div>
                  <h2 style={{ fontSize: 19, fontWeight: 700, margin: 0, color: "#0f172a" }}>{t.tutorialTitle}</h2>
                  <p style={{ fontSize: 13, color: "#64748b", margin: "6px 0 0", lineHeight: 1.5 }}>{t.tutorialIntro}</p>
                </div>
                <button onClick={() => setShowTutorial(false)} style={S.closeBtn}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11 3L3 11M3 3l8 8" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round"/></svg>
                </button>
              </div>

              {/* Step 1: Desktop */}
              <div style={{ padding: 16, background: "#f8fafc", borderRadius: 12, marginBottom: 10, border: "1px solid #edf2f7" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 22, height: 22, background: "#2563eb", color: "#fff", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, fontFamily: "var(--mono)" }}>1</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>💻 {t.tutorialStep1Title}</div>
                </div>
                <p style={{ fontSize: 12, color: "#475569", lineHeight: 1.6, margin: "0 0 10px", paddingLeft: 30 }}>{t.tutorialStep1}</p>
                
                {/* Browser + FPL Points page illustration */}
                <div style={{ paddingLeft: 30 }}>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 6, fontWeight: 500 }}>
                    {lang === "en" ? "Here's how it looks in the browser:" : "Evo kako to izgleda u browseru:"}
                  </div>
                  
                  {/* Container with screenshot + arrow overlay */}
                  <div style={{ position: "relative", paddingBottom: 28 }}>
                    
                    {/* Browser frame */}
                    <div style={{ borderRadius: 8, overflow: "hidden", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
                      
                      {/* Top bar with traffic lights + URL */}
                      <div style={{ background: "#f1f3f4", padding: "8px 10px", borderBottom: "1px solid #dadce0" }}>
                        {/* Traffic lights */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                          <div style={{ display: "flex", gap: 4 }}>
                            <div style={{ width: 9, height: 9, borderRadius: "50%", background: "#ed6a5e" }}/>
                            <div style={{ width: 9, height: 9, borderRadius: "50%", background: "#f5bf4f" }}/>
                            <div style={{ width: 9, height: 9, borderRadius: "50%", background: "#62c554" }}/>
                          </div>
                        </div>
                        
                        {/* URL row with red border */}
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M9 3 L4 7 L9 11" stroke="#5f6368" strokeWidth="1.6" strokeLinecap="round" fill="none"/></svg>
                          <svg width="11" height="11" viewBox="0 0 14 14" fill="none" style={{ opacity: 0.4 }}><path d="M5 3 L10 7 L5 11" stroke="#5f6368" strokeWidth="1.6" strokeLinecap="round" fill="none"/></svg>
                          <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M12 7 A5 5 0 1 1 7 2 M12 2 L12 5 L9 5" stroke="#5f6368" strokeWidth="1.5" strokeLinecap="round" fill="none"/></svg>
                          <div style={{ flex: 1, background: "#fff", border: "2px solid #dc2626", borderRadius: 14, padding: "5px 10px", fontSize: 10, color: "#202124", fontFamily: "var(--mono)", display: "flex", alignItems: "center", gap: 4, position: "relative", zIndex: 2 }}>
                            <svg width="9" height="9" viewBox="0 0 14 14" fill="none" style={{ opacity: 0.6, flexShrink: 0 }}>
                              <rect x="3" y="6" width="8" height="6" rx="1" fill="none" stroke="#5f6368" strokeWidth="1.3"/>
                              <path d="M5 6 V4 A2 2 0 0 1 9 4 V6" fill="none" stroke="#5f6368" strokeWidth="1.3"/>
                            </svg>
                            <span style={{ color: "#202124", fontWeight: 600 }}>fantasy.premierleague.com/entry/5647565/event/34</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Fantasy logo banner */}
                      <div style={{ background: "linear-gradient(180deg, #4f5dab 0%, #6e6cb6 100%)", padding: "6px 10px" }}>
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 4 }}>
                          <svg width="10" height="10" viewBox="0 0 14 14" fill="none"><path d="M7 1 L13 4 L13 9 L7 13 L1 9 L1 4 Z" fill="#fff"/></svg>
                          <span style={{ color: "#fff", fontSize: 9, fontWeight: 700, letterSpacing: "0.4px" }}>Fantasy</span>
                        </div>
                      </div>
                      
                      {/* Top nav with Points highlighted */}
                      <div style={{ background: "#37003c", padding: "5px 10px", borderBottom: "1px solid #2d0030" }}>
                        <div style={{ display: "flex", gap: 11, justifyContent: "center", flexWrap: "wrap" }}>
                          <span style={{ color: "#fff", fontSize: 8, opacity: 0.7 }}>Status</span>
                          <span style={{ color: "#fff", fontSize: 8, fontWeight: 700, borderBottom: "1.5px solid #00ff85" }}>Points</span>
                          <span style={{ color: "#fff", fontSize: 8, opacity: 0.7 }}>Pick Team</span>
                          <span style={{ color: "#fff", fontSize: 8, opacity: 0.7 }}>Transfers</span>
                          <span style={{ color: "#fff", fontSize: 8, opacity: 0.7 }}>Leagues</span>
                          <span style={{ color: "#fff", fontSize: 8, opacity: 0.7 }}>Fixtures</span>
                        </div>
                      </div>
                      
                      {/* Main content: stats panel + GW box + pitch */}
                      <div style={{ background: "#fff", padding: 8 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: 8 }}>
                          
                          {/* Left: Manager stats */}
                          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 5, padding: 6 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                              <div style={{ width: 12, height: 12, background: "#37003c", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "#00ff85", fontSize: 8, fontWeight: 700 }}>+</div>
                              <div style={{ fontSize: 9, fontWeight: 700, color: "#37003c" }}>FPL_Manager</div>
                            </div>
                            <div style={{ fontSize: 7, color: "#6b7280", marginBottom: 6 }}>M. Petrovic 🇷🇸</div>
                            <div style={{ fontSize: 8, fontWeight: 700, color: "#37003c", marginBottom: 4 }}>Points & Rankings</div>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 7, color: "#6b7280", marginBottom: 1 }}><span>Overall points</span><span style={{ color: "#37003c", fontWeight: 700 }}>1,803</span></div>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 7, color: "#6b7280", marginBottom: 1 }}><span>Overall rank</span><span style={{ color: "#37003c", fontWeight: 700 }}>2.5M</span></div>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 7, color: "#6b7280" }}><span>GW points</span><span style={{ color: "#37003c", fontWeight: 700 }}>32</span></div>
                          </div>
                          
                          {/* Right: GW box + pitch */}
                          <div>
                            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 5, padding: 5, marginBottom: 5 }}>
                              <div style={{ textAlign: "center", fontSize: 9, fontWeight: 700, color: "#37003c" }}>Gameweek 34</div>
                              <div style={{ display: "flex", justifyContent: "space-around", marginTop: 4 }}>
                                <div style={{ textAlign: "center" }}><div style={{ fontSize: 11, fontWeight: 700, color: "#37003c", fontFamily: "var(--mono)" }}>3</div><div style={{ fontSize: 6, color: "#6b7280" }}>Avg</div></div>
                                <div style={{ textAlign: "center" }}><div style={{ fontSize: 11, fontWeight: 700, color: "#37003c", fontFamily: "var(--mono)" }}>67</div><div style={{ fontSize: 6, color: "#6b7280" }}>High</div></div>
                                <div style={{ textAlign: "center", background: "#00ff85", padding: "2px 6px", borderRadius: 3 }}><div style={{ fontSize: 11, fontWeight: 700, color: "#37003c", fontFamily: "var(--mono)" }}>32</div><div style={{ fontSize: 6, color: "#37003c", fontWeight: 700 }}>Latest</div></div>
                              </div>
                            </div>
                            
                            {/* Pitch */}
                            <div style={{ background: "linear-gradient(180deg, #00ff85 0%, #00d975 100%)", borderRadius: 5, padding: 5, minHeight: 78, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                              <svg viewBox="0 0 90 60" style={{ width: "100%", height: 60 }}>
                                <rect x="0" y="0" width="90" height="60" fill="#16a34a"/>
                                <rect x="20" y="2" width="50" height="22" fill="none" stroke="#fff" strokeWidth="0.5"/>
                                <rect x="32" y="2" width="26" height="10" fill="none" stroke="#fff" strokeWidth="0.5"/>
                                <line x1="0" y1="30" x2="90" y2="30" stroke="#fff" strokeWidth="0.5"/>
                                <circle cx="45" cy="30" r="6" fill="none" stroke="#fff" strokeWidth="0.5"/>
                                <rect x="42" y="6" width="6" height="6" fill="#94a3b8"/>
                                <rect x="20" y="22" width="6" height="5" fill="#dc2626"/>
                                <rect x="40" y="22" width="6" height="5" fill="#dc2626"/>
                                <rect x="60" y="22" width="6" height="5" fill="#fff"/>
                                <rect x="14" y="36" width="6" height="6" fill="#dc2626"/>
                                <rect x="30" y="36" width="6" height="6" fill="#fff"/>
                                <rect x="46" y="36" width="6" height="6" fill="#dc2626"/>
                                <rect x="62" y="36" width="6" height="6" fill="#dc2626"/>
                                <rect x="78" y="36" width="6" height="6" fill="#dc2626"/>
                                <rect x="32" y="48" width="6" height="6" fill="#fbbf24"/>
                                <rect x="52" y="48" width="6" height="6" fill="#dc2626"/>
                              </svg>
                            </div>
                          </div>
                          
                        </div>
                      </div>
                    </div>
                    
                    {/* Red arrow overlay - bottom-left to URL bar */}
                    <svg style={{ position: "absolute", bottom: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 3, overflow: "visible" }} viewBox="0 0 400 320" preserveAspectRatio="xMidYMid meet">
                      <defs>
                        <marker id="arrTutorial" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                          <polygon points="0 0, 6 3, 0 6" fill="#dc2626"/>
                        </marker>
                      </defs>
                      <path d="M 90 305 Q 70 200, 90 70" stroke="#dc2626" strokeWidth="2" fill="none" strokeLinecap="round" markerEnd="url(#arrTutorial)" />
                    </svg>
                  </div>
                  
                  {/* Red warning */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 8, marginTop: 12 }}>
                    <div style={{ background: "#dc2626", color: "#fff", width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>!</div>
                    <div style={{ fontSize: 11, color: "#991b1b", fontWeight: 600, lineHeight: 1.4 }}>
                      {lang === "en" ? <>Copy the whole URL — we'll extract the number between <span style={{ background: "#fee2e2", padding: "1px 3px", borderRadius: 2, fontFamily: "var(--mono)" }}>/entry/</span> and <span style={{ background: "#fee2e2", padding: "1px 3px", borderRadius: 2, fontFamily: "var(--mono)" }}>/event/</span></> : <>Kopiraj ceo URL — mi cemo izvuci broj izmedju <span style={{ background: "#fee2e2", padding: "1px 3px", borderRadius: 2, fontFamily: "var(--mono)" }}>/entry/</span> i <span style={{ background: "#fee2e2", padding: "1px 3px", borderRadius: 2, fontFamily: "var(--mono)" }}>/event/</span></>}
                    </div>
                  </div>
                  
                  {/* New CTA: "Nalepi CEO LINK u FplCenter" */}
                  <div style={{ padding: "11px 14px", background: "#2563eb", borderRadius: 8, textAlign: "center", boxShadow: "0 2px 8px rgba(37,99,235,0.3)", marginTop: 10 }}>
                    <div style={{ color: "#fff", fontSize: 14, fontWeight: 700 }}>
                      {lang === "en" ? <>Paste the <span style={{ fontWeight: 800, letterSpacing: "0.5px" }}>WHOLE LINK</span> in FplCenter</> : <>Nalepi <span style={{ fontWeight: 800, letterSpacing: "0.5px" }}>CEO LINK</span> u FplCenter</>}
                    </div>
                    <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 10, marginTop: 4, fontWeight: 500 }}>
                      {lang === "en" ? "We'll extract the number automatically" : "Mi cemo automatski izvuci broj"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2: Mobile app only */}
              <div style={{ padding: 16, background: "#fef3c7", borderRadius: 12, marginBottom: 10, border: "1px solid #fde68a" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 22, height: 22, background: "#b45309", color: "#fff", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, fontFamily: "var(--mono)" }}>2</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#78350f" }}>📱 {t.tutorialStep2Title}</div>
                </div>
                <p style={{ fontSize: 12, color: "#78350f", lineHeight: 1.6, margin: 0, paddingLeft: 30 }}>{t.tutorialStep2}</p>
              </div>

              {/* Step 3: Easiest */}
              <div style={{ padding: 16, background: "#dcfce7", borderRadius: 12, marginBottom: 18, border: "1px solid #bbf7d0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 22, height: 22, background: "#15803d", color: "#fff", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, fontFamily: "var(--mono)" }}>✨</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#14532d" }}>⚡ {t.tutorialStep3Title}</div>
                </div>
                <p style={{ fontSize: 12, color: "#14532d", lineHeight: 1.6, margin: 0, paddingLeft: 30 }}>{t.tutorialStep3}</p>
              </div>

              <button onClick={() => setShowTutorial(false)} style={{ ...S.btn, marginTop: 0 }}>
                {t.closeBtn}
              </button>
            </div>
          </div>
        )}

        <div style={{ position: "absolute", top: 16, right: 20, display: "flex", background: "#fff", borderRadius: 8, padding: 2, border: "1px solid #e2e8f0", boxShadow: "0 1px 2px rgba(0,0,0,0.04)", zIndex: 10 }}>
          <button onClick={() => setLang("en")} style={{ padding: "5px 10px", border: "none", borderRadius: 6, background: lang === "en" ? "#2563eb" : "transparent", color: lang === "en" ? "#fff" : "#64748b", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "var(--sans)", letterSpacing: "0.3px" }}>EN</button>
          <button onClick={() => setLang("sr")} style={{ padding: "5px 10px", border: "none", borderRadius: 6, background: lang === "sr" ? "#2563eb" : "transparent", color: lang === "sr" ? "#fff" : "#64748b", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "var(--sans)", letterSpacing: "0.3px" }}>SR</button>
        </div>

        <div style={S.homeWrap}>
          <div style={S.hero}>
            <div style={S.heroGlow}/>
            <div style={{ position: "relative", zIndex: 1 }}>
              <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}><Logo size={64}/></div>
              <h1 style={S.heroTitle}>FPL Center</h1>
              <p style={S.heroSub}>{t.tagline}</p>
            </div>
          </div>

          {/* v53: Prvo sto se procita na home ekranu do 21.08 19:30.
              Ide IZNAD unosa jer je unos do tada besmislen bez objasnjenja. */}
          <PreSeasonNotice t={t} lang={lang} />

          {/* v52: Obavestenje kad su podaci iz prosle sezone obrisani.
              Prikazuje se SAMO ako je stvarno nesto bilo sacuvano — nov korisnik ga ne vidi. */}
          {SEASON_WAS_RESET && (
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 12, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: "#92400e", marginBottom: 4, fontFamily: "var(--sans)" }}>{t.seasonResetTitle}</div>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: "#a16207", lineHeight: 1.5, fontFamily: "var(--sans)" }}>{t.seasonResetBody}</div>
            </div>
          )}

          <div style={S.card}>
            <div style={{ display: "flex", background: "#f1f5f9", borderRadius: 10, padding: 3, marginBottom: 20, gap: 2 }}>
              <button onClick={() => { setInputMode("player"); setError(null); setSearchResults(null); }} style={{ flex: 1, padding: "8px 6px", borderRadius: 8, border: "none", background: inputMode === "player" ? "#fff" : "transparent", color: inputMode === "player" ? "#2563eb" : "#64748b", fontWeight: 600, fontSize: 12, cursor: "pointer", boxShadow: inputMode === "player" ? "0 1px 3px rgba(0,0,0,0.08)" : "none", fontFamily: "var(--sans)" }}>{t.tabPlayer}</button>
              <button onClick={() => { setInputMode("team"); setError(null); setSearchResults(null); }} style={{ flex: 1, padding: "8px 6px", borderRadius: 8, border: "none", background: inputMode === "team" ? "#fff" : "transparent", color: inputMode === "team" ? "#2563eb" : "#64748b", fontWeight: 600, fontSize: 12, cursor: "pointer", boxShadow: inputMode === "team" ? "0 1px 3px rgba(0,0,0,0.08)" : "none", fontFamily: "var(--sans)" }}>{t.tabTeamName}</button>
              <button onClick={() => { setInputMode("league"); setError(null); setSearchResults(null); }} style={{ flex: 1, padding: "8px 6px", borderRadius: 8, border: "none", background: inputMode === "league" ? "#fff" : "transparent", color: inputMode === "league" ? "#2563eb" : "#64748b", fontWeight: 600, fontSize: 12, cursor: "pointer", boxShadow: inputMode === "league" ? "0 1px 3px rgba(0,0,0,0.08)" : "none", fontFamily: "var(--sans)" }}>{t.tabLeague}</button>
            </div>

            {(inputMode === "player" || inputMode === "team") ? (
              <>
                {/* v52: Baza ima ID-eve iz 2025/26 dok se ne uradi ponovni scrape (22–23.08).
                    Do tada pretraga po imenu moze da nadje coveka i vrati MRTAV ili TUDJ ID.
                    Zato jasno upozorenje + precica na unos Team ID-a.
                    OVAJ BLOK SE BRISE POSLE PONOVNOG SCRAPE-A — vidi STALE_DB ispod. */}
                {STALE_DB && (
                  <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: 10, marginBottom: 12 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 600, color: "#991b1b", lineHeight: 1.5, fontFamily: "var(--sans)" }}>{t.staleDbWarning}</div>
                    <button
                      onClick={() => { setInputMode("league"); setError(null); setSearchResults(null); trackEvent("Stale DB Fallback Click"); }}
                      style={{ marginTop: 6, background: "none", border: "none", padding: 0, color: "#dc2626", fontSize: 11.5, fontWeight: 800, cursor: "pointer", fontFamily: "var(--sans)" }}
                    >{t.useTeamIdInstead}</button>
                  </div>
                )}
                <div style={{ marginBottom: 12 }}>
                  <label style={S.label}>{inputMode === "team" ? t.tabTeamName : t.tabPlayer}</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      value={teamId}
                      onChange={(e) => {
                        const value = e.target.value;
                        // If URL with /entry/, extract Team ID
                        if (value.includes("/entry/")) {
                          setTeamId(extractTeamId(value));
                        } else {
                          // Otherwise keep as-is (number or name search)
                          setTeamId(value);
                        }
                        setUserLeagues(null);
                        setUserInfo(null);
                        // Cisti prethodne search rezultate kad korisnik kuca dalje
                        setSearchResults(null);
                      }}
                      onPaste={(e) => {
                        const pasted = e.clipboardData.getData("text");
                        if (pasted.includes("/entry/")) {
                          e.preventDefault();
                          setTeamId(extractTeamId(pasted));
                          setUserLeagues(null);
                          setUserInfo(null);
                        }
                      }}
                      placeholder={inputMode === "team" ? t.teamNamePlaceholder : t.playerNamePlaceholder}
                      style={S.input}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && teamId) {
                          const trimmed = teamId.trim();
                          if (/^\d+$/.test(trimmed)) {
                            lookupTeam();
                          } else if (isSearchTerm(trimmed) && trimmed.length >= 2) {
                            performSearch();
                          }
                        }
                      }}
                    />
                    {/* Search results dropdown */}
                    {(searchLoading || searchResults) && (
                      <div style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: 6, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.08)", maxHeight: 320, overflowY: "auto", zIndex: 10 }}>
                        {searchLoading && (
                          <div style={{ padding: 16, textAlign: "center", color: "#64748b", fontSize: 13 }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                              <span style={{ width: 12, height: 12, border: "2px solid rgba(37,99,235,0.3)", borderTopColor: "#2563eb", borderRadius: "50%", animation: "spin 0.6s linear infinite", display: "inline-block" }}/>
                              {t.searchLoading}
                            </span>
                          </div>
                        )}
                        {!searchLoading && searchResults && searchResults.length === 0 && (
                          <div style={{ padding: 16, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>{t.searchNoResults}</div>
                        )}
                        {!searchLoading && searchResults && searchResults.length > 0 && (
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            {searchResults.map((r, idx) => {
                              const flag = regionFlag(r.regionIso);
                              const rankStr = formatRankShort(r.overallRank);
                              return (
                              <button
                                key={r.id}
                                onClick={() => handleSearchPick(r.id)}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 10,
                                  padding: "10px 12px",
                                  background: "transparent",
                                  border: "none",
                                  borderBottom: idx < searchResults.length - 1 ? "1px solid #f1f5f9" : "none",
                                  cursor: "pointer",
                                  textAlign: "left",
                                  fontFamily: "var(--sans)",
                                  transition: "background 0.15s",
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = "#eff6ff"}
                                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                              >
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "flex", alignItems: "center", gap: 6 }}>
                                    {flag && <span style={{ fontSize: 14, flexShrink: 0 }}>{flag}</span>}
                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{r.firstName} {r.lastName}</span>
                                  </div>
                                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {r.teamName}
                                  </div>
                                </div>
                                {rankStr && (
                                  <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                                    {rankStr}
                                  </div>
                                )}
                              </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {!userLeagues && (
                  <>
                    <button
                      onClick={() => {
                        const trimmed = teamId.trim();
                        if (/^\d+$/.test(trimmed)) {
                          lookupTeam();
                        } else if (isSearchTerm(trimmed) && trimmed.length >= 2) {
                          performSearch();
                        }
                      }}
                      disabled={!teamId || loading || searchLoading || teamId.trim().length < 2}
                      style={{ ...S.btn, opacity: (!teamId || loading || searchLoading || teamId.trim().length < 2) ? 0.5 : 1, cursor: (!teamId || loading || searchLoading || teamId.trim().length < 2) ? "default" : "pointer" }}
                    >
                      {(loading || searchLoading) ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><span style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.6s linear infinite", display: "inline-block" }}/>{t.loading}</span> : (isSearchTerm(teamId) ? (lang === "sr" ? "Pretrazi" : "Search") : t.findLeagues)}
                    </button>

                    {/* Demo league divider + button
                        v52: gejtovano na DEMO_LEAGUE_ID. Stari hardkodovan 1957483
                        je mrtav (404) — dugme bi svakom novom posetiocu na GW1
                        dalo gresku umesto demoa. */}
                    {DEMO_LEAGUE_ID && (
                    <>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 18, marginBottom: 14 }}>
                      <div style={{ flex: 1, height: 1, background: "#e2e8f0" }}/>
                      <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500, letterSpacing: "0.5px", textTransform: "uppercase" }}>{t.or}</span>
                      <div style={{ flex: 1, height: 1, background: "#e2e8f0" }}/>
                    </div>
                    <button
                      onClick={() => loadLeagueByID(DEMO_LEAGUE_ID, "demo")}
                      disabled={loading}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        width: "100%",
                        padding: "13px 16px",
                        background: "#eff6ff",
                        border: "1px solid #bfdbfe",
                        borderRadius: 10,
                        color: "#2563eb",
                        fontSize: 14,
                        fontWeight: 600,
                        fontFamily: "var(--sans)",
                        cursor: loading ? "default" : "pointer",
                        opacity: loading ? 0.5 : 1,
                        transition: "background 0.15s",
                      }}
                      onMouseOver={(e) => !loading && (e.currentTarget.style.background = "#dbeafe")}
                      onMouseOut={(e) => (e.currentTarget.style.background = "#eff6ff")}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M5 3l6 5-6 5V3z" fill="#2563eb"/>
                      </svg>
                      {t.tryDemo}
                    </button>
                    </>
                    )}
                  </>
                )}
                {userLeagues && userInfo && (
                  <>
                    <div style={{ padding: "14px 16px", background: "#eff6ff", borderRadius: 10, marginBottom: 14, border: "1px solid #dbeafe" }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{userInfo.name}</div>
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                        {userInfo.teamName}
                        {userInfo.overallRank && <> · Rank {userInfo.overallRank.toLocaleString()} · {userInfo.totalPoints} pts</>}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 10 }}>{t.selectLeague}</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto" }}>
                      {userLeagues.length === 0 ? (
                        <div style={{ textAlign: "center", padding: 20, color: "#94a3b8", fontSize: 12 }}>{t.noLeagues}</div>
                      ) : sortLeagues(userLeagues).map(lg => (
                        <button key={lg.id} onClick={() => loadLeagueByID(lg.id, "my_leagues")} disabled={loading} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: "#f8fafc", borderRadius: 10, border: "1px solid #edf2f7", cursor: loading ? "default" : "pointer", textAlign: "left", fontFamily: "var(--sans)", transition: "background 0.15s, border 0.15s" }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lg.name}</div>
                            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
                              {lg.league_type === "s" ? t.public : t.private}
                              {lg.entry_rank && ` · ${t.position} #${lg.entry_rank.toLocaleString()}`}
                            </div>
                          </div>
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ color: "#94a3b8", marginLeft: 8 }}><path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </button>
                      ))}
                    </div>
                    <button onClick={() => { setUserLeagues(null); setUserInfo(null); }} style={{ width: "100%", padding: "10px", marginTop: 12, background: "transparent", border: "1px solid #e2e8f0", borderRadius: 10, color: "#64748b", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "var(--sans)" }}>{t.changeTeamId}</button>
                  </>
                )}
                {error && <p style={{ color: "#dc2626", fontSize: 12, marginTop: 12 }}>{error}</p>}
              </>
            ) : (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label style={S.label}>{t.leagueId}</label>
                  <input type="text" value={leagueId} onChange={(e) => setLeagueId(e.target.value)} placeholder={t.leagueIdPlaceholderNew} style={S.input} onKeyDown={(e) => e.key === "Enter" && leagueId && loadLeagueByID(leagueId, "typed")}/>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={S.label}>{t.teamIdOptional} <span style={{ fontWeight: 400, opacity: 0.5 }}>{t.optional}</span></label>
                  <input type="text" value={teamId} onChange={(e) => setTeamId(e.target.value)} placeholder="e.g. 1528511" style={S.input}/>
                </div>
                <button onClick={() => loadLeagueByID(leagueId, "typed")} disabled={!leagueId || loading} style={{ ...S.btn, opacity: !leagueId || loading ? 0.5 : 1, cursor: !leagueId || loading ? "default" : "pointer" }}>
                  {loading ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><span style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.6s linear infinite", display: "inline-block" }}/>{t.loading}</span> : t.loadLeague}
                </button>
                {error && <p style={{ color: "#dc2626", fontSize: 12, marginTop: 12 }}>{error}</p>}
                <p style={S.hint}>{t.leagueIdHint}</p>
              </>
            )}
          </div>

          {/* v52: Email capture na home ekranu. GW1 je jedini dan kad saobracaj
              sam dolazi — ako forma nije ziva, talas prolazi bez ijedne adrese. */}
          {EMAIL_CAPTURE_ENABLED && <EmailCapture t={t} lang={lang} source="home" teamId={null} />}

          <div style={S.features}>
            {t.features.map((f, i) => (
              <div key={i} style={S.featureItem}>
                <span style={{ fontSize: 20 }}>{f.icon}</span>
                <div><div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{f.t}</div><div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>{f.d}</div></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // v60: planer. Ucitava se lenjo — dok se tab ne otvori, chunk se ne skine.
  if (screen === "planner") {
    return (
      <div style={S.page}><style>{CSS}</style>
        <PlannerBoundary fallback={
          <div style={{ padding: "34px 18px", textAlign: "center" }}>
            <div style={{ fontSize: 13.5, lineHeight: 1.65, color: "#0f172a", marginBottom: 6 }}>
              {t.plannerCrash}
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.6, color: "#64748b", marginBottom: 18 }}>
              {t.plannerCrashHint}
            </div>
            <button
              onClick={backToLeague}
              style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 9, padding: "10px 18px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "var(--sans)", marginRight: 8 }}
            >{t.plannerCrashBack}</button>
            <button
              onClick={() => {
                try { localStorage.removeItem("fpl_plan"); } catch (e) { /* storage nedostupan */ }
                window.location.reload();
              }}
              style={{ background: "#f8fafc", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: 9, padding: "10px 18px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "var(--sans)" }}
            >{t.plannerCrashReset}</button>
          </div>
        }>
        <Suspense fallback={
          <div style={{ display: "flex", justifyContent: "center", padding: "80px 0" }}>
            <div style={{ width: 24, height: 24, border: "2.5px solid #dbeafe", borderTopColor: "#2563eb", borderRadius: "50%", animation: "spin 0.7s linear infinite" }}/>
          </div>
        }>
          <Planner
            teamId={plannerTeamId}
            bootstrap={bootstrap}
            t={t}
            lang={lang}
            season={SEASON}
            tabs={<LeagueTabs active="planner" t={t} plannerEnabled={PLANNER_ENABLED} onLive={backToLeague} onPlanner={() => {}} onSetPieces={openSetPieces} />}
            onBack={backToLeague}
            inAppBrowser={IN_APP_BROWSER}
            track={trackEvent}
          />
        </Suspense>
        </PlannerBoundary>
      </div>
    );
  }

  // v57: set-piece ekran. Stoji izmedju home-a i lige, ne dira ni jedan ni drugi.
  if (screen === "setpieces") {
    return (
      <div style={S.page}><style>{CSS}</style>
        <SetPieceTakers
          bootstrap={bootstrap}
          loading={setPieceLoading}
          error={setPieceError}
          t={t}
          tabs={<LeagueTabs active="setpieces" t={t} plannerEnabled={PLANNER_ENABLED} onLive={backToLeague} onPlanner={openPlanner} onSetPieces={() => {}} />}
          onBack={backToLeague}
          onRetry={openSetPieces}
        />
      </div>
    );
  }

  if (screen === "league") {
    const currentManagerLeagues = selectedManager ? managerLeagues[selectedManager.id] : null;
    return (
      <div style={S.page}><style>{CSS}</style>
        {/* Pull-to-refresh indicator */}
        {(pullDistance > 0 || isRefreshing) && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "12px 0",
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(10px)",
            zIndex: 150,
            transform: isRefreshing ? "translateY(0)" : `translateY(${Math.max(0, pullDistance - 60)}px)`,
            opacity: isRefreshing ? 1 : Math.min(pullDistance / 60, 1),
            transition: isRefreshing ? "transform 0.2s" : "none",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 20, height: 20,
                border: "2.5px solid #dbeafe",
                borderTopColor: "#2563eb",
                borderRadius: "50%",
                animation: isRefreshing ? "spin 0.7s linear infinite" : "none",
                transform: isRefreshing ? "none" : `rotate(${pullDistance * 3}deg)`,
              }}/>
              <div style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}>
                {isRefreshing ? (lang === "en" ? "Refreshing..." : "Osvežavanje...") : pullDistance > 60 ? (lang === "en" ? "Release to refresh" : "Pusti da osvežiš") : (lang === "en" ? "Pull to refresh" : "Povuci da osvežiš")}
              </div>
            </div>
          </div>
        )}
        {/* v58: zaglavlje + traka tabova u jednom sticky omotacu. */}
        <div style={{ position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ ...S.header, position: "static" }}>
          <button onClick={() => { setScreen("home"); clearInterval(intervalRef.current); localStorage.removeItem("fpl_last_league"); window.scrollTo(0, 0); }} style={S.backBtn}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
            <div
              onClick={() => {
                if (teamId && headerLeagues && headerLeagues.length > 1) {
                  setShowLeagueDropdown(!showLeagueDropdown);
                }
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                cursor: (teamId && headerLeagues && headerLeagues.length > 1) ? "pointer" : "default",
                maxWidth: "100%",
              }}
            >
              <div style={{ ...S.headerTitle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {leagueData?.league?.name || "FPL Center"}
              </div>
              {teamId && headerLeagues && headerLeagues.length > 1 && (
                <svg width="12" height="12" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, transition: "transform 0.2s", transform: showLeagueDropdown ? "rotate(180deg)" : "rotate(0)" }}>
                  <path d="M3 5 L7 9 L11 5" stroke="#64748b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <div style={S.headerMeta}>GW {currentGW?.id || "?"}</div>

            {/* Dropdown menu */}
            {showLeagueDropdown && headerLeagues && (
              <>
                {/* Click-outside catcher */}
                <div
                  onClick={() => setShowLeagueDropdown(false)}
                  style={{ position: "fixed", inset: 0, zIndex: 199 }}
                />
                <div style={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  left: 0,
                  minWidth: 260,
                  maxWidth: 340,
                  background: "#fff",
                  borderRadius: 12,
                  boxShadow: "0 8px 32px rgba(15,23,42,0.15), 0 2px 4px rgba(0,0,0,0.04)",
                  border: "1px solid #e2e8f0",
                  maxHeight: 400,
                  overflowY: "auto",
                  zIndex: 200,
                  padding: 4,
                }}>
                  {sortLeagues(headerLeagues).map(lg => {
                    const isCurrent = String(lg.id) === String(leagueId);
                    return (
                      <button
                        key={lg.id}
                        onClick={() => {
                          setShowLeagueDropdown(false);
                          if (!isCurrent) navigateToLeague(lg.id);
                        }}
                        disabled={isCurrent}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "10px 12px",
                          background: isCurrent ? "#eff6ff" : "transparent",
                          border: "none",
                          borderRadius: 8,
                          cursor: isCurrent ? "default" : "pointer",
                          textAlign: "left",
                          fontFamily: "var(--sans)",
                          marginBottom: 2,
                        }}
                        onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.background = "#f8fafc"; }}
                        onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.background = "transparent"; }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: isCurrent ? "#2563eb" : "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {lg.name}
                          </div>
                          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 1 }}>
                            {lg.league_type === "s" ? t.public : t.private}
                            {lg.entry_rank && ` · #${lg.entry_rank.toLocaleString()}`}
                          </div>
                        </div>
                        {isCurrent && (
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginLeft: 8, flexShrink: 0 }}>
                            <path d="M3 7 L6 10 L11 4" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <div style={{ display: "flex", background: "#fff", borderRadius: 8, padding: 2, border: "1px solid #e2e8f0", marginRight: 8 }}>
            <button onClick={() => setLang("en")} style={{ padding: "4px 8px", border: "none", borderRadius: 6, background: lang === "en" ? "#2563eb" : "transparent", color: lang === "en" ? "#fff" : "#64748b", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "var(--sans)" }}>EN</button>
            <button onClick={() => setLang("sr")} style={{ padding: "4px 8px", border: "none", borderRadius: 6, background: lang === "sr" ? "#2563eb" : "transparent", color: lang === "sr" ? "#fff" : "#64748b", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "var(--sans)" }}>SR</button>
          </div>
          <button onClick={() => {
            const openTally = () => { if (window.Tally) window.Tally.openPopup("QK8dJY", { width: 380, hideTitle: false, alignLeft: false, overlay: true, hideOnSubmit: true, hideOnSubmitDelay: 2500, emoji: false }); };
            if (window.Tally) { openTally(); return; }
            const existing = document.getElementById("tally-embed-script");
            if (existing) { existing.addEventListener("load", openTally, { once: true }); return; }
            const s = document.createElement("script");
            s.id = "tally-embed-script";
            s.src = "https://tally.so/widgets/embed.js";
            s.async = true;
            s.onload = openTally;
            document.body.appendChild(s);
          }} style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }} aria-label="Feedback / Utisci">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
          </button>
        </div>
          <LeagueTabs active="live" t={t} plannerEnabled={PLANNER_ENABLED} onLive={() => {}} onPlanner={openPlanner} onSetPieces={openSetPieces} />
        </div>

        {/* Dashboard hero - shown when user's Team ID matches a manager OR user has loaded info */}
        {(() => {
          if (!teamId || !leagueData?.league) return null;
          
          // Try to find user in top 50 first (full data available)
          const meInTop50 = sortedManagers.find(m => String(m.id) === String(teamId));
          let me = meInTop50;
          
          if (!me) {
            // User is below top 50 - need at minimum userInfo
            if (!userInfo) return null;
            
            // Try to find this league in userLeagues to get specific rank
            const currentLeagueId = leagueData.league.id;
            const myLeagueEntry = userLeagues?.find(lg => lg.id === currentLeagueId);
            
            // CRITICAL: Only show dashboard if user is actually a member of this league
            // (myLeagueEntry exists in their userLeagues). Otherwise they're just viewing
            // someone else's league - don't fake their position.
            if (!myLeagueEntry) return null;
            
            // Build synthetic 'me' object from available data
            me = {
              id: teamId,
              name: userInfo.name,
              teamName: userInfo.teamName,
              liveRank: myLeagueEntry.entry_rank || 999,
              previousRank: myLeagueEntry.entry_last_rank || null,
              rank: myLeagueEntry.entry_rank,
              gwNet: userInfo.eventPoints || 0,
              totalLive: userInfo.totalPoints || 0,
              hasPicks: false,
            };
          }
          
          if (!me) return null;
          
          const leader = sortedManagers[0];
          const isLeader = me.liveRank === 1;
          const next = sortedManagers[me.liveRank] || null;
          
          // Format rank: 999 → "999", 1000-9999 → "1.2K", 10K-999K → "147K", 1M+ → "2.4M"
          const formatRank = (rank) => {
            if (!rank && rank !== 0) return "—";
            if (rank < 1000) return rank.toLocaleString();
            if (rank < 10000) return (rank / 1000).toFixed(1).replace(/\.0$/, "") + "K";
            if (rank < 1000000) return Math.round(rank / 1000) + "K";
            return (rank / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
          };
          const overallRank = userInfo?.overallRank || me.rank || null;
          
          // Get true total league size from userLeagues
          const currentLeagueId = leagueData.league.id;
          const myLeagueEntry = userLeagues?.find(lg => lg.id === currentLeagueId);
          const totalLeagueMembers = myLeagueEntry?.rank_count || sortedManagers.length;
          
          // Detect if this is the OVERALL global league (>1M members)
          const isOverallGlobalLeague = totalLeagueMembers > 1000000;
          
          // Third card logic
          let thirdCard;
          if (isLeader) {
            const aheadVal = next ? me.totalLive - next.totalLive : 0;
            thirdCard = { value: aheadVal, arrow: "▲", color: "#86efac", label: t.aheadOf };
          } else if (me.liveRank <= 50) {
            const behindVal = leader.totalLive - me.totalLive;
            thirdCard = { value: behindVal, arrow: "▼", color: "#fca5a5", label: t.behindLeader };
          } else {
            const rankDiff = me.previousRank ? me.previousRank - me.liveRank : 0;
            if (rankDiff > 0) {
              thirdCard = { value: rankDiff > 1000 ? formatRank(rankDiff) : rankDiff, arrow: "▲", color: "#86efac", label: t.changeInGw };
            } else if (rankDiff < 0) {
              thirdCard = { value: Math.abs(rankDiff) > 1000 ? formatRank(Math.abs(rankDiff)) : Math.abs(rankDiff), arrow: "▼", color: "#fca5a5", label: t.changeInGw };
            } else {
              thirdCard = { value: "0", arrow: "=", color: "#cbd5e1", label: t.changeInGw };
            }
          }
          
          // Fourth card - GLOBALNO percentile only for overall global league
          let fourthCard;
          if (isOverallGlobalLeague && overallRank && overallRank > 0) {
            const percentile = (overallRank / 11000000) * 100;
            let bucket;
            if (percentile <= 1) bucket = 1;
            else if (percentile <= 5) bucket = 5;
            else if (percentile <= 10) bucket = 10;
            else if (percentile <= 20) bucket = 20;
            else if (percentile <= 30) bucket = 30;
            else if (percentile <= 40) bucket = 40;
            else if (percentile <= 50) bucket = 50;
            else if (percentile <= 60) bucket = 60;
            else if (percentile <= 70) bucket = 70;
            else if (percentile <= 80) bucket = 80;
            else if (percentile <= 90) bucket = 90;
            else bucket = 100;
            fourthCard = { value: `TOP ${bucket}%`, color: bucket <= 10 ? "#fcd34d" : "#fff", label: t.global };
          } else {
            fourthCard = { value: formatRank(overallRank), color: "#fff", label: t.overallRank };
          }
          
          return (
            <div
              onClick={(e) => {
                // Don't trigger if click is on Share button
                if (e.target.closest('[data-share-btn]')) return;
                // Only open modal for top 50 users (have full picks/history loaded)
                // Below top 50 would require expensive on-demand fetches that cause modal jumping
                if (me.hasPicks) setSelectedManager(me);
              }}
              style={{
                margin: "12px 20px 0",
                padding: "14px 16px",
                background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                borderRadius: 14,
                color: "#fff",
                position: "relative",
                overflow: "hidden",
                boxShadow: "0 4px 14px rgba(37,99,235,0.25)",
                cursor: me.hasPicks ? "pointer" : "default",
              }}>
              <div style={{
                position: "absolute",
                top: -40,
                right: -30,
                width: 140,
                height: 140,
                background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)",
                borderRadius: "50%",
                pointerEvents: "none",
              }}/>
              <div style={{ position: "relative", zIndex: 1 }}>
                {/* Top row: team name + medal/share (always show share, medal if top 3) */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{me.teamName}</div>
                    <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>{me.name}</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, flexShrink: 0, marginLeft: 8 }}>
                    {me.liveRank <= 3 && (
                      <Medal position={me.liveRank} size={42}/>
                    )}
                    <button
                      data-share-btn="true"
                      onClick={(e) => {
                        e.stopPropagation();
                        trackEvent("Share Click", { source: "table_row", position: "league_table" });
                        setShowShareModal(true);
                      }}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 3,
                        background: "transparent",
                        border: "none",
                        padding: "4px 6px",
                        cursor: "pointer",
                        borderRadius: 6,
                      }}
                      aria-label="Share dashboard"
                    >
                      <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
                        <circle cx="3" cy="8" r="2" stroke="#fff" strokeWidth="1.6"/>
                        <circle cx="13" cy="3" r="2" stroke="#fff" strokeWidth="1.6"/>
                        <circle cx="13" cy="13" r="2" stroke="#fff" strokeWidth="1.6"/>
                        <line x1="5" y1="7" x2="11" y2="4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"/>
                        <line x1="5" y1="9" x2="11" y2="12" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"/>
                      </svg>
                      <div style={{ fontSize: 11, color: "#fff", letterSpacing: "0.5px", textTransform: "uppercase", fontWeight: 500 }}>Share</div>
                    </button>
                  </div>
                </div>
                
                {/* Position number */}
                <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                  <span style={{ fontSize: 38, fontWeight: 800, fontFamily: "var(--sans)", lineHeight: 1, letterSpacing: "-1px" }}>#{formatRank(me.liveRank)}</span>
                  <span style={{ fontSize: 16, opacity: 0.55, fontWeight: 500 }}>/{formatRank(totalLeagueMembers)}</span>
                </div>
                
                {/* 2x2 stats grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px 16px", marginTop: 12, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.18)" }}>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 700, fontFamily: "var(--sans)", lineHeight: 1, letterSpacing: "-0.3px" }}>{me.gwNet}</div>
                    <div style={{ fontSize: 9, opacity: 0.7, textTransform: "uppercase", fontWeight: 600, marginTop: 3, letterSpacing: "0.5px" }}>{t.gwNet}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 700, fontFamily: "var(--sans)", lineHeight: 1, letterSpacing: "-0.3px" }}>{me.totalLive.toLocaleString()}</div>
                    <div style={{ fontSize: 9, opacity: 0.7, textTransform: "uppercase", fontWeight: 600, marginTop: 3, letterSpacing: "0.5px" }}>{t.total}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 700, fontFamily: "var(--sans)", lineHeight: 1, color: thirdCard.color, display: "flex", alignItems: "center", gap: 2, letterSpacing: "-0.3px" }}>
                      <span style={{ display: "inline-block", transform: "translateY(-1px)", fontSize: 11 }}>{thirdCard.arrow}</span>
                      <span>{thirdCard.value}</span>
                    </div>
                    <div style={{ fontSize: 9, opacity: 0.7, textTransform: "uppercase", fontWeight: 600, marginTop: 3, letterSpacing: "0.5px" }}>{thirdCard.label}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 700, fontFamily: "var(--sans)", lineHeight: 1, color: fourthCard.color, letterSpacing: "-0.3px" }}>{fourthCard.value}</div>
                    <div style={{ fontSize: 9, opacity: 0.7, textTransform: "uppercase", fontWeight: 600, marginTop: 3, letterSpacing: "0.5px" }}>{fourthCard.label}</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* NudgeBanner - real-time hvalisanje banner za Lider/Winner/Captain
            Hijerarhija: Lider > Winner > Captain (samo jedan se prikazuje)
            Pouzdanost: koristi SAMO trenutno ucitanu ligu (real-time podaci) */}
        {(() => {
          // Bezbednosna provera: bez teamId, lige, bootstrap-a, ili currentGW-a, banner se ne pokazuje
          if (!teamId || !leagueData?.league || !bootstrap || !currentGW) return null;

          // Per-GW dismiss provera
          const dismissKey = `nudge-dismissed-gw${currentGW.id}`;
          let isDismissed = false;
          try {
            isDismissed = localStorage.getItem(dismissKey) === "true";
          } catch (e) { /* localStorage nedostupan, nastavi */ }
          if (isDismissed) return null;

          // Trazim korisnika u top 50 (potpuni podaci) ili gradim sintetic me
          const meInTop50 = sortedManagers.find(m => String(m.id) === String(teamId));
          if (!meInTop50) return null; // Banner samo za korisnike u top 50 (gde imamo real-time gwNet/liveRank/picks)

          const me = meInTop50;
          const currentLeagueId = leagueData.league.id;
          // rank_count za "X mgr" tekst, fallback na sortedManagers.length
          const totalCount = userLeagues?.find(lg => lg.id === currentLeagueId)?.rank_count || sortedManagers.length;
          const leagueName = leagueData.league.name || "";

          // GW finalized check - data_checked je kljucan flag (ne samo finished)
          const isGwFinalized = currentGW.data_checked === true;

          // === TRIGGER 1: LIDER (real-time) ===
          const isLeaderTrigger = me.liveRank === 1;

          // === TRIGGER 2: GW WINNER (samo ako je GW finalized) ===
          const allGwNets = sortedManagers.map(m => m.gwNet || 0);
          const maxGwNet = allGwNets.length > 0 ? Math.max(...allGwNets) : 0;
          const isWinnerTrigger = isGwFinalized && me.gwNet === maxGwNet && me.gwNet > 0;

          // === TRIGGER 3: CAPTAIN (real-time, threshold 12 NET) ===
          let isCaptainTrigger = false;
          let captainName = "";
          let captainNetPts = 0;
          let isTC = false;
          if (me.hasPicks && me.picks && enrichedLive && bootstrap) {
            const captainPick = me.picks.find(p => p.is_captain);
            if (captainPick) {
              const capLive = enrichedLive[captainPick.element];
              const capPlayer = bootstrap.elements.find(p => p.id === captainPick.element);
              const capPoints = capLive?.points || 0;
              const multiplier = captainPick.multiplier || 0;
              captainNetPts = capPoints * multiplier;
              isCaptainTrigger = captainNetPts >= 12;
              captainName = capPlayer?.web_name || "?";
              isTC = multiplier === 3;
            }
          }

          // === HIJERARHIJA - samo jedan banner ===
          let bannerType = null;
          let bannerData = null;
          let cardScrollTarget = null;
          if (isLeaderTrigger) {
            bannerType = "leader";
            bannerData = { leagueName, count: totalCount };
            cardScrollTarget = "leader";
          } else if (isWinnerTrigger) {
            bannerType = "winner";
            bannerData = { leagueName, count: totalCount, pts: me.gwNet };
            cardScrollTarget = "gwwinner";
          } else if (isCaptainTrigger) {
            bannerType = "captain";
            bannerData = { captainName, pts: captainNetPts, isTC };
            cardScrollTarget = "captain";
          } else {
            // v77: default stanje - nikad prazna nedelja bez poziva na akciju.
            // Kad nema lidera/winnera/kapitena, i dalje nudimo share slot,
            // isti markup/pozicija, samo neutralan sadrzaj (share-vidljivost-v4).
            bannerType = "default";
            bannerData = { gwId: currentGW.id };
            cardScrollTarget = null;
          }

          const handleShare = () => {
            try { trackEvent("Banner Click", { type: bannerType, league: leagueName }); } catch (e) { /* ignore */ }
            // v52: vrh share funnela. Do v51 "Share Click" NIJE postojao u kodu
            // (Plausible je pokazivao 1 unique — ostatak iz stare verzije),
            // pa se nije moglo videti da li ljudi ne otvaraju modal
            // ili ga otvaraju a generisanje slike puca.
            trackEvent("Share Click", { source: "banner", position: bannerType });
            setShareModalScrollTo(cardScrollTarget);
            setShowShareModal(true);
          };

          const handleDismiss = () => {
            try { trackEvent("Banner Dismissed", { type: bannerType, league: leagueName }); } catch (e) { /* ignore */ }
            try { localStorage.setItem(dismissKey, "true"); } catch (e) { /* ignore */ }
            // Force re-render - localStorage promene ne triggeruju re-render same po sebi
            setNudgeDismissTick(prev => prev + 1);
          };

          // Plausible "Banner Shown" - okida se prvi put po sesiji per banner tip per GW
          // sessionStorage sprecava duplo trackovanje na iste re-rendere unutar iste sesije
          try {
            const shownKey = `nudge-shown-${bannerType}-gw${currentGW.id}`;
            if (typeof sessionStorage !== "undefined" && !sessionStorage.getItem(shownKey)) {
              trackEvent("Banner Shown", { type: bannerType, league: leagueName });
              sessionStorage.setItem(shownKey, "true");
            }
          } catch (e) { /* ignore */ }

          return (
            <NudgeBanner
              bannerType={bannerType}
              data={bannerData}
              t={t}
              onShare={handleShare}
              onDismiss={handleDismiss}
            />
          );
        })()}

        <div style={S.fixStrip}>
          {fixtures.map((f) => (
            <div key={f.id} style={{ ...S.fixCard, cursor: "pointer" }} onClick={() => setSelectedFixture(f)}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#334155" }}>{f.home}</span>
                <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "var(--mono)", color: "#2563eb", minWidth: 34, textAlign: "center" }}>{f.status === "upcoming" ? "vs" : `${f.homeScore} – ${f.awayScore}`}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#334155" }}>{f.away}</span>
              </div>
              <StatusPill status={f.status} minute={f.minute}/>
            </div>
          ))}
        </div>

        <div style={{ ...S.tableContainer, position: "relative" }}>
          <table style={S.table}>
            <thead><tr>
              <th style={{ ...S.th, textAlign: "center", width: 40 }}>#</th>
              <th style={{ ...S.th, textAlign: "left" }}>{t.headerMgr}</th>
              <th className="hide-mobile" style={{ ...S.th, textAlign: "center" }}>{t.headerCap}</th>
              <th className="hide-mobile" style={{ ...S.th, textAlign: "center" }}>{t.headerGW}</th>
              <th className="hide-mobile" style={{ ...S.th, textAlign: "center" }}>{t.headerHit}</th>
              <th style={{ ...S.th, textAlign: "center" }}>{t.headerNet}</th>
              <th style={{ ...S.th, textAlign: "center" }}>{t.headerTotal}</th>
              <th style={{ ...S.th, textAlign: "center", width: 44 }}></th>
            </tr></thead>
            <tbody>
              {sortedManagers.map((m) => {
                const isMe = teamId && String(m.id) === String(teamId);
                // v56: VS dugme se nudi samo kad je precica ODMAH izvodljiva —
                // ja sam u tabeli sa picks-ima i rival takodje. Ako nisam u top 50,
                // ostaje stari put kroz manager modal, koji ume da dovuce moje picks.
                const canDuel = !isMe && !!m.hasPicks && !!teamId &&
                  sortedManagers.some(x => String(x.id) === String(teamId) && x.hasPicks);
                const shimmerNum = <span className="shimmer" style={{ width: 30, height: 12, display: "inline-block" }}>&nbsp;</span>;
                const shimmerSmall = <span className="shimmer" style={{ width: 42, height: 10, display: "inline-block" }}>&nbsp;</span>;
                const rankDelta = (!isDataLoading && m.previousRank && m.hasPicks) ? m.previousRank - m.liveRank : 0;
                return (
                  <tr key={m.id} onClick={() => { if (!m.hasPicks) return; trackEvent("Manager Modal Opened"); setSelectedManager(m); }} className="trow mgr-row" style={{ cursor: m.hasPicks ? "pointer" : "default", background: isMe ? "#eff6ff" : "#fff" }}>
                    <td style={{ ...S.td, textAlign: "center", fontWeight: 700, color: "#94a3b8", fontSize: 11, fontFamily: "var(--mono)" }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0, lineHeight: 1.15 }}>
                        <span>{m.liveRank}</span>
                        {rankDelta !== 0 && (
                          <span style={{ fontSize: 9, fontWeight: 800, color: rankDelta > 0 ? "#16a34a" : "#dc2626", fontFamily: "var(--mono)", letterSpacing: "-0.3px" }}>
                            {rankDelta > 0 ? "▲" : "▼"}{Math.abs(rankDelta)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ ...S.td, textAlign: "left" }}>
                      <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 13 }}>{m.name}</div>
                      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
                        <span className="show-mobile-only" style={{ color: "#2563eb", fontWeight: 600 }}>
                          {m.hasPicks ? m.captainName : shimmerSmall}
                        </span>
                        <span className="hide-mobile">{m.teamName}</span>
                        {m.activeChip && <ChipBadge chip={m.activeChip}/>}
                      </div>
                    </td>
                    <td className="hide-mobile" style={{ ...S.td, textAlign: "center" }}>
                      {m.hasPicks ? <span style={S.captainPill}>{m.captainName}</span> : shimmerSmall}
                    </td>
                    <td className="hide-mobile" style={{ ...S.td, textAlign: "center", fontFamily: "var(--mono)", fontSize: 13 }}>
                      {m.hasPicks ? m.gwPoints : shimmerNum}
                    </td>
                    <td className="hide-mobile" style={{ ...S.td, textAlign: "center", fontFamily: "var(--mono)", fontSize: 13, color: m.hitCost > 0 ? "#dc2626" : "#cbd5e1" }}>
                      {m.hasPicks ? (m.hitCost > 0 ? `−${m.hitCost}` : "—") : shimmerNum}
                    </td>
                    <td key={`net-${m.gwNet}`} style={{ ...S.td, textAlign: "center", fontFamily: "var(--mono)", fontSize: 14, fontWeight: 700, color: "#2563eb" }}>
                      {m.hasPicks ? m.gwNet : shimmerNum}
                    </td>
                    <td style={{ ...S.td, textAlign: "center", fontFamily: "var(--mono)", fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                      {m.hasPicks ? m.totalLive : (m.totalLive || shimmerNum)}
                    </td>
                    <td style={{ ...S.td, textAlign: "center", width: 44, padding: "8px 4px" }}>
                      {canDuel ? (
                        <button
                          onClick={(e) => {
                            // stopPropagation: bez ovoga bi se ispod otvorio i manager modal
                            e.stopPropagation();
                            const myManager = sortedManagers.find(x => String(x.id) === String(teamId));
                            if (!myManager?.hasPicks) return;
                            trackEvent("Compare Started", { source: "table_vs" });
                            setCompareRival(m);
                            setSelectedManager(myManager);
                            setShowCompareModal(true);
                          }}
                          aria-label={t.compareVs || "Duel"}
                          style={{ width: 30, height: 24, borderRadius: 7, background: "#1e3a8a", color: "#fff", fontSize: 9, fontWeight: 800, letterSpacing: "0.3px", border: "none", padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--sans)" }}
                        >VS</button>
                      ) : (
                        <span style={{ color: m.hasPicks ? "#2563eb" : "transparent", fontSize: 20, fontWeight: 600 }}>{m.hasPicks ? "\u203A" : ""}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ padding: 20 }}>
          <h3 style={S.sectionTitle}>{t.provisionalBonus}</h3>
          <div style={S.bonusGrid} className="bonus-grid-mobile">
            {fixtures.filter((f) => f.status !== "upcoming").map((f) => {
              const matchPlayerIds = bootstrap.elements.filter((p) => p.team === f.team_h || p.team === f.team_a).map((p) => p.id);
              const matchBpsList = matchPlayerIds.map(id => ({ id, bps: liveData[id]?.bps || 0 }));
              const matchBonusMap = calculateProvisionalBonus(matchBpsList);
              const sorted = matchPlayerIds
                .map((id) => ({ id, name: bootstrap.elements.find((p) => p.id === id)?.web_name || "?", bps: liveData[id]?.bps || 0, bonus: matchBonusMap[id] || 0 }))
                .filter(p => p.bps > 0)
                .sort((a, b) => b.bps - a.bps)
                .slice(0, 6);
              return (
                <div key={f.id} style={S.bonusCard}>
                  <div style={S.bonusHeader}>
                    <span>{f.home} {f.homeScore}–{f.awayScore} {f.away}</span>
                    <StatusPill status={f.status} minute={f.minute}/>
                  </div>
                  {sorted.map((p) => (
                    <div key={p.id} style={{ display: "flex", alignItems: "center", padding: "5px 0" }}>
                      <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: "#334155" }}>{p.name}</span>
                      <span style={{ fontSize: 12, fontFamily: "var(--mono)", fontWeight: 600, color: "#334155", minWidth: 28, textAlign: "right" }}>{p.bps}</span>
                      {p.bonus > 0 && (
                        <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: "#15803d", background: "#dcfce7", padding: "2px 7px", borderRadius: 6, fontFamily: "var(--mono)", minWidth: 28, textAlign: "center" }}>+{p.bonus}</span>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {selectedFixture && (
          <div style={S.overlay} onClick={() => setSelectedFixture(null)}>
            <div style={{ ...S.modal, maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, color: "#0f172a", lineHeight: 1.3 }}>
                    {selectedFixture.homeFull} {selectedFixture.homeScore} – {selectedFixture.awayScore} {selectedFixture.awayFull}
                  </h2>
                  <div style={{ marginTop: 4 }}><StatusPill status={selectedFixture.status} minute={selectedFixture.minute} showMinute={true}/></div>
                </div>
                <button onClick={() => setSelectedFixture(null)} style={S.closeBtn}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11 3L3 11M3 3l8 8" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round"/></svg>
                </button>
              </div>

              <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 10 }}>{t.goalsAssists}</div>

              {(() => {
                const goalsStat = selectedFixture.stats.find(s => s.identifier === "goals_scored");
                const assistsStat = selectedFixture.stats.find(s => s.identifier === "assists");
                const goalsHome = goalsStat?.h || [];
                const goalsAway = goalsStat?.a || [];
                const assistsHome = assistsStat?.h || [];
                const assistsAway = assistsStat?.a || [];
                const getName = (pid) => bootstrap?.elements.find(p => p.id === pid)?.web_name || "?";
                const hasEvents = goalsHome.length || goalsAway.length || assistsHome.length || assistsAway.length;

                if (!hasEvents) {
                  return <div style={{ textAlign: "center", padding: 20, color: "#94a3b8", fontSize: 12 }}>{t.noEvents}</div>;
                }
                return (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#2563eb", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8 }}>{selectedFixture.home}</div>
                      {goalsHome.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 10, fontWeight: 600, color: "#64748b", marginBottom: 4 }}>⚽ {t.goals}</div>
                          {goalsHome.map((g, i) => (
                            <div key={i} style={{ fontSize: 12, color: "#0f172a", padding: "3px 0", display: "flex", justifyContent: "space-between" }}>
                              <span>{getName(g.element)}</span>
                              {g.value > 1 && <span style={{ color: "#2563eb", fontWeight: 700 }}>×{g.value}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      {assistsHome.length > 0 && (
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 600, color: "#64748b", marginBottom: 4 }}>🎯 {t.assists}</div>
                          {assistsHome.map((a, i) => (
                            <div key={i} style={{ fontSize: 12, color: "#334155", padding: "3px 0", display: "flex", justifyContent: "space-between" }}>
                              <span>{getName(a.element)}</span>
                              {a.value > 1 && <span style={{ color: "#2563eb", fontWeight: 700 }}>×{a.value}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#2563eb", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8 }}>{selectedFixture.away}</div>
                      {goalsAway.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 10, fontWeight: 600, color: "#64748b", marginBottom: 4 }}>⚽ {t.goals}</div>
                          {goalsAway.map((g, i) => (
                            <div key={i} style={{ fontSize: 12, color: "#0f172a", padding: "3px 0", display: "flex", justifyContent: "space-between" }}>
                              <span>{getName(g.element)}</span>
                              {g.value > 1 && <span style={{ color: "#2563eb", fontWeight: 700 }}>×{g.value}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      {assistsAway.length > 0 && (
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 600, color: "#64748b", marginBottom: 4 }}>🎯 {t.assists}</div>
                          {assistsAway.map((a, i) => (
                            <div key={i} style={{ fontSize: 12, color: "#334155", padding: "3px 0", display: "flex", justifyContent: "space-between" }}>
                              <span>{getName(a.element)}</span>
                              {a.value > 1 && <span style={{ color: "#2563eb", fontWeight: 700 }}>×{a.value}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {selectedPlayer && (
          <div style={S.overlay} onClick={() => setSelectedPlayer(null)}>
            <div style={{ ...S.modal, maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
              {(() => {
                const player = bootstrap?.elements.find(p => p.id === selectedPlayer.id);
                const live = liveData?.[selectedPlayer.id];
                const playerTeam = bootstrap?.teams.find(tt => tt.id === player?.team);
                const fx = fixtures.find(f => f.team_h === player?.team || f.team_a === player?.team);
                const oppTeamId = fx?.team_h === player?.team ? fx?.team_a : fx?.team_h;
                const oppTeam = bootstrap?.teams.find(tt => tt.id === oppTeamId);
                const isHome = fx?.team_h === player?.team;
                const provBonus = bonusMap[selectedPlayer.id] || 0;

                if (!player || !live) return null;

                const posMap = { 1: "GK", 2: "DEF", 3: "MID", 4: "FWD" };

                // DGW detection — fixtures is already filtered to current GW
                // Count fixtures for this player's team that have started or finished
                const playerFixtures = fixtures.filter(f =>
                  (f.team_h === player.team || f.team_a === player.team) &&
                  (f.status === "finished" || f.status === "inPlay")
                );
                const matchCount = Math.max(playerFixtures.length, 1);
                const isDGW = matchCount >= 2;

                const items = [];

                // Minutes: single GW = 60+ min → 2 pts; DGW = per match
                if (live.minutes > 0) {
                  let minPts;
                  if (isDGW) {
                    // In DGW, FPL awards 2pts per match where player played 60+ mins
                    // We approximate: if avg minutes per match >= 60 → full 2*matchCount
                    // Otherwise if any minutes played → matchCount * 1 (appearance bonus)
                    const avgMins = live.minutes / matchCount;
                    if (avgMins >= 60) minPts = matchCount * 2;
                    else if (live.minutes >= 60) minPts = 2 + (matchCount - 1); // partial: one full match + appearances
                    else minPts = matchCount; // just appearances
                  } else {
                    minPts = live.minutes >= 60 ? 2 : 1;
                  }
                  items.push({ label: t.minutes, value: `${live.minutes}'`, points: minPts });
                }

                if (live.goals > 0) {
                  const pts = player.element_type === 1 || player.element_type === 2 ? 6 : player.element_type === 3 ? 5 : 4;
                  items.push({ label: t.goals, value: live.goals, points: live.goals * pts });
                }
                if (live.assists > 0) items.push({ label: t.assists, value: live.assists, points: live.assists * 3 });

                // Clean sheet: in DGW, can get multiple clean sheets (live.cleanSheet is count in API)
                // But live.cleanSheet is boolean in our mapping, so we approximate
                if (live.cleanSheet && (player.element_type === 1 || player.element_type === 2)) {
                  const csPts = isDGW ? 4 * (live.cleanSheet === true ? 1 : live.cleanSheet) : 4;
                  items.push({ label: t.cleanSheet, value: "✓", points: csPts });
                } else if (live.cleanSheet && player.element_type === 3) {
                  const csPts = isDGW ? 1 * (live.cleanSheet === true ? 1 : live.cleanSheet) : 1;
                  items.push({ label: t.cleanSheet, value: "✓", points: csPts });
                }

                if (live.saves >= 3 && player.element_type === 1) items.push({ label: t.saves, value: live.saves, points: Math.floor(live.saves / 3) });
                if (live.penaltiesSaved > 0) items.push({ label: t.penaltiesSaved, value: live.penaltiesSaved, points: live.penaltiesSaved * 5 });
                if (live.penaltiesMissed > 0) items.push({ label: t.penaltiesMissed, value: live.penaltiesMissed, points: -live.penaltiesMissed * 2 });
                if (live.yellows > 0) items.push({ label: t.yellowCards, value: live.yellows, points: -live.yellows });
                if (live.reds > 0) items.push({ label: t.redCards, value: live.reds, points: -live.reds * 3 });
                if (live.ownGoals > 0) items.push({ label: t.ownGoals, value: live.ownGoals, points: -live.ownGoals * 2 });
                if (live.goalsConceded >= 2 && (player.element_type === 1 || player.element_type === 2)) {
                  items.push({ label: t.goalsConceded, value: live.goalsConceded, points: -Math.floor(live.goalsConceded / 2) });
                }
                if (live.defensiveContribution > 0) {
                  const isMid = player.element_type === 3;
                  const isDefOrGk = player.element_type === 1 || player.element_type === 2;
                  const threshold = isMid ? 12 : 10;
                  // In DGW, threshold is per match (still counting cumulative actions)
                  const effectiveThreshold = threshold * matchCount;
                  const qualifies = (isMid || isDefOrGk) && live.defensiveContribution >= effectiveThreshold;
                  const defPts = qualifies ? (isDGW ? 2 * matchCount : 2) : 0;
                  items.push({ label: t.defensiveContributions, value: live.defensiveContribution, points: defPts });
                }

                const totalPoints = live.points + (live.bonus === 0 ? provBonus : 0);
                // U DGW, total je samo live.points (FPL ga vec sumira tacno).
                // Ne dodajemo provBonus jer je bagovan u DGW (per-fixture overwrite).
                const dgwTotalPoints = live.points;

                return (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: "#0f172a" }}>
                          {player.first_name} {player.second_name}
                        </h2>
                        <p style={{ fontSize: 12, color: "#94a3b8", margin: "3px 0 0" }}>
                          <span style={{ fontWeight: 700, color: "#2563eb" }}>{posMap[player.element_type]}</span>
                          {" · "}{playerTeam?.name}
                          {fx && oppTeam && <>{" · "}{isHome ? "v" : "@"} {oppTeam.short_name}</>}
                        </p>
                      </div>
                      <button onClick={() => setSelectedPlayer(null)} style={S.closeBtn}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11 3L3 11M3 3l8 8" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round"/></svg>
                      </button>
                    </div>

                    {isDGW ? (
                      <>
                        <div style={{ background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>
                            {t.dgwBannerTitle}
                          </div>
                          <div style={{ fontSize: 13, color: "#92400e", lineHeight: 1.45 }}>
                            {t.dgwBanner}
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", padding: "12px 14px", background: "#2563eb", borderRadius: 10 }}>
                          <span style={{ flex: 1, color: "#fff", fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                            {t.totalPoints}
                          </span>
                          <span style={{ fontFamily: "var(--mono)", fontWeight: 800, fontSize: 20, color: "#fff" }}>{dgwTotalPoints}</span>
                        </div>
                      </>
                    ) : live.minutes === 0 && fx?.status !== "finished" ? (
                      <div style={{ textAlign: "center", padding: 30, color: "#94a3b8", fontSize: 13 }}>{t.notPlayed}</div>
                    ) : (
                      <>
                        <div style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 12 }}>
                          {items.map((it, i) => (
                            <div key={i} style={{ display: "flex", alignItems: "center", padding: "8px 12px", background: "#f8fafc", borderRadius: 8, fontSize: 13 }}>
                              <span style={{ flex: 1, color: "#334155", fontWeight: 500 }}>{it.label}</span>
                              <span style={{ fontFamily: "var(--mono)", fontSize: 12, color: "#94a3b8", marginRight: 14, minWidth: 24, textAlign: "right" }}>{it.value}</span>
                              <span style={{ fontFamily: "var(--mono)", fontWeight: 700, minWidth: 36, textAlign: "right", color: it.points >= 0 ? "#2563eb" : "#dc2626" }}>
                                {it.points > 0 ? "+" : ""}{it.points}
                              </span>
                            </div>
                          ))}
                          {(live.bonus > 0 || provBonus > 0) && (
                            <div style={{ display: "flex", alignItems: "center", padding: "8px 12px", background: "#dcfce7", borderRadius: 8, fontSize: 13 }}>
                              <span style={{ flex: 1, color: "#15803d", fontWeight: 600 }}>
                                {t.bonusPoints}{live.bonus === 0 && provBonus > 0 ? " (prov.)" : ""}
                              </span>
                              <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: "#15803d", marginRight: 14 }}>{live.bps} BPS</span>
                              <span style={{ fontFamily: "var(--mono)", fontWeight: 700, color: "#15803d", minWidth: 36, textAlign: "right" }}>
                                +{live.bonus || provBonus}
                              </span>
                            </div>
                          )}
                        </div>

                        <div style={{ display: "flex", alignItems: "center", padding: "12px 14px", background: "#2563eb", borderRadius: 10, marginTop: 8 }}>
                          <span style={{ flex: 1, color: "#fff", fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                            {t.totalPoints}
                          </span>
                          <span style={{ fontFamily: "var(--mono)", fontWeight: 800, fontSize: 20, color: "#fff" }}>{totalPoints}</span>
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {selectedManager && !selectedPlayer && (() => {
          // If selectedManager doesn't have picks (synthetic me from below top 50),
          // try to use freshly-fetched picks from managerPicks state
          const pickedData = managerPicks[selectedManager.id];
          const hasFetchedPicks = !!pickedData && !!enrichedLive && !!bootstrap;
          
          // Build enriched selectedManager with picks if available
          let enrichedSelected = selectedManager;
          if (!selectedManager.picks && hasFetchedPicks) {
            const processedPicks = processPicksSaAutosubovima(pickedData, enrichedLive, bootstrap, fixtures);
            // Find captain
            const capPick = pickedData.picks.find(p => p.is_captain);
            const capPlayer = capPick ? bootstrap.elements.find(e => e.id === capPick.element) : null;
            enrichedSelected = {
              ...selectedManager,
              picks: processedPicks,
              activeChip: pickedData.active_chip,
              captainName: capPlayer?.web_name || "?",
              captainId: capPick?.element || null,
            };
          }
          
          return (
          <div style={S.overlay} onClick={() => setSelectedManager(null)}>
            <div style={S.modal} onClick={(e) => e.stopPropagation()}>
              {/* NOVI HEADER: ime + #pozicija sivkasto, ispod tim ime + chip badge */}
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{enrichedSelected.name}</h2>
                    <span style={{ fontSize: 20, fontWeight: 600, color: "#94a3b8", flexShrink: 0 }}>#{enrichedSelected.liveRank}</span>
                    {enrichedSelected.liveRank <= 3 && <Medal position={enrichedSelected.liveRank} size={28}/>}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
                    <span style={{ fontSize: 13, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{enrichedSelected.teamName}</span>
                    {enrichedSelected.activeChip && <ChipBadge chip={enrichedSelected.activeChip}/>}
                  </div>
                </div>
                <button onClick={() => setSelectedManager(null)} style={{ ...S.closeBtn, flexShrink: 0, marginLeft: 8 }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11 3L3 11M3 3l8 8" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round"/></svg>
                </button>
              </div>

              {/* DUGMAD: Uporedi + Podeli, jednake sirine */}
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                {String(selectedManager.id) === String(teamId) ? (
                  <>
                    <button onClick={() => setShowCompareModal(true)} style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 12px", background: "#f1f5f9", border: "none", borderRadius: 10, color: "#334155", fontSize: 13, fontWeight: 600, fontFamily: "var(--sans)", cursor: "pointer" }}>
                      <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                        <path d="M4 3v8M10 3v8M2 6l2-2 2 2M8 8l2 2 2-2" stroke="#475569" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {t.compare || "Compare"}
                    </button>
                    <button onClick={() => { trackEvent("Share Click", { source: "manager_modal", position: "modal_button" }); setShowShareModal(true); }} style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 12px", background: "#2563eb", border: "none", borderRadius: 10, color: "#fff", fontSize: 13, fontWeight: 600, fontFamily: "var(--sans)", cursor: "pointer", boxShadow: "0 2px 8px rgba(37,99,235,0.25)" }}>
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <circle cx="3" cy="8" r="2" stroke="#fff" strokeWidth="1.6"/>
                        <circle cx="13" cy="3" r="2" stroke="#fff" strokeWidth="1.6"/>
                        <circle cx="13" cy="13" r="2" stroke="#fff" strokeWidth="1.6"/>
                        <line x1="5" y1="7" x2="11" y2="4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"/>
                        <line x1="5" y1="9" x2="11" y2="12" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"/>
                      </svg>
                      {t.share}
                    </button>
                  </>
                ) : (
                  teamId && (
                    <button onClick={async () => {
                      if (compareLoading) return;
                      let myManager = sortedManagers.find(m => String(m.id) === String(teamId));
                      
                      if (myManager?.hasPicks) {
                        trackEvent("Compare Started", { source: "compare_with_me", userInTop50: true });
                        setCompareRival(selectedManager);
                        setSelectedManager(myManager);
                        setShowCompareModal(true);
                        return;
                      }
                      
                      if (!currentGW || !bootstrap || !enrichedLive) return;
                      
                      setCompareLoading(true);
                      try {
                        const [picks, history] = await Promise.all([
                          apiFetch(`/entry/${teamId}/event/${currentGW.id}/picks/`).catch(() => null),
                          apiFetch(`/entry/${teamId}/history/`).catch(() => null),
                        ]);
                        if (!picks) { setCompareLoading(false); return; }
                        
                        const isBboost = picks.active_chip === "bboost";
                        const processedPicks = processPicksSaAutosubovima(picks, enrichedLive, bootstrap, fixtures);
                        
                        let gwPoints = 0;
                        const toCount = isBboost ? processedPicks : processedPicks.filter(p => p.multiplier > 0);
                        toCount.forEach(pick => {
                          const live = enrichedLive[pick.element];
                          if (live) gwPoints += live.points * pick.multiplier;
                        });
                        const hitCost = picks.entry_history?.event_transfers_cost || 0;
                        
                        if (history) {
                          setManagerHistories(prev => ({ ...prev, [teamId]: history }));
                        }
                        setManagerPicks(prev => ({ ...prev, [teamId]: picks }));
                        
                        myManager = {
                          id: teamId,
                          name: userInfo?.name || "?",
                          teamName: userInfo?.teamName || "?",
                          liveRank: 999,
                          gwNet: gwPoints - hitCost,
                          totalLive: userInfo?.totalPoints || 0,
                          picks: processedPicks,
                          activeChip: picks.active_chip,
                          hasPicks: true,
                        };
                        
                        trackEvent("Compare Started", { source: "compare_with_me", userInTop50: false });
                        setCompareRival(selectedManager);
                        setSelectedManager(myManager);
                        setShowCompareModal(true);
                      } catch (e) {}
                      setCompareLoading(false);
                    }} disabled={compareLoading} style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 12px", background: "#2563eb", border: "none", borderRadius: 10, color: "#fff", fontSize: 13, fontWeight: 600, fontFamily: "var(--sans)", cursor: compareLoading ? "default" : "pointer", boxShadow: "0 2px 8px rgba(37,99,235,0.25)", opacity: compareLoading ? 0.7 : 1 }}>
                      {compareLoading ? (
                        <>
                          <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.6s linear infinite", display: "inline-block" }}/>
                          {t.loading}
                        </>
                      ) : (
                        <>
                          <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                            <path d="M4 3v8M10 3v8M2 6l2-2 2 2M8 8l2 2 2-2" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          {t.compareWithMe || "Compare with me"}
                        </>
                      )}
                    </button>
                  )
                )}
              </div>

              {/* HERO RANK KARTICA: trenutni rank veliki + najbolji/najgori sub-stats */}
              {(() => {
                // v77: rank dolazi iz /entry/{id}/ — ISTI izvor koji cita hero
                // kartica na dashboardu. Kad je modal tvoja sopstvena kartica,
                // uzima se direktno userInfo, pa su hero i modal isti broj po
                // konstrukciji, ne po slucajnosti. Za tudji tim se koristi
                // managerEntries, koji puni onaj /entry/ poziv koji ionako vec
                // ide zbog liste liga (efekat iznad) — dakle bez novog fetch-a.
                // Logika je u src/overall-rank.js, 33 testa u fpl-planner-dev.
                const history = managerHistories[selectedManager.id];
                const liveOverall =
                  (String(selectedManager.id) === String(teamId) ? userInfo?.overallRank : null)
                  || managerEntries[selectedManager.id]?.overallRank
                  || null;
                const rankStats = overallRankStats(history, liveOverall, currentGW?.id);
                if (!rankStats) return null;
                const current = rankStats.current;
                const highest = rankStats.highest;
                const lowest = rankStats.lowest;
                const rankDirection = rankStats.direction; // "up" | "down" | null
                const formatBigRank = (r) => {
                  if (r < 1000) return r.toLocaleString();
                  if (r < 1000000) return Math.round(r / 1000) + "K";
                  return (r / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
                };
                return (
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14, marginBottom: 10, textAlign: "center" }}>
                    <div style={{ fontSize: 9, color: "#64748b", letterSpacing: "0.6px", marginBottom: 4, textTransform: "uppercase", fontWeight: 600 }}>{t.currentRank}</div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                      <div style={{ fontSize: 28, fontWeight: 700, color: "#1d4ed8", fontFamily: "var(--sans)", lineHeight: 1, letterSpacing: "-0.5px" }}>{current.toLocaleString()}</div>
                      {rankDirection && (
                        <svg width="18" height="18" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                          {rankDirection === "up" ? (
                            <path d="M7 11V3M3 7L7 3L11 7" stroke="#15803d" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                          ) : (
                            <path d="M7 3V11M3 7L7 11L11 7" stroke="#b91c1c" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                          )}
                        </svg>
                      )}
                    </div>
                    {highest !== null && lowest !== null && (
                      <div style={{ display: "flex", justifyContent: "center", gap: 24, marginTop: 10, paddingTop: 10, borderTop: "1px solid #e2e8f0" }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#15803d", fontFamily: "var(--sans)" }}>{formatBigRank(highest)}</div>
                          <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 1, textTransform: "uppercase", letterSpacing: "0.4px", fontWeight: 600 }}>{t.highestRank}</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#b91c1c", fontFamily: "var(--sans)" }}>{formatBigRank(lowest)}</div>
                          <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 1, textTransform: "uppercase", letterSpacing: "0.4px", fontWeight: 600 }}>{t.lowestRank}</div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* GW NET / UKUPNO grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 11, textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#2563eb", lineHeight: 1, fontFamily: "var(--sans)" }}>{selectedManager.gwNet}</div>
                  <div style={{ fontSize: 10, color: "#94a3b8", letterSpacing: "0.4px", marginTop: 3, textTransform: "uppercase", fontWeight: 600 }}>{t.gwNet}</div>
                </div>
                <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 11, textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#2563eb", lineHeight: 1, fontFamily: "var(--sans)" }}>{selectedManager.totalLive.toLocaleString()}</div>
                  <div style={{ fontSize: 10, color: "#94a3b8", letterSpacing: "0.4px", marginTop: 3, textTransform: "uppercase", fontWeight: 600 }}>{t.total}</div>
                </div>
              </div>

              {/* KOMPAKTNI CIP TRACKER: mali krugovi sa stiklom */}
              {(() => {
                const history = managerHistories[selectedManager.id];
                if (!history?.chips) return null;
                const gwNum = currentGW?.id || 1;
                const isFirstHalf = gwNum <= 19;
                const chipDefs = [
                  { key: "wildcard", abbr: "WC" },
                  { key: "bboost", abbr: "BB" },
                  { key: "3xc", abbr: "TC" },
                  { key: "freehit", abbr: "FH" },
                ];
                const relevantChips = history.chips.filter(c => {
                  if (c.name === "manager") return false;
                  return isFirstHalf ? c.event <= 19 : c.event >= 20;
                });
                const activeChip = enrichedSelected.activeChip;
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 12px", background: "#f8fafc", borderRadius: 10, marginBottom: 14 }}>
                    <div style={{ fontSize: 9, color: "#94a3b8", letterSpacing: "0.5px", fontWeight: 600 }}>
                      {lang === "en" ? "CHIPS" : "CIPOVI"}
                    </div>
                    <div style={{ display: "flex", gap: 12 }}>
                      {chipDefs.map(chip => {
                        const usedChip = relevantChips.find(c => c.name === chip.key);
                        const isActive = activeChip === chip.key;
                        const isUsed = !!usedChip && !isActive;
                        return (
                          <div key={chip.key} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                            <div style={{
                              width: 14, height: 14, borderRadius: "50%",
                              background: isActive ? "#dbeafe" : isUsed ? "#dcfce7" : "#fff",
                              border: isActive ? "1px solid #2563eb" : isUsed ? "1px solid #86efac" : "1px dashed #cbd5e1",
                              display: "flex", alignItems: "center", justifyContent: "center"
                            }}>
                              {isActive && <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#1d4ed8" }}/>}
                              {isUsed && (
                                <svg width="7" height="7" viewBox="0 0 14 14" fill="none">
                                  <path d="M3 7L6 10L11 4" stroke="#15803d" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                            </div>
                            <div style={{
                              fontSize: 7, fontWeight: 600, letterSpacing: "0.3px",
                              color: isActive ? "#1d4ed8" : isUsed ? "#475569" : "#94a3b8"
                            }}>{chip.abbr}</div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Spacer - gura bank pill desno */}
                    <div style={{ flex: 1 }}></div>
                    {/* Bank pill - cinch sack ikonica + iznos (uvek prikazan, i kad je 0) */}
                    {(() => {
                      const picksData = managerPicks[selectedManager.id];
                      const bankRaw = picksData?.entry_history?.bank ?? selectedManager?.entry_history?.bank ?? 0;
                      const bankM = (bankRaw / 10).toFixed(1);
                      return (
                        <div style={{
                          background: "#eff6ff",
                          border: "1px solid #bfdbfe",
                          borderRadius: 8,
                          padding: "4px 9px",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          flexShrink: 0,
                        }}>
                          {/* Cinch sack money bag ikonica - Varijanta C (vezice + nabor + £ unutra) */}
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-label="Money bag">
                            <path d="M9 4 L8.5 6.5" stroke="#2563eb" strokeWidth="1" strokeLinecap="round"/>
                            <path d="M15 4 L15.5 6.5" stroke="#2563eb" strokeWidth="1" strokeLinecap="round"/>
                            <path d="M9 6 Q12 5 15 6 L14.5 7.5 L9.5 7.5 Z" fill="white" stroke="#2563eb" strokeWidth="1.3" strokeLinejoin="round"/>
                            <path d="M9.5 7.5 Q4 9 4 14.5 Q4 20.5 12 20.5 Q20 20.5 20 14.5 Q20 9 14.5 7.5 Z" fill="white" stroke="#2563eb" strokeWidth="1.5" strokeLinejoin="round"/>
                            <text x="12" y="16.5" textAnchor="middle" fontSize="9" fontWeight="900" fill="#2563eb" fontFamily="var(--sans)">£</text>
                          </svg>
                          <span style={{ fontSize: 12, color: "#1e3a8a", fontWeight: 800, letterSpacing: "-0.2px", fontFamily: "var(--sans)" }}>{bankM}M</span>
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}

              <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8 }}>{t.team} (GW {currentGW?.id})</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 20 }}>
                {(enrichedSelected.picks || []).map((pick, pIdx) => {
                  const player = bootstrap?.elements.find((p) => p.id === pick.element);
                  const live = liveData?.[pick.element];
                  const isBench = pick.position > 11;
                  const isInactive = pick.multiplier === 0 && !isBench;
                  const posMap = { 1: "GK", 2: "DEF", 3: "MID", 4: "FWD" };
                  const pointsWithBonus = (live?.points || 0) + (live?.bonus === 0 ? (bonusMap[pick.element] || 0) : 0);
                  // v75 (01.09.2026) · POENI NA KLUPI.
                  //
                  // Bilo je `pointsWithBonus * pick.multiplier`, a rezerva ima mnozilac 0,
                  // pa je SVAKA rezerva pokazivala 0 bez obzira sta je odigrala. Primer sa
                  // ekrana 31.08: Cash je odigrao 77 minuta i ima 2 poena, kod nas je pisalo 0.
                  // Covek gleda klupu da vidi koga je propustio da postavi — nula mu je
                  // dezinformacija, ne informacija.
                  //
                  // Ko se broji (mnozilac > 0) i dalje ide pomnozen, da kapitenovo
                  // udvostrucenje ostane vidljivo. Ko se ne broji prikazuje sirov ucinak.
                  // Da se ne pomesa sa onim sto ulazi u skor: red je vec na opacity 0.5 i
                  // stoji ispod crte KLUPA, a plava boja za 8+ ostaje rezervisana ISKLJUCIVO
                  // za igraca koji stvarno donosi poene.
                  //
                  // Bench Boost nije dirnut: tada FPL salje mnozilac 1 i za klupu, pa te
                  // rezerve prolaze kroz granu "broji se".
                  const seBroji = (pick.multiplier || 0) > 0;
                  const prikazPoena = seBroji ? pointsWithBonus * pick.multiplier : pointsWithBonus;
                  return (
                    <div key={pIdx}>
                      {pick.position === 12 && <div style={{ textAlign: "center", fontSize: 9, fontWeight: 700, color: "#cbd5e1", letterSpacing: "2px", padding: "8px 0 4px" }}>{t.bench}</div>}
                      <div
                        onClick={() => setSelectedPlayer({ id: pick.element })}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", background: pick.wasSubbedIn ? "#dcfce7" : pick.wasSubbedOut ? "#fef2f2" : "#f8fafc", borderRadius: 8, fontSize: 12, opacity: isBench || isInactive ? (pick.wasSubbedOut ? 0.7 : 0.5) : 1, borderLeft: pick.is_captain ? "3px solid #2563eb" : pick.is_vice_captain ? "3px solid #cbd5e1" : "3px solid transparent", cursor: "pointer", transition: "background 0.15s" }}
                      >
                        <span style={{ fontFamily: "var(--mono)", fontSize: 10, fontWeight: 700, color: "#94a3b8", width: 28 }}>{posMap[player?.element_type] || "?"}</span>
                        <span style={{ flex: 1, fontWeight: 600, color: "#1e293b", display: "flex", alignItems: "center", gap: 4 }}>
                          {pick.wasSubbedIn && <span style={{ fontSize: 13, fontWeight: 900, color: "#15803d" }}>↑</span>}
                          {pick.wasSubbedOut && <span style={{ fontSize: 13, fontWeight: 900, color: "#b91c1c" }}>↓</span>}
                          {player?.web_name || "?"}
                          {pick.is_captain && <span style={{ color: "#2563eb", fontWeight: 700 }}>(C)</span>}
                          {pick.is_vice_captain && <span style={{ color: "#94a3b8" }}>(V)</span>}
                        </span>
                        <span style={{ fontFamily: "var(--mono)", fontSize: 10, color: "#94a3b8", width: 28, textAlign: "center" }}>{live?.isUpcoming ? "—" : `${live?.minutes || 0}'`}</span>
                        <span style={{ fontFamily: "var(--mono)", fontWeight: 700, width: 50, textAlign: "right", color: seBroji && prikazPoena >= 8 ? "#2563eb" : "#334155" }}>
                          {prikazPoena}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* TRANSFERI sekcija - lazy fetch, prikazuje se samo ako ima transfera */}
              {(() => {
                const transfersData = managerTransfers[selectedManager.id];
                if (!transfersData) return null; // jos nije fetched
                if (transfersData.loading) {
                  return (
                    <div style={{ marginBottom: 20, padding: "12px 0", borderTop: "0.5px solid #e5e7eb" }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "1px" }}>
                        {t.transfersTitle} · GW {currentGW?.id}
                      </div>
                      <div style={{ textAlign: "center", padding: 12, color: "#94a3b8", fontSize: 11 }}>{t.transfersLoading}</div>
                    </div>
                  );
                }
                const transfers = transfersData.transfers || [];
                if (transfers.length === 0) return null; // 0 transfera = ne prikazuj sekciju

                // Sub-text logika: hit cost ili WC/FH chip kontekst
                const picksData = managerPicks[selectedManager.id];
                const hitCost = picksData?.entry_history?.event_transfers_cost ?? 0;
                const activeChip = enrichedSelected.activeChip;
                const isWC = activeChip === "wildcard";
                const isFH = activeChip === "freehit";
                const count = transfers.length;
                let subText = "";
                if (isWC || isFH) {
                  subText = (t.transfersChip || "").replace("{count}", count).replace("{chip}", isWC ? "WC" : "FH");
                } else if (hitCost > 0) {
                  const tmpl = count === 1 ? t.transfersHitOne : t.transfersHit;
                  subText = (tmpl || "").replace("{count}", count).replace("{cost}", `-${hitCost}`);
                } else {
                  const tmpl = count === 1 ? t.transfersFreeOne : t.transfersFree;
                  subText = (tmpl || "").replace("{count}", count);
                }
                const subTextColor = (hitCost > 0 && !isWC && !isFH) ? "#b91c1c" : "#94a3b8";

                // Racunanje slobodnih transfera preostalih za OVAJ GW
                // Iteriramo unazad od GW1 da bismo znali stanje na pocetku trenutnog GW-a
                // FPL pravila 25/26: pocetak GW1 = 1 free, max 5 sacuvanih, +1 svake nedelje
                // WC/FH ne troše transfere ali rolovanje +1 i dalje vazi
                const history = managerHistories[selectedManager.id];
                let transfersLeftText = null;
                if (history?.current && history.current.length > 0) {
                  const currentGwId = currentGW?.id || 1;
                  const chips = history.chips || [];
                  let freeAtStart = 1; // GW1 starts with 1 free
                  let computedLeft = null;
                  for (let i = 0; i < history.current.length; i++) {
                    const gw = history.current[i];
                    if (gw.event === currentGwId) {
                      // doslo do trenutnog GW-a: izracunaj left
                      if (isWC || isFH) {
                        // chip ne troši transfere - left ostaje freeAtStart (vizuelno smisleno: jos imas free u banci)
                        computedLeft = freeAtStart;
                      } else {
                        const used = gw.event_transfers || 0;
                        computedLeft = Math.max(0, freeAtStart - used);
                      }
                      break;
                    }
                    // proslost: rolovanje za sledeci GW
                    const used = gw.event_transfers || 0;
                    const chipForGw = chips.find(c => c.event === gw.event);
                    const chipName = chipForGw?.name;
                    if (chipName === "wildcard" || chipName === "freehit") {
                      // chip GW: free se ne trosi, ali rolovanje +1 ide normalno
                      freeAtStart = Math.min(5, freeAtStart + 1);
                    } else {
                      freeAtStart = Math.min(5, Math.max(0, freeAtStart - used) + 1);
                    }
                  }
                  if (computedLeft !== null && !isNaN(computedLeft)) {
                    const tmpl = computedLeft === 1 ? t.transfersLeftOne : t.transfersLeft;
                    transfersLeftText = (tmpl || "").replace("{count}", computedLeft);
                  }
                }

                return (
                  <div style={{ marginBottom: 20, paddingTop: 14, borderTop: "0.5px solid #e5e7eb" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10, gap: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "1px" }}>
                        {t.transfersTitle} · GW {currentGW?.id}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: subTextColor }}>{subText}</div>
                        {/* DISABLED v51: transfersLeftText display sakriven jer racunanje ima bag (prikazuje vise nego sto covek realno ima). Logika racunanja ostaje iznad da se ne gubi. */}
                        {false && transfersLeftText && (
                          <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>{transfersLeftText}</div>
                        )}
                      </div>
                    </div>

                    {transfers.map((tr, idx) => {
                      const playerOut = bootstrap?.elements.find(p => p.id === tr.element_out);
                      const playerIn = bootstrap?.elements.find(p => p.id === tr.element_in);
                      const outCost = tr.element_out_cost ? (tr.element_out_cost / 10).toFixed(1) : "?";
                      const inCost = tr.element_in_cost ? (tr.element_in_cost / 10).toFixed(1) : "?";
                      return (
                        <div key={idx} style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "8px 4px",
                          borderTop: idx > 0 ? "0.5px solid #e5e7eb" : "none",
                        }}>
                          {/* OUT */}
                          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                            <div style={{ width: 22, height: 22, background: "#fee2e2", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                                <path d="M2 7H12M7 2L2 7L7 12" stroke="#b91c1c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: "#1d1d1f", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "var(--sans)" }}>{playerOut?.web_name || "?"}</div>
                              <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 500, fontFamily: "var(--sans)" }}>£{outCost}m</div>
                            </div>
                          </div>
                          {/* Strelica */}
                          <svg width="14" height="14" viewBox="0 0 14 14" style={{ flexShrink: 0 }}>
                            <path d="M3 7H11M7 3L11 7L7 11" stroke="#cbd5e1" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          {/* IN */}
                          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                            <div style={{ width: 22, height: 22, background: "#d1fae5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                                <path d="M12 7H2M7 2L12 7L7 12" stroke="#15803d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: "#1d1d1f", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "var(--sans)" }}>{playerIn?.web_name || "?"}</div>
                              <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 500, fontFamily: "var(--sans)" }}>£{inCost}m</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8 }}>{t.otherLeagues}</div>
              {leaguesLoading && !currentManagerLeagues ? (
                <div style={{ textAlign: "center", padding: 20, color: "#94a3b8", fontSize: 12 }}>{t.loading}</div>
              ) : currentManagerLeagues && currentManagerLeagues.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {sortLeagues(currentManagerLeagues.filter(lg => String(lg.id) !== String(leagueId))).slice(0, 10).map(lg => (
                    <button
                      key={lg.id}
                      onClick={() => navigateToLeague(lg.id)}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "#f8fafc", borderRadius: 8, fontSize: 12, border: "1px solid #edf2f7", cursor: "pointer", textAlign: "left", fontFamily: "var(--sans)", width: "100%", transition: "background 0.15s, border 0.15s" }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lg.name}</div>
                        <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 1 }}>
                          {lg.league_type === "s" ? t.public : t.private}
                          {lg.entry_rank && ` · ${lg.entry_rank.toLocaleString()} / ${lg.rank_count ? lg.rank_count.toLocaleString() : "?"}`}
                        </div>
                      </div>
                      {lg.entry_rank && (
                        <div style={{ marginLeft: 12, textAlign: "right" }}>
                          <div style={{ fontFamily: "var(--mono)", fontSize: 13, fontWeight: 700, color: "#2563eb" }}>#{lg.entry_rank.toLocaleString()}</div>
                          {lg.entry_last_rank && lg.entry_last_rank !== lg.entry_rank && (
                            <div style={{ fontSize: 9, color: lg.entry_rank < lg.entry_last_rank ? "#16a34a" : "#dc2626", fontFamily: "var(--mono)" }}>
                              {lg.entry_rank < lg.entry_last_rank ? "▲" : "▼"} {Math.abs(lg.entry_last_rank - lg.entry_rank)}
                            </div>
                          )}
                        </div>
                      )}
                      <svg width="12" height="12" viewBox="0 0 14 14" fill="none" style={{ color: "#94a3b8", marginLeft: 8, flexShrink: 0 }}>
                        <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: 16, color: "#94a3b8", fontSize: 11 }}>{t.noOtherLeagues}</div>
              )}
            </div>
          </div>
          );
        })()}

        {/* H2H COMPARE MODAL */}
        {showCompareModal && selectedManager && (
          <div style={S.overlay} onClick={() => { setShowCompareModal(false); setCompareRival(null); }}>
            <div style={{ ...S.modal, maxWidth: 420, padding: 0 }} onClick={(e) => e.stopPropagation()}>
              {!compareRival ? (
                <H2HRivalPicker
                  myId={selectedManager.id}
                  sortedManagers={sortedManagers}
                  t={t}
                  onClose={() => { setShowCompareModal(false); setCompareRival(null); }}
                  onPick={(r) => { trackEvent("Compare Started", { source: "picker" }); setCompareRival(r); }}
                />
              ) : (
                <H2HCompare
                  myManager={{ ...selectedManager, gwNum: currentGW?.id }}
                  rivalManager={compareRival}
                  bootstrap={bootstrap}
                  liveData={liveData}
                  bonusMap={bonusMap}
                  t={t}
                  lang={lang}
                  leagueName={leagueData?.league?.name || ""}
                  onClose={() => { setShowCompareModal(false); setCompareRival(null); }}
                  onBack={() => setCompareRival(null)}
                />
              )}
            </div>
          </div>
        )}

        {/* SHARE MODAL */}
        {showShareModal && (() => {
          // If selectedManager is set (opened from manager modal), use it
          // Otherwise (opened from dashboard share button), use the logged-in user
          let me = selectedManager || sortedManagers.find(m => String(m.id) === String(teamId));
          
          // Fallback for users below top 50: build synthetic me from userInfo
          if (!me && teamId && userInfo) {
            const currentLeagueId = leagueData?.league?.id;
            const myLeagueEntry = userLeagues?.find(lg => lg.id === currentLeagueId);
            me = {
              id: teamId,
              name: userInfo.name,
              teamName: userInfo.teamName,
              liveRank: myLeagueEntry?.entry_rank || userInfo.overallRank || 999,
              previousRank: myLeagueEntry?.entry_last_rank || null,
              rank: myLeagueEntry?.entry_rank || userInfo.overallRank,
              gwNet: userInfo.eventPoints || 0,
              totalLive: userInfo.totalPoints || 0,
              hasPicks: false,
              picks: null,
            };
          }
          
          if (!me) return null;
          const leagueName = leagueData?.league?.name || "";
          const gwNum = currentGW?.id || 0;
          const handle = "@" + (me.name || "").replace(/\s+/g, "_");

          // League size + overall detekcija (ista logika kao u dashboard hero karti)
          const currentLeagueId = leagueData?.league?.id;
          const myLeagueEntry = userLeagues?.find(lg => lg.id === currentLeagueId);
          const totalLeagueMembers = myLeagueEntry?.rank_count || sortedManagers.length;
          const isOverallGlobalLeague = totalLeagueMembers > 1000000;
          const overallRank = userInfo?.overallRank || me.rank || null;

          // Calculate triggers
          const isLeagueLeader = me.liveRank === 1;
          // GW winner check: has the highest gwNet in sortedManagers
          const allGwNets = sortedManagers.map(m => m.gwNet || 0);
          const maxGwNet = allGwNets.length > 0 ? Math.max(...allGwNets) : 0;
          const isGWWinner = me.gwNet === maxGwNet && me.gwNet > 0;
          // Captain check
          const captainPick = me.picks?.find(p => p.is_captain);
          const captainPlayer = captainPick ? bootstrap?.elements.find(p => p.id === captainPick.element) : null;
          const captainLive = captainPick ? liveData?.[captainPick.element] : null;
          // Calculate captain points same way as team table: (points + provisional_bonus) * multiplier
          const rawCapPoints = captainLive?.points || 0;
          const capProvBonus = bonusMap[captainPick?.element] || 0;
          const capBaseTotal = rawCapPoints + (captainLive?.bonus === 0 ? capProvBonus : 0);
          const captainMultiplier = captainPick?.multiplier || 2;
          const captainPts = capBaseTotal * captainMultiplier;
          const isCaptainKing = captainPts >= 12;
          const captainGoals = captainLive?.goals || 0;
          const captainAssists = captainLive?.assists || 0;
          const captainBonus = (captainLive?.bonus || 0) + (captainLive?.bonus === 0 ? capProvBonus : 0);

          // Overall Rank Milestone calculation
          // v77: ISTI izvor kao hero kartica i manager modal. Ranije se ovde citao
          // history, dok je overallRankFormatted nize u OVOM ISTOM modalu citao
          // svez /entry/ — pa je jedna share kartica umela da kaze "TOP 50K",
          // a ona pored nje 74K, za isti tim u istoj sekundi.
          const myHistory = managerHistories[me.id];
          const myLiveOverall =
            (String(me.id) === String(teamId) ? userInfo?.overallRank : null)
            || managerEntries[me.id]?.overallRank
            || null;
          const currentOverallRank = overallRankStats(myHistory, myLiveOverall, currentGW?.id)?.current ?? null;

          // Tier tiers — highest tier user qualifies for
          const tierLevels = [
            { limit: 5, label: "TOP 5" },
            { limit: 10, label: "TOP 10" },
            { limit: 20, label: "TOP 20" },
            { limit: 50, label: "TOP 50" },
            { limit: 100, label: "TOP 100" },
            { limit: 200, label: "TOP 200" },
            { limit: 500, label: "TOP 500" },
            { limit: 1000, label: "TOP 1K" },
            { limit: 5000, label: "TOP 5K" },
            { limit: 10000, label: "TOP 10K" },
            { limit: 20000, label: "TOP 20K" },
            { limit: 50000, label: "TOP 50K" },
            { limit: 100000, label: "TOP 100K" },
            { limit: 200000, label: "TOP 200K" },
            { limit: 500000, label: "TOP 500K" },
            { limit: 1000000, label: "TOP 1M" },
          ];
          let rankTier = null;
          if (currentOverallRank) {
            const matchedTier = tierLevels.find(t => currentOverallRank <= t.limit);
            rankTier = matchedTier || null;
          }
          const hasRankTier = rankTier !== null;

          // Best GW for rank card
          let bestGwPoints = 0, bestGwNum = null, bestGwCaptainName = "", bestGwCaptainPts = 0;
          if (myHistory?.current && myHistory.current.length > 0) {
            const bestGw = myHistory.current.reduce((best, gw) => (gw.points > (best?.points || 0) ? gw : best), null);
            if (bestGw) {
              bestGwPoints = bestGw.points;
              bestGwNum = bestGw.event;
            }
          }
          // For current GW, use captain from live picks
          if (gwNum === bestGwNum && captainPlayer) {
            bestGwCaptainName = captainPlayer.web_name;
            bestGwCaptainPts = captainPts;
          }

          // Percentile text (approximate, based on ~11M managers)
          let percentileText = "";
          if (currentOverallRank) {
            const pct = (currentOverallRank / 11000000) * 100;
            if (pct < 0.001) percentileText = `Top ${pct.toFixed(4)}%`;
            else if (pct < 0.01) percentileText = `Top ${pct.toFixed(3)}%`;
            else if (pct < 0.1) percentileText = `Top ${pct.toFixed(2)}%`;
            else if (pct < 1) percentileText = `Top ${pct.toFixed(2)}%`;
            else percentileText = `Top ${pct.toFixed(1)}%`;
          }

          const cards = [
            {
              id: "dashboard",
              unlocked: true,
              lockReason: "",
              title: lang === "sr" ? "Moj Dashboard" : "My Dashboard",
              emoji: "📊",
              component: ShareCardDashboard,
              data: {
                leagueName,
                gwNum,
                position: me.liveRank,
                totalManagers: totalLeagueMembers || sortedManagers.length,
                isOverallGlobalLeague,
                percentileBucket: isOverallGlobalLeague && overallRank ? (() => {
                  const p = (overallRank / 11000000) * 100;
                  if (p <= 1) return 1;
                  if (p <= 5) return 5;
                  if (p <= 10) return 10;
                  if (p <= 20) return 20;
                  if (p <= 30) return 30;
                  if (p <= 40) return 40;
                  if (p <= 50) return 50;
                  if (p <= 60) return 60;
                  if (p <= 70) return 70;
                  if (p <= 80) return 80;
                  if (p <= 90) return 90;
                  return 100;
                })() : null,
                managerName: me.name,
                teamName: me.teamName,
                gwNet: me.gwNet,
                totalPts: me.totalLive,
                deltaValue: me.liveRank === 1 && sortedManagers[1]
                  ? me.totalLive - sortedManagers[1].totalLive
                  : sortedManagers[0].totalLive - me.totalLive,
                overallRankFormatted: (() => {
                  const r = userInfo?.overallRank || me.rank || 0;
                  if (!r) return "—";
                  if (r < 1000) return r.toLocaleString();
                  if (r < 1000000) return Math.round(r / 1000) + "K";
                  return (r / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
                })(),
                handle,
              }
            },
            {
              id: "leader",
              unlocked: isLeagueLeader,
              lockReason: t.shareCard1Lock,
              title: t.shareCard1Title,
              emoji: "🥇",
              component: ShareCardLeader,
              data: {
                leagueName,
                gwNum,
                gwNet: me.gwNet,
                totalPts: me.totalLive,
                handle,
              }
            },
            {
              id: "gwwinner",
              unlocked: isGWWinner,
              lockReason: t.shareCard2Lock,
              title: t.shareCard2Title,
              emoji: "👑",
              component: ShareCardGWWinner,
              data: {
                leagueName,
                gwNum,
                gwNet: me.gwNet,
                managerName: me.name,
                teamName: me.teamName,
                handle,
              }
            },
            {
              id: "captain",
              unlocked: isCaptainKing,
              lockReason: t.shareCard3Lock,
              title: t.shareCard3Title,
              emoji: "⭐",
              component: ShareCardCaptain,
              data: {
                captainName: captainPlayer?.web_name || "",
                captainPts,
                captainGoals,
                captainAssists,
                captainBonus,
                handle,
              }
            },
            {
              id: "rank",
              unlocked: hasRankTier,
              lockReason: t.shareCard4Lock,
              title: hasRankTier ? `${t.shareCard4Title} · ${rankTier.label}` : t.shareCard4Title,
              emoji: "🌟",
              component: ShareCardRank,
              bgColor: "#0f172a",
              data: {
                managerName: me.name,
                teamName: me.teamName,
                tier: rankTier?.label || "",
                exactRank: currentOverallRank || 0,
                percentileText,
                bestGwPoints,
                bestGwNum: bestGwNum || gwNum,
                captainName: bestGwCaptainName,
                captainPts: bestGwCaptainPts,
                handle,
              }
            },
          ];

          return (
            <div style={S.overlay} onClick={() => setShowShareModal(false)}>
              <div style={{ ...S.modal, maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                  <div>
                    <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: "#0f172a" }}>{t.shareYourMoment}</h2>
                    <p style={{ fontSize: 11, color: "#94a3b8", margin: "2px 0 0" }}>{t.shareSubtitle}</p>
                  </div>
                  <button onClick={() => setShowShareModal(false)} style={S.closeBtn}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11 3L3 11M3 3l8 8" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round"/></svg>
                  </button>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {cards.map(card => (
                    <div key={card.id} data-share-card-id={card.id}>
                      <ShareCardRow card={card} t={t} lang={lang} />
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: 16, padding: "10px 12px", background: "#f8fafc", borderRadius: 8, fontSize: 11, color: "#64748b", lineHeight: 1.5 }}>
                  💡 {lang === "sr" ? "Podeli sliku na Instagram, WhatsApp ili Twitter. Automatski generisemo sliku u obliku Instagram Story-ja." : "Share the image on Instagram, WhatsApp or Twitter. We auto-generate an Instagram Story-sized image."}
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  }
  return null;
}

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Onest:wght@400;500;600;700;800;900&display=swap');
  /* v59 (28.08.2026) — jedan font umesto dva.
     Filipov zahtev: nula bez tackice i bez kose crte. Tackica u nuli nije bila
     losa postavka nego SVRHA monospace fontova (da se 0 ne pomesa sa O), pa se
     vracala u svakom mono fontu. Zato je mono izbacen, a --mono ostavljen kao
     ime promenljive da se ne dira 50 mesta u kodu — sad pokazuje na isti sans.
     Poravnanje cifara u kolonama drzi font-variant-numeric: tabular-nums,
     nasledjeno sa :root na celu aplikaciju.
     Debljine 800 i 900 se stvarno koriste (39 + 12 mesta) i sada se i ucitavaju;
     ranije su trazene samo do 700 pa ih je browser sam podebljavao. */
  :root { --sans: 'Onest', -apple-system, BlinkMacSystemFont, sans-serif; --mono: 'Onest', -apple-system, BlinkMacSystemFont, sans-serif; font-variant-numeric: tabular-nums; }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
  @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin { to{transform:rotate(360deg)} }
  @keyframes glowPulse { 0%,100%{opacity:0.4;transform:scale(1)} 50%{opacity:0.7;transform:scale(1.05)} }
  @keyframes urlPulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.4); }
    50% { box-shadow: 0 0 0 4px rgba(37, 99, 235, 0); }
  }
  @keyframes pulseDot {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.3); opacity: 0.6; }
  }
  @keyframes shimmer {
    0% { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
  @keyframes maintPulse {
    0%, 100% { transform: scale(1); opacity: 0.7; }
    50% { transform: scale(1.15); opacity: 1; }
  }
  @keyframes maintDot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(0.75); }
  }
  *{box-sizing:border-box;margin:0;padding:0}
  body { font-family: var(--sans); }
  input::placeholder{color:#b0bec5}
  input:focus{outline:none;border-color:#2563eb !important;box-shadow:0 0 0 3px rgba(37,99,235,0.08) !important}
  .trow:hover{background:#f0f7ff !important}
  .mgr-row td { border-bottom: 1px solid #cbd5e1 !important; }
  .mgr-row:last-child td { border-bottom: none !important; }
  table{border-spacing:0}
  ::-webkit-scrollbar{height:4px} ::-webkit-scrollbar-track{background:transparent} ::-webkit-scrollbar-thumb{background:#ddd;border-radius:4px}
  .shimmer {
    display: inline-block;
    background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 40%, #f1f5f9 80%);
    background-size: 800px 100%;
    animation: shimmer 1.6s linear infinite;
    border-radius: 4px;
  }
  
  /* Mobile visibility */
  .show-mobile-only { display: none; }
  
  @media (max-width: 640px) {
    .hide-mobile { display: none !important; }
    .show-mobile-only { display: inline !important; }
    .bonus-grid-mobile {
      display: flex !important;
      overflow-x: auto !important;
      gap: 10px !important;
      padding-bottom: 8px !important;
      scroll-snap-type: x mandatory !important;
    }
    .bonus-grid-mobile > div {
      min-width: 260px !important;
      scroll-snap-align: start !important;
      flex-shrink: 0 !important;
    }
  }
`;

const S = {
  page: { fontFamily: "var(--sans)", background: "#f4f7fb", color: "#1e293b", minHeight: "100vh", WebkitFontSmoothing: "antialiased", position: "relative" },
  homeWrap: { maxWidth: 440, margin: "0 auto", padding: "0 20px 48px", animation: "fadeUp 0.5s ease" },
  hero: { position: "relative", padding: "56px 0 36px", textAlign: "center", overflow: "hidden" },
  heroGlow: { position: "absolute", top: -40, left: "50%", transform: "translateX(-50%)", width: 260, height: 260, borderRadius: "50%", background: "radial-gradient(circle, rgba(37,99,235,0.1) 0%, transparent 70%)", animation: "glowPulse 4s ease infinite", pointerEvents: "none" },
  heroTitle: { fontSize: 34, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.5px", lineHeight: 1.1 },
  heroSub: { fontSize: 15, color: "#64748b", marginTop: 6, fontWeight: 400 },
  card: { background: "#fff", borderRadius: 16, padding: 24, marginBottom: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(37,99,235,0.03)", border: "1px solid #e8eef6" },
  label: { display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 6, letterSpacing: "0.3px" },
  input: { width: "100%", padding: "11px 14px", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 10, color: "#1e293b", fontSize: 14, fontFamily: "var(--mono)", transition: "border 0.2s, box-shadow 0.2s" },
  btn: { width: "100%", padding: "13px", marginTop: 4, background: "#2563eb", border: "none", borderRadius: 12, color: "#fff", fontSize: 14, fontWeight: 600, fontFamily: "var(--sans)", letterSpacing: "0.2px", boxShadow: "0 2px 8px rgba(37,99,235,0.25)" },
  hint: { fontSize: 11, color: "#94a3b8", marginTop: 14, lineHeight: 1.6 },
  features: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  featureItem: { display: "flex", alignItems: "center", gap: 10, background: "#fff", border: "1px solid #edf2f7", borderRadius: 12, padding: "14px 12px", boxShadow: "0 1px 2px rgba(0,0,0,0.02)" },
  header: { display: "flex", alignItems: "center", gap: 12, padding: "12px 20px", background: "rgba(255,255,255,0.72)", backdropFilter: "blur(20px) saturate(180%)", WebkitBackdropFilter: "blur(20px) saturate(180%)", borderBottom: "1px solid rgba(0,0,0,0.06)", position: "sticky", top: 0, zIndex: 100 },
  backBtn: { background: "none", border: "none", cursor: "pointer", padding: 6, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" },
  headerTitle: { fontSize: 16, fontWeight: 700, color: "#0f172a" },
  headerMeta: { fontSize: 11, color: "#94a3b8", fontFamily: "var(--mono)", marginTop: 1 },
  liveDot: { display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", background: "#f0fdf4", borderRadius: 20 },
  fixStrip: { display: "flex", gap: 7, padding: "10px 20px", overflowX: "auto" },
  fixCard: { minWidth: 110, background: "#fff", borderRadius: 11, padding: "7px 10px", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0, border: "1px solid #edf2f7", boxShadow: "0 1px 2px rgba(0,0,0,0.02)", transition: "transform 0.15s, box-shadow 0.15s" },
  tableContainer: { padding: "0 12px", overflowX: "auto", animation: "fadeUp 0.4s ease" },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 13, background: "#fff", borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", border: "1px solid #edf2f7" },
  th: { padding: "11px 10px", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.8px", color: "#94a3b8", borderBottom: "1px solid #f1f5f9", background: "#fafbfd" },
  td: { padding: "11px 10px", borderBottom: "1px solid #f4f6f8", fontSize: 13 },
  captainPill: { display: "inline-block", padding: "3px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600, background: "#f0f7ff", color: "#2563eb" },
  sectionTitle: { fontSize: 13, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 12 },
  bonusGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 },
  bonusCard: { background: "#fff", border: "1px solid #edf2f7", borderRadius: 12, padding: 14, boxShadow: "0 1px 2px rgba(0,0,0,0.02)" },
  bonusHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, fontWeight: 700, color: "#334155", paddingBottom: 8, marginBottom: 6, borderBottom: "1px solid #f1f5f9" },
  overlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.18)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: 56, zIndex: 200, overflowY: "auto" },
  modal: { background: "#fff", borderRadius: 18, padding: 24, width: "100%", maxWidth: 400, margin: "0 16px 40px", animation: "fadeUp 0.3s ease", boxShadow: "0 8px 40px rgba(15,23,42,0.1), 0 1px 3px rgba(0,0,0,0.04)", border: "1px solid #edf2f7" },
  closeBtn: { background: "#f8fafc", border: "none", width: 30, height: 30, borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  statRow: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 20 },
  statCard: { background: "#f8fafc", borderRadius: 12, padding: 12, textAlign: "center", border: "1px solid #f1f5f9" },
};
